import { arrayRemove, arrayUnion, deleteField, doc, getDoc, serverTimestamp } from "firebase/firestore";
import { GoogleAuthProvider, signInWithCredential } from "firebase/auth";
import { auth, db } from "../../lib/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, isSameDay, isBefore, startOfDay, startOfHour, addDays } from "date-fns";
import { enUS } from "date-fns/locale";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  Clock,
  MoreVertical,
  Pencil,
  Copy,
  Archive,
  ArchiveRestore,
  Trash2,
  RotateCcw,
  RefreshCw,
  X,
} from "lucide-react";
import { useAuth } from "../../app/useAuth";
import { useProfileInteraction } from "../../hooks/useProfileInteraction";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useAutoVoteSettings } from "../../hooks/useAutoVoteSettings";
import { useFriends } from "../../hooks/useFriends";
import { useQuestingGroups } from "../../hooks/useQuestingGroups";
import { useCalendarNavigation } from "../../hooks/useCalendarNavigation";
import { useAvailabilityEvents } from "../../hooks/useAvailabilityEvents";
import { useAvailabilityMarks } from "../../hooks/useAvailabilityMarks";
import { useLinkedCalendars as useUserLinkedCalendars } from "../../hooks/useLinkedCalendars";
import { useFinalizedSessions } from "../../hooks/useFinalizedSessions";
import { useSchedulerData } from "./hooks/useSchedulerData";
import { useSchedulerEmbeddedPollVotes } from "./hooks/useSchedulerEmbeddedPollVotes";
import { useNotifications } from "../../hooks/useNotifications";
import { useUserProfiles, useUserProfilesByIds } from "../../hooks/useUserProfiles";
import {
  deleteSchedulerVote,
  deleteSchedulerWithRelatedData,
  fetchSchedulersByIds,
  schedulerVoteDocRef,
  setScheduler,
  updateScheduler,
  upsertSchedulerSlot,
  upsertSchedulerVote,
} from "../../lib/data/schedulers";
import { linkGoogleAccount } from "../../lib/auth";
import { Switch } from "../../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { AvatarBubble, AvatarStack, VotingAvatarStack } from "../../components/ui/voter-avatars";
import { buildColorMap } from "../../components/ui/voter-avatar-utils";
import { UserIdentity } from "../../components/UserIdentity";
import { LoadingState } from "../../components/ui/spinner";
import { ErrorState } from "../../components/ui/error-state";
import { resolveLoadState } from "../../lib/load-state";
import { CalendarJumpControls } from "../../components/ui/calendar-jump-controls";
import { PollStatusMeta } from "../../components/poll-status-meta";
import { SessionVotingDeadlineStatus } from "../../components/session-voting-deadline-status";
import { buildEmailSet, isValidEmail, normalizeEmail, normalizeEmailList } from "../../lib/utils";
import { getUserLabel } from "../../lib/identity";
import { buildGoogleCalendarEventUrl } from "../../lib/google-calendar";
import { SessionCalendarControl } from "../../components/session-calendar-control";
import { resolveIdentifier } from "../../lib/identifiers";
import {
  pollInviteNotificationId,
  pollInviteLegacyNotificationId,
} from "../../lib/data/notifications";
import { fetchPublicProfilesByIds, findUserIdByEmail, findUserIdsByEmails } from "../../lib/data/users";
import { acceptPollInvite, declinePollInvite, removeParticipantFromPoll, revokePollInvite } from "../../lib/data/pollInvites";
import { recordRecentInvitees } from "../../lib/data/recent-invitees";
import {
  breakBasicPollTieForParent,
  cloneEmbeddedBasicPolls,
  deleteBasicPollVote,
  finalizeEmbeddedBasicPoll,
  fetchRequiredEmbeddedPollFinalizeSummary,
  reopenEmbeddedBasicPoll,
  submitBasicPollVote,
} from "../../lib/data/basicPolls";
import {
  addRankedOptionToVoteDraft,
  moveRankedOptionInVoteDraft,
  removeRankedOptionFromVoteDraft,
  setMultipleChoiceOptionOnVoteDraft,
  setOtherTextOnVoteDraft,
} from "../../lib/basic-polls/vote-draft";
import { hasSubmittedVoteForPoll } from "../../lib/basic-polls/vote-submission";
import { isEmbeddedPollVotable } from "../../lib/basic-polls/embedded-poll-votable";
import { getUnansweredRequiredEmbeddedPolls } from "../../lib/basic-polls/required-embedded-gate";
import { buildNotificationActor, emitPollEvent } from "../../lib/data/notification-events";
import { validateInviteCandidate } from "./utils/invite-utils";
import { nudgeDiscordSessionPoll, repostDiscordPollCard } from "../../lib/data/discord";
import { createReminderSettingsSnapshot } from "../reminders/reminder-settings";
import {
  normalizeAutoNagSettings,
  resolveLatestNagDate,
} from "../reminders/auto-nag";
import {
  filterSlotsByRequiredAttendance,
  sortResultSlots,
} from "./utils/required-attendance";
import { resolveInitialCalendarMonth } from "./utils/initial-calendar-month";
import { calendarSyncLabel, isCalendarSyncActionable } from "./utils/calendar-sync-label";
import { slotSpanWindow } from "./utils/slot-overlay-summary";
import { buildOverlayRecords } from "./utils/overlay-records";
import { buildSlotIndicatorSignals } from "./utils/slot-indicator-signals";
import {
  formatZonedDateTimeRange,
  formatZonedTime,
  formatZonedTimeRange,
  resolveDisplayTimeZone,
  resolvePollTimeZone,
  shouldShowTimeZone,
  toDisplayDate,
} from "../../lib/time";
import { intervalsOverlap } from "../../lib/intervals";
import { CloneDialog } from "./components/clone-dialog";
import { CopyVotesDialog } from "./components/copy-votes-dialog";
import { FinalizeDialog } from "./components/finalize-dialog";
import { PendingVotesDialog } from "./components/pending-votes-dialog";
import { PropagateVotesPromptDialog } from "./components/PropagateVotesPromptDialog.jsx";
import { FinalizeEmbeddedPollsChoiceDialog } from "./components/finalize-embedded-polls-choice-dialog";
import { RequiredEmbeddedFinalizeWarningDialog } from "./components/required-embedded-finalize-warning-dialog";
import { VoteDialog } from "./components/vote-dialog";
import { VoteToggle } from "./components/vote-toggle";
import { QuickVoteCluster } from "./components/QuickVoteCluster.jsx";
import { SchedulerCalendarSlot } from "./components/SchedulerCalendarSlot.jsx";
import { SlotIndicator } from "./components/SlotIndicator.jsx";
import { SlotAutoIndicator } from "./components/SlotAutoIndicator.jsx";
import { CalendarOverlayBlock } from "./components/CalendarOverlayBlock.jsx";
import { layoutCalendarOverlays } from "./utils/calendar-overlay-layout.js";
import {
  hasUnacknowledgedRefIds,
  pillVariantFor,
  slotVisualsFor,
} from "./utils/slot-visuals.js";
import { SlotStatusText } from "./utils/slot-status-text.jsx";
import {
  buildConfirmOverridePatch,
  buildOverridePatch,
  buildRestorePatch,
} from "./utils/auto-vote-overrides";
import { ReopenDialog } from "./components/reopen-dialog";
import { DeleteDialog } from "./components/delete-dialog";
import { CancelDialog } from "./components/cancel-dialog";
import { InvitePromptDialog } from "./components/invite-prompt-dialog";
import { LeaveDialog } from "./components/leave-dialog";
import { RemoveParticipantDialog } from "./components/remove-participant-dialog";
import { RevokeInviteDialog } from "./components/revoke-invite-dialog";
import { ProfileInteractionModals } from "../../components/ProfileInteractionModals";
import { RsvpPanel } from "./components/RsvpPanel";
import GuestNameCard from "./components/guest-name-card";
import NotPublicScreen from "./components/not-public-screen";
import MakeAccountNudge from "./components/make-account-nudge";
import GuestVoteConflictModal from "./components/guest-vote-conflict-modal";
import { CalendarToolbar } from "./components/CalendarToolbar";
import { BasicPollVotingCard } from "../../components/polls/basic-poll-voting-card";
import { PollDiscordMetaRow } from "../../components/polls/poll-discord-meta-row";
import { PollMarkdownContent } from "../../components/polls/poll-markdown-content";
import { PollNudgeButton } from "../../components/polls/poll-nudge-button";
import { AutoNagStatusControl } from "../reminders/AutoNagStatusControl";
import { PollOptionNoteDialog } from "../../components/polls/poll-option-note-dialog";
import { buildEffectiveTallies, buildUserBlockInfo } from "./utils/effective-votes";
import { computeNextUserTouchedSlots } from "./utils/user-touched-slots";
import { buildVoteSubmitPatch, withVoteMapsIfChanged } from "./utils/vote-submit-patch";
import { canUserCopyVotes } from "./utils/copy-votes-eligibility";
import { shouldEmitPollLifecycleEvent } from "./utils/poll-lifecycle-notifications";
import { formatFinalizedSessionTime } from "./utils/notification-date-labels";
import { parseEmbeddedPollIdFromSearch } from "./utils/embedded-poll-deep-link";
import { formatCompactDuration } from "./utils/calendar-month-vote-controls";
import {
  resolveVoterForDisplay,
  resolveVoterIdentityForDisplay,
  safeParticipantLabel,
} from "./utils/voter-display";
import { buildSchedulerRosterRow } from "./utils/roster-view";
import { canGuestVote as computeCanGuestVote, shouldShowNameCard } from "../../lib/guest-voting";
import {
  buildGuestVoteStash,
  clearGuestVote,
  migrateGuestVote,
  popGuestVote,
  resumeGuestVoteMigration,
  stashGuestVote,
} from "../../lib/guest-vote-migration";
import { hasSubmittedSchedulerVote, isAttendingVote } from "../../lib/vote-utils";
import { getVoteTally } from "../../lib/vote-tally";
import {
  isSchedulerVotingOpen,
  resolveSchedulerVotingDeadline,
} from "../../lib/voting-deadlines";
import {
  canViewOtherVotesForUser,
  getVoteIdentityDisplayMode,
  resolveVoteVisibility,
  resolveVoteAnonymization,
  resolveEmbeddedPollPrivacy,
} from "../../lib/vote-visibility";
import {
  anonymizeUsers,
  buildAnonymousIdentityMap,
} from "../../lib/anonymous-voter-identities";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calendar-styles.css";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { "en-US": enUS },
});

// Tailwind color classes for a roster status badge. "Voted" is emerald; every
// other state (Pending / Hidden / — / Pending invite) is slate, except pending
// invites which use amber to match the dashed-invite styling.
function rosterStatusBadgeClass(row) {
  if (row?.isPendingInvite) return "text-amber-600 dark:text-amber-300";
  if (row?.statusValue === "voted") return "text-emerald-600 dark:text-emerald-300";
  return "text-slate-400 dark:text-slate-500";
}

// Identity & email-privacy (Model Y): creator-typed addresses that are NOT
// registered participants and not the creator's own email. The server derives +
// gates the registered-participant attendee set from each participant's chosen
// public identifier + calendar-share consent, so only these truly-external
// addresses are sent as `externalAttendees` on finalize/reschedule. The server
// additionally drops any that resolve to a registered user's email (backstop).
function computeExternalAttendees(parsedEmails, creatorEmail, participantEmails) {
  const participantEmailSet = new Set(
    (participantEmails || []).map((email) => normalizeEmail(email)).filter(Boolean)
  );
  const normalizedCreator = normalizeEmail(creatorEmail);
  return normalizeEmailList(parsedEmails).filter((email) => {
    const normalized = normalizeEmail(email);
    return (
      Boolean(normalized) &&
      normalized !== normalizedCreator &&
      !participantEmailSet.has(normalized)
    );
  });
}

export default function SchedulerPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    settings,
    archivePoll,
    unarchivePoll,
    isArchived,
    recentInvitees,
    publicIdentifierType,
    linkedCalendarEmail,
    shareEmailForCalendar,
  } = useUserSettings();
  // Viewer context for the unified add-to-calendar prominence model (§4.1).
  const calendarViewer = useMemo(
    () => ({ publicIdentifierType, linkedCalendarEmail, shareEmailForCalendar }),
    [publicIdentifierType, linkedCalendarEmail, shareEmailForCalendar]
  );
  const { autoVoteEnabled, autoVoteMode } = useAutoVoteSettings(user?.uid);
  const { friends } = useFriends();
  const { getGroupColor, groups } = useQuestingGroups();
  const { handleAvatarClick: schedulerHandleAvatarClick, handleUserClick: schedulerHandleUserClick, modalProps: schedulerModalProps } = useProfileInteraction();
  const { removeLocal: removeNotification } = useNotifications();
  const {
    scheduler,
    schedulerDocRef,
    questingGroup,
    slots,
    allVotes,
    canReadAllVotes,
    canReadPeerVoteStatus,
    rosterStatusKnown,
    userVote,
    userVoteRef,
  } = useSchedulerData({ schedulerId: id, user });
  const [view, setView] = useState("list");
  const [draftVotes, setDraftVotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [sortMode, setSortMode] = useState("preferred");
  const [requiredAttendance, setRequiredAttendance] = useState([]);
  const [noTimesWork, setNoTimesWork] = useState(false);
  const [finalizeSlotId, setFinalizeSlotId] = useState(null);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [eventTitle, setEventTitle] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [eventDuration, setEventDuration] = useState(240);
  const [eventAttendees, setEventAttendees] = useState("");
  const [createCalendarEvent, setCreateCalendarEvent] = useState(true);
  const [deleteOldEvent, setDeleteOldEvent] = useState(true);
  const [isReschedule, setIsReschedule] = useState(false);
  const [selectedCalendarId, setSelectedCalendarId] = useState("");
  const [pendingVotesOpen, setPendingVotesOpen] = useState(false);
  const [pendingFinalizeSlotId, setPendingFinalizeSlotId] = useState(null);
  const [pendingFinalizeBusy, setPendingFinalizeBusy] = useState(false);
  const [requiredFinalizeWarningOpen, setRequiredFinalizeWarningOpen] = useState(false);
  const [requiredFinalizeSummary, setRequiredFinalizeSummary] = useState([]);
  const [requiredFinalizeChecking, setRequiredFinalizeChecking] = useState(false);
  const [finalizeEmbeddedChoiceOpen, setFinalizeEmbeddedChoiceOpen] = useState(false);
  const [finalizeOutstandingEmbeddedPolls, setFinalizeOutstandingEmbeddedPolls] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneTitle, setCloneTitle] = useState("");
  const [cloneInvites, setCloneInvites] = useState([]);
  const [cloneInviteInput, setCloneInviteInput] = useState("");
  const [cloneInviteError, setCloneInviteError] = useState(null);
  const [cloneSaving, setCloneSaving] = useState(false);
  const [cloneClearVotes, setCloneClearVotes] = useState(false);
  const [cloneGroupId, setCloneGroupId] = useState(null);
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [restoreSaving, setRestoreSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteUpdateCalendar, setDeleteUpdateCalendar] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [invitePromptOpen, setInvitePromptOpen] = useState(false);
  const [invitePromptBusy, setInvitePromptBusy] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveSaving, setLeaveSaving] = useState(false);
  const [nudgeSending, setNudgeSending] = useState(false);
  const [repostSending, setRepostSending] = useState(false);
  const [calendarSyncing, setCalendarSyncing] = useState(false);
  const [removeMemberOpen, setRemoveMemberOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [revokeInviteOpen, setRevokeInviteOpen] = useState(false);
  const [inviteToRevoke, setInviteToRevoke] = useState(null);
  const [copyVotesOpen, setCopyVotesOpen] = useState(false);
  const [blockingSchedulersById, setBlockingSchedulersById] = useState({});
  // Phase 3a-cascade T4.5 — propagate-votes modal surfaces after the user
  // retracts attending state on a finalized poll's winning slot. Asks
  // whether to restore prior votes on other open polls whose
  // auto-rsvp-cascade overrides will clear once the busyWindow retracts.
  const [propagateModal, setPropagateModal] = useState({
    open: false,
    candidates: [],
    contextLabel: null,
    applying: false,
  });
  const {
    embeddedPolls,
    embeddedPollsLoading,
    embeddedPollVoteCounts,
    embeddedPollStatusKnown,
    embeddedVotesByPoll,
    embeddedMyVotes,
    setEmbeddedMyVotes,
    embeddedVoteDrafts,
    setEmbeddedVoteDrafts,
  } = useSchedulerEmbeddedPollVotes({
    schedulerId: id,
    userId: user?.uid || null,
    isCreator: scheduler.data?.creatorId === user?.uid,
    schedulerVoteVisibility: scheduler.data?.voteVisibility ?? null,
    schedulerVoteAnonymization: scheduler.data?.voteAnonymization ?? null,
    schedulerHideVoterIdentities: scheduler.data?.hideVoterIdentities === true,
  });
  const [embeddedSubmittingByPoll, setEmbeddedSubmittingByPoll] = useState({});
  const [embeddedClearingByPoll, setEmbeddedClearingByPoll] = useState({});
  const [embeddedVoteErrors, setEmbeddedVoteErrors] = useState({});
  const [embeddedLifecycleBusyByPoll, setEmbeddedLifecycleBusyByPoll] = useState({});
  const [embeddedOptionNoteViewer, setEmbeddedOptionNoteViewer] = useState(null);
  const embeddedPollCardRefs = useRef({});
  const embeddedHighlightTimerRef = useRef(null);
  const [targetEmbeddedPollId, setTargetEmbeddedPollId] = useState(null);
  const [handledEmbeddedPollId, setHandledEmbeddedPollId] = useState(null);
  const [highlightedEmbeddedPollId, setHighlightedEmbeddedPollId] = useState(null);
  const isLocked = scheduler.data?.status !== "OPEN";
  const sessionVotingDeadline = resolveSchedulerVotingDeadline(scheduler.data);
  const sessionVotingDeadlineMs = sessionVotingDeadline?.getTime?.() ?? null;
  const [sessionDeadlineTick, setSessionDeadlineTick] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(sessionVotingDeadlineMs)) return undefined;
    const remainingMs = sessionVotingDeadlineMs - Date.now();
    if (remainingMs <= 0) return undefined;
    const timer = window.setTimeout(
      () => setSessionDeadlineTick((current) => current + 1),
      Math.min(remainingMs + 25, 2_147_000_000)
    );
    return () => window.clearTimeout(timer);
  }, [sessionDeadlineTick, sessionVotingDeadlineMs]);
  // Render-time evaluation plus the boundary timer above makes the page flip
  // closed without a refresh while keeping one canonical helper predicate.
  const sessionVotingNow = Date.now();
  const sessionVotingOpen = isSchedulerVotingOpen(scheduler.data, sessionVotingNow);
  const isVotingInputLocked = isLocked || !sessionVotingOpen;
  const previousWinnerSlotId = scheduler.data?.rescheduledFrom?.slotId
    || scheduler.data?.calendarSync?.rescheduled?.fromSlotId
    || null;
  const isPollArchived = isArchived(id);
  const isCreator = scheduler.data?.creatorId === user?.uid;
  const normalizedUserEmail = normalizeEmail(user?.email) || null;
  const pollTimeZone = scheduler.data?.timezone || null;
  const displayTimeZone = useMemo(
    () => resolveDisplayTimeZone({ pollTimeZone, settings }),
    [pollTimeZone, settings]
  );
  const showTimeZone = useMemo(() => shouldShowTimeZone(settings), [settings]);
  const pendingInviteEmails = useMemo(
    () => scheduler.data?.pendingInvites || [],
    [scheduler.data?.pendingInvites]
  );
  const pendingInviteEmailSet = useMemo(
    () =>
      new Set(
        pendingInviteEmails
          .map((email) => normalizeEmail(email))
          .filter(Boolean)
      ),
    [pendingInviteEmails]
  );
  const isGroupMember = Boolean(
    scheduler.data?.questingGroupId &&
      user?.uid &&
      questingGroup.data?.memberIds?.includes(user.uid)
  );
  const isExplicitParticipant = useMemo(() => {
    return Boolean(user?.uid && scheduler.data?.participantIds?.includes(user.uid));
  }, [scheduler.data?.participantIds, user?.uid]);
  const isPendingInvite = useMemo(
    () => (normalizedUserEmail ? pendingInviteEmailSet.has(normalizedUserEmail) : false),
    [normalizedUserEmail, pendingInviteEmailSet]
  );
  const isAcceptedParticipant = useMemo(() => {
    if (isGroupMember) return true;
    if (!isExplicitParticipant) return false;
    return !isPendingInvite;
  }, [isGroupMember, isExplicitParticipant, isPendingInvite]);
  // A link-shared guest (anonymous user) may vote without being a participant.
  // Mirrors the rules-layer `isLinkSharedGuestVoteAllowed` allowance: anon +
  // allowLinkSharing + voting still open. Real users never take this path.
  const canGuestVote = useMemo(
    () => computeCanGuestVote({ user, scheduler: scheduler.data, isLocked: isVotingInputLocked }),
    [user, scheduler.data, isVotingInputLocked]
  );
  const [guestName, setGuestName] = useState("");
  // Post-submit "make an account" nudge + guest->real vote migration (Task 7).
  const [showGuestNudge, setShowGuestNudge] = useState(false);
  const [guestConverting, setGuestConverting] = useState(false);
  const [guestConflict, setGuestConflict] = useState(null);
  const guestConflictResolverRef = useRef(null);
  const [calendarView, setCalendarView] = useState("month");
  const [calendarDate, setCalendarDate] = useState(new Date());
  // Chunk E4 — overlay visibility ternary control. Defaults to
  // "overlapping" so users see only overlays that intersect a poll slot.
  const [overlayVisibilityMode, setOverlayVisibilityMode] = useState("overlapping");
  const [expandedSlots, setExpandedSlots] = useState({});

  // Chunk E4 — subscribe to the user's availability data sources used to
  // render translucent overlays in the week + day calendar views.
  // `useUserLinkedCalendars` is aliased to avoid colliding with the
  // local `linkedCalendars` (which derives from settings.googleCalendarIds
  // and is used for finalize-event creation).
  const availabilityEvents = useAvailabilityEvents(user?.uid);
  const availabilityMarks = useAvailabilityMarks(user?.uid);
  const userLinkedCalendars = useUserLinkedCalendars(user?.uid);
  const finalizedSessions = useFinalizedSessions(user?.uid);
  const questingGroupMemberIds = useMemo(
    () => questingGroup.data?.memberIds || [],
    [questingGroup.data?.memberIds]
  );
  const { profiles: questingGroupMemberProfiles } = useUserProfilesByIds(questingGroupMemberIds);
  const questingGroupMembers = useMemo(() => {
    if (!questingGroupMemberIds.length) return [];
    return questingGroupMemberIds
      .map((id) => questingGroupMemberProfiles[id]?.email)
      .filter(Boolean)
      .map((email) => normalizeEmail(email));
  }, [questingGroupMemberIds, questingGroupMemberProfiles]);
  const questingGroupMemberSet = useMemo(
    () => new Set(questingGroupMemberIds),
    [questingGroupMemberIds]
  );
  const questingGroupMemberEmailSet = useMemo(
    () => buildEmailSet(questingGroupMembers),
    [questingGroupMembers]
  );
  const questingGroupName =
    questingGroup.data?.name || scheduler.data?.questingGroupName || null;
  const discordMessageUrl = scheduler.data?.discord?.messageUrl || null;
  const discordEventUrl = scheduler.data?.discord?.scheduledEventUrl || null;
  const discordStatus = scheduler.data?.discord?.messageId
    ? "Posted to Discord"
    : scheduler.data?.questingGroupId && questingGroup.data?.discord?.channelId
      ? "Discord linked"
      : scheduler.data?.questingGroupId
        ? "Discord not linked"
        : null;
  const googleCalendarUrl = useMemo(() => {
    return buildGoogleCalendarEventUrl({
      eventId: scheduler.data?.googleEventId,
      calendarId: scheduler.data?.googleCalendarId || "primary",
    });
  }, [scheduler.data?.googleEventId, scheduler.data?.googleCalendarId]);
  const calendarSyncState = scheduler.data?.calendarSync?.state || null;
  const calendarSyncEnabled = scheduler.data?.calendarSyncEnabled !== false && Boolean(scheduler.data?.googleEventId);
  const latestNagAt = useMemo(() => {
    return resolveLatestNagDate(
      scheduler.data?.nagState,
      scheduler.data?.discord?.nudgeLastSentAt
    );
  }, [scheduler.data?.discord?.nudgeLastSentAt, scheduler.data?.nagState]);
  const normalizedAutoNag = normalizeAutoNagSettings(scheduler.data?.autoNag);
  const activeAutoNagSettings = normalizedAutoNag.valid ? normalizedAutoNag.settings : null;
  const autoNagEnabled = activeAutoNagSettings?.enabled === true;
  const earliestSlotAt = useMemo(() => {
    return (slots.data || [])
      .map((slot) => {
        const rawStart = slot?.start;
        const start = rawStart?.toDate?.() || new Date(rawStart);
        return Number.isFinite(start?.getTime?.()) ? start : null;
      })
      .filter(Boolean)
      .sort((left, right) => left.getTime() - right.getTime())[0] || null;
  }, [slots.data]);
  const questingGroupColor = useMemo(() => {
    const groupId = scheduler.data?.questingGroupId;
    if (!groupId) return null;
    return getGroupColor(groupId);
  }, [getGroupColor, scheduler.data?.questingGroupId]);
  const cloneSelectedGroup = useMemo(() => {
    if (!cloneGroupId) return null;
    const match = groups.find((group) => group.id === cloneGroupId);
    if (match) return match;
    if (cloneGroupId === scheduler.data?.questingGroupId) {
      return {
        id: cloneGroupId,
        name: questingGroup.data?.name || scheduler.data?.questingGroupName || "Questing group",
        members: questingGroupMembers || [],
      };
    }
    return {
      id: cloneGroupId,
      name: scheduler.data?.questingGroupName || "Questing group",
      members: [],
    };
  }, [
    cloneGroupId,
    groups,
    questingGroup.data,
    questingGroupMembers,
    scheduler.data?.questingGroupId,
    scheduler.data?.questingGroupName,
  ]);
  const cloneGroupMembers = useMemo(
    () => (cloneSelectedGroup?.members || []).filter(Boolean),
    [cloneSelectedGroup]
  );
  const cloneGroupMemberEmails = useMemo(
    () => normalizeEmailList(cloneGroupMembers),
    [cloneGroupMembers]
  );
  const cloneGroupMemberSet = useMemo(
    () => buildEmailSet(cloneGroupMemberEmails),
    [cloneGroupMemberEmails]
  );
  const cloneGroupColor = useMemo(
    () => (cloneSelectedGroup?.id ? getGroupColor(cloneSelectedGroup.id) : null),
    [cloneSelectedGroup?.id, getGroupColor]
  );
  const cloneInviteSet = useMemo(() => buildEmailSet(cloneInvites), [cloneInvites]);
  const cloneRecommendedEmails = useMemo(() => {
    const userEmail = user?.email ? normalizeEmail(user.email) : null;
    return friends
      .map((email) => normalizeEmail(email))
      .filter(Boolean)
      .filter((email) => email !== userEmail)
      .filter((email) => !cloneInviteSet.has(email))
      .filter((email) => !cloneGroupMemberSet.has(email));
  }, [friends, cloneInviteSet, cloneGroupMemberSet, user?.email]);
  const profileEmails = useMemo(() => {
    return normalizeEmailList([
      ...questingGroupMembers,
      ...cloneGroupMemberEmails,
      ...cloneInvites,
      ...cloneRecommendedEmails,
    ]);
  }, [questingGroupMembers, cloneGroupMemberEmails, cloneInvites, cloneRecommendedEmails]);
  const { enrichUsers } = useUserProfiles(profileEmails);
  const groupUsers = useMemo(() => {
    if (questingGroupMemberIds.length) {
      return questingGroupMemberIds
        .map((id) => {
          const profile = questingGroupMemberProfiles[id];
          if (!profile) return null;
          return {
            ...profile,
            email: profile.email ? normalizeEmail(profile.email) : profile.email,
            avatar: profile.photoURL || null,
          };
        })
        .filter(Boolean);
    }
    return enrichUsers(questingGroupMembers);
  }, [questingGroupMemberIds, questingGroupMemberProfiles, enrichUsers, questingGroupMembers]);
  const cloneGroupUsers = useMemo(
    () => enrichUsers(cloneGroupMembers),
    [enrichUsers, cloneGroupMembers]
  );
  const cloneInviteUsers = useMemo(() => enrichUsers(cloneInvites), [enrichUsers, cloneInvites]);
  const cloneRecommendedUsers = useMemo(
    () => enrichUsers(cloneRecommendedEmails),
    [enrichUsers, cloneRecommendedEmails]
  );
  const cloneGroupOptions = useMemo(() => {
    const base = groups.map((group) => ({
      id: group.id,
      name: group.name,
      members: group.members?.length || 0,
    }));
    if (scheduler.data?.questingGroupId && !base.some((item) => item.id === scheduler.data.questingGroupId)) {
      base.unshift({
        id: scheduler.data.questingGroupId,
        name: scheduler.data.questingGroupName || "Current group",
        members: 0,
      });
    }
    return base;
  }, [groups, scheduler.data?.questingGroupId, scheduler.data?.questingGroupName]);

  useEffect(() => {
    if (!cloneGroupId) return;
    setCloneInvites((prev) =>
      prev.filter((email) => !cloneGroupMemberSet.has(normalizeEmail(email)))
    );
  }, [cloneGroupId, cloneGroupMemberSet]);

  useEffect(() => {
    if (!scheduler.data || !user?.email || !id) return;
    if (isCreator) return;
    const participantMatch = scheduler.data.participantIds?.includes(user?.uid);
    if (isGroupMember) {
      setInvitePromptOpen(false);
      return;
    }
    if (isPendingInvite) {
      setInvitePromptOpen(true);
      return;
    }
    if (scheduler.data.allowLinkSharing && !participantMatch) {
      setInvitePromptOpen(true);
    } else {
      setInvitePromptOpen(false);
    }
  }, [id, scheduler.data, user?.email, user?.uid, isCreator, isGroupMember, isPendingInvite]);

  useEffect(() => {
    if (!userVote.data) return;
    setDraftVotes(userVote.data.votes || {});
    setNoTimesWork(Boolean(userVote.data.noTimesWork));
    // Seed the guest name from the loaded vote so editing after a reload doesn't
    // overwrite the saved name with the empty initial state (the name card is
    // skipped when a vote already exists, so it would otherwise never repopulate).
    if (userVote.data.guestName) setGuestName(userVote.data.guestName);
  }, [userVote.data]);

  // Engine-derived per-slot fields (Phase 2 Chunk D, refined in E1). These
  // are written by the auto-vote engine and surfaced via SlotIndicator.
  const voteAutoSourceBySlotId = useMemo(
    () => userVote.data?.voteAutoSource || {},
    [userVote.data?.voteAutoSource]
  );
  const voteAutoReasonBySlotId = useMemo(
    () => userVote.data?.voteAutoReason || {},
    [userVote.data?.voteAutoReason]
  );
  const voteProposedBySlotId = useMemo(
    () => userVote.data?.voteProposed || {},
    [userVote.data?.voteProposed]
  );
  // Phase 3 — userTouchedSlots tells the engine that this slot's vote is the
  // user's deliberate choice and should not be re-cast/re-submitted (Tier 2)
  // or re-cast unless the affecting source actually changed (Tier 3).
  const userTouchedSlotsSet = useMemo(() => {
    const arr = userVote.data?.userTouchedSlots;
    return new Set(Array.isArray(arr) ? arr : []);
  }, [userVote.data?.userTouchedSlots]);

  // Friendly-name lookup for the no-group-match info-mode tooltip in
  // SlotIndicator. The user's own questing groups already include the
  // poll's group + any group their marks are scoped to (since marks are
  // user-owned and reference groups they belong to).
  const groupNames = useMemo(() => {
    const map = {};
    (groups || []).forEach((g) => {
      if (g?.id && g?.name) map[g.id] = g.name;
    });
    if (scheduler.data?.questingGroupId && scheduler.data?.questingGroupName) {
      map[scheduler.data.questingGroupId] = scheduler.data.questingGroupName;
    }
    if (questingGroup.data?.id && questingGroup.data?.name) {
      map[questingGroup.data.id] = questingGroup.data.name;
    }
    return map;
  }, [
    groups,
    scheduler.data?.questingGroupId,
    scheduler.data?.questingGroupName,
    questingGroup.data?.id,
    questingGroup.data?.name,
  ]);

  function hasSubmittedEmbeddedVote(poll, voteDoc) {
    return hasSubmittedVoteForPoll(poll, voteDoc);
  }

  function openEmbeddedOptionNoteViewer(pollTitle, option) {
    const note = String(option?.note || "").trim();
    if (!note) return;
    setEmbeddedOptionNoteViewer({
      pollTitle: String(pollTitle || "Add-on poll"),
      optionLabel: String(option?.label || "Option"),
      note,
    });
  }

  function canVoteEmbeddedPoll(poll) {
    // Deadline gate lives in the pure helper (unit-tested). Wall-clock read
    // stays here so the predicate stays deterministic in tests.
    return isEmbeddedPollVotable({
      poll,
      isAcceptedParticipant,
      canGuestVote,
      schedulerCancelled: scheduler.data?.status === "CANCELLED",
      now: Date.now(),
    });
  }

  // Scroll an embedded poll card into view and pulse the highlight — reused by
  // the deep-link effect's behavior so the required-poll gate can point a blocked
  // voter straight at the add-on poll they still need to answer. The pulse timer
  // is tracked on a ref and any prior one is cleared, so repeated blocks don't
  // stack overlapping timers.
  function focusEmbeddedPoll(pollId) {
    if (!pollId) return;
    const node = embeddedPollCardRefs.current[pollId];
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setHighlightedEmbeddedPollId(pollId);
    if (embeddedHighlightTimerRef.current) {
      clearTimeout(embeddedHighlightTimerRef.current);
    }
    embeddedHighlightTimerRef.current = setTimeout(() => {
      embeddedHighlightTimerRef.current = null;
      setHighlightedEmbeddedPollId((current) => (current === pollId ? null : current));
    }, 4000);
  }

  // Shared guard for every path that commits an ATTENDING session vote (explicit
  // "Submit votes", click-to-override on an engine-proposed slot, and the restore
  // handlers). Returns true — and surfaces the descriptive toast + scrolls to the
  // first offender — when a still-open required add-on poll is unanswered; false
  // when it's clear to proceed. Fail-open while embedded polls are still loading
  // (the creator's finalize warning is the backstop). The pure helper itself
  // treats a not-yet-loaded my-vote entry as non-blocking, so this never
  // false-blocks during the load / post-submit-echo window.
  function isAttendingVoteBlockedByRequiredPolls() {
    if (embeddedPollsLoading) return false;
    const unansweredRequired = getUnansweredRequiredEmbeddedPolls({
      embeddedPolls,
      myVotesByPollId: embeddedMyVotes,
      isAcceptedParticipant,
      canGuestVote,
      schedulerCancelled: scheduler.data?.status === "CANCELLED",
      now: Date.now(),
    });
    if (unansweredRequired.length === 0) return false;
    const titles = unansweredRequired.map(
      (poll) => `"${String(poll?.title || "").trim() || "Untitled poll"}"`
    );
    const message =
      unansweredRequired.length === 1
        ? `Answer the required add-on poll ${titles[0]} before submitting your vote.`
        : `Answer these required add-on polls before submitting your vote: ${titles.join(", ")}.`;
    toast.error(message);
    focusEmbeddedPoll(unansweredRequired[0].id);
    return true;
  }

  useEffect(() => {
    const pollId = parseEmbeddedPollIdFromSearch(location.search);
    setTargetEmbeddedPollId(pollId);
    setHandledEmbeddedPollId(null);
    if (!pollId) {
      setHighlightedEmbeddedPollId(null);
    }
  }, [location.search]);

  useEffect(() => {
    if (!targetEmbeddedPollId || embeddedPollsLoading || handledEmbeddedPollId === targetEmbeddedPollId) return;
    const hasPoll = embeddedPolls.some((poll) => poll.id === targetEmbeddedPollId);
    if (!hasPoll) return;
    const targetNode = embeddedPollCardRefs.current[targetEmbeddedPollId];
    if (!targetNode || typeof targetNode.scrollIntoView !== "function") return;

    targetNode.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedEmbeddedPollId(targetEmbeddedPollId);
    setHandledEmbeddedPollId(targetEmbeddedPollId);

    const timer = setTimeout(() => {
      setHighlightedEmbeddedPollId((current) =>
        current === targetEmbeddedPollId ? null : current
      );
    }, 4000);

    return () => clearTimeout(timer);
  }, [
    embeddedPolls,
    embeddedPollsLoading,
    handledEmbeddedPollId,
    targetEmbeddedPollId,
  ]);

  const explicitParticipantIds = useMemo(
    () => scheduler.data?.participantIds || [],
    [scheduler.data?.participantIds]
  );
  const { profiles: explicitParticipantProfilesById } = useUserProfilesByIds(
    explicitParticipantIds
  );

  const profilesByUserId = useMemo(() => {
    return {
      ...(explicitParticipantProfilesById || {}),
      ...(questingGroupMemberProfiles || {}),
    };
  }, [explicitParticipantProfilesById, questingGroupMemberProfiles]);

  const pollPriorityAtMs = useMemo(() => {
    if (scheduler.data?.status !== "FINALIZED" || !scheduler.data?.winningSlotId) return null;
    const slotId = scheduler.data.winningSlotId;
    const perSlot = scheduler.data?.finalizedSlotPriorityAtMs?.[slotId] ?? null;
    return perSlot ?? scheduler.data?.finalizedAtMs ?? null;
  }, [
    scheduler.data?.finalizedAtMs,
    scheduler.data?.finalizedSlotPriorityAtMs,
    scheduler.data?.status,
    scheduler.data?.winningSlotId,
  ]);

  const mergedVoteDocs = useMemo(() => {
    return user?.uid &&
      userVote.data &&
      !allVotes.data.some((voteDoc) => voteDoc?.id === user.uid)
      ? [...allVotes.data, { id: user.uid, ...userVote.data }]
      : allVotes.data;
  }, [allVotes.data, user?.uid, userVote.data]);

  const { tallies, slotVoters } = useMemo(() => {
    return buildEffectiveTallies({
      schedulerId: id,
      schedulerStatus: scheduler.data?.status || "OPEN",
      pollPriorityAtMs,
      slots: slots.data,
      voteDocs: mergedVoteDocs,
      profilesById: profilesByUserId,
    });
  }, [
    mergedVoteDocs,
    id,
    pollPriorityAtMs,
    profilesByUserId,
    scheduler.data?.status,
    slots.data,
  ]);

  const visibleVoteDocs = useMemo(() => {
    if (
      user?.uid &&
      userVote.data &&
      !allVotes.data.some((voteDoc) => voteDoc?.id === user.uid)
    ) {
      return [...allVotes.data, { id: user.uid, ...userVote.data }];
    }
    return allVotes.data;
  }, [allVotes.data, user?.uid, userVote.data]);
  const showSchedulerVoteDetails = canReadAllVotes;
  const showSchedulerVoteProgress = canReadPeerVoteStatus;
  const schedulerVoteIdentityDisplayMode = getVoteIdentityDisplayMode({
    isCreator,
    hideVoterIdentities: scheduler.data?.hideVoterIdentities,
    voteAnonymization: scheduler.data?.voteAnonymization,
    detailsRevealed: showSchedulerVoteDetails,
  });
  // Identity-render is fully governed by the reveal-aware display mode (R9): the
  // per-slot detail cards show identities (real names, or aliases when
  // anonymized) once the mode is no longer "hidden" — i.e. once details are
  // revealed. Under hideVoterIdentities the mode is "hidden" until the reveal
  // trigger, THEN flips to "named", so identities appear after reveal (matching
  // the dashboard SessionCard, which already derives from the same mode).
  const showSchedulerVoterIdentities = schedulerVoteIdentityDisplayMode !== "hidden";

  const { infoBySlotId: userBlockersBySlotId } = useMemo(() => {
    return buildUserBlockInfo({
      schedulerId: id,
      slots: slots.data,
      userVoteDoc: userVote.data,
    });
  }, [id, slots.data, userVote.data]);

  const blockerSchedulerIds = useMemo(() => {
    const ids = new Set();
    Object.values(userBlockersBySlotId || {}).forEach((blocker) => {
      if (blocker?.sourceSchedulerId) ids.add(blocker.sourceSchedulerId);
    });
    return Array.from(ids);
  }, [userBlockersBySlotId]);

  useEffect(() => {
    if (!blockerSchedulerIds.length) {
      setBlockingSchedulersById((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    let cancelled = false;
    fetchSchedulersByIds(blockerSchedulerIds)
      .then((docs) => {
        if (cancelled) return;
        setBlockingSchedulersById(docs || {});
      })
      .catch((err) => {
        console.warn("Failed to fetch blocking schedulers:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [blockerSchedulerIds.join("|")]);

  const explicitParticipantProfiles = useMemo(() => {
    if (!explicitParticipantIds.length) return [];
    return explicitParticipantIds
      .map((id) => explicitParticipantProfilesById[id])
      .filter(Boolean);
  }, [explicitParticipantIds, explicitParticipantProfilesById]);
  const explicitParticipantEmails = useMemo(
    () =>
      explicitParticipantProfiles
        .map((profile) => profile.email)
        .filter(Boolean)
        .map((email) => normalizeEmail(email)),
    [explicitParticipantProfiles]
  );
  const participantEmails = useMemo(() => {
    const merged = new Set([
      ...explicitParticipantEmails,
      ...questingGroupMembers,
    ]);
    return Array.from(merged);
  }, [explicitParticipantEmails, questingGroupMembers]);
  const { enrichUsers: enrichPendingInviteUsers } = useUserProfiles(pendingInviteEmails);
  const pendingInviteUsers = useMemo(
    () => enrichPendingInviteUsers(pendingInviteEmails),
    [enrichPendingInviteUsers, pendingInviteEmails]
  );
  const submittedVoteDocs = useMemo(
    () => visibleVoteDocs.filter((voteDoc) => hasSubmittedSchedulerVote(voteDoc)),
    [visibleVoteDocs]
  );
  const voterEmails = useMemo(
    () => submittedVoteDocs.map((voteDoc) => voteDoc.userEmail).filter(Boolean),
    [submittedVoteDocs]
  );
  const uniqueEmails = useMemo(() => {
    const set = new Set([...(participantEmails || []), ...(voterEmails || [])]);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [participantEmails, voterEmails]);
  const colorMap = useMemo(() => buildColorMap(uniqueEmails), [uniqueEmails]);
  const voteMapById = useMemo(
    () => new Map(visibleVoteDocs.map((voteDoc) => [voteDoc.id, voteDoc])),
    [visibleVoteDocs]
  );
  const submittedVoteMapById = useMemo(
    () => new Map(submittedVoteDocs.map((voteDoc) => [voteDoc.id, voteDoc])),
    [submittedVoteDocs]
  );
  const voteMapByEmail = useMemo(
    () =>
      new Map(
        visibleVoteDocs
          .filter((voteDoc) => voteDoc.userEmail)
          .map((voteDoc) => [normalizeEmail(voteDoc.userEmail), voteDoc])
      ),
    [visibleVoteDocs]
  );
  const submittedVoteMapByEmail = useMemo(
    () =>
      new Map(
        submittedVoteDocs
          .filter((voteDoc) => voteDoc.userEmail)
          .map((voteDoc) => [normalizeEmail(voteDoc.userEmail), voteDoc])
      ),
    [submittedVoteDocs]
  );
  const submittedVoteIdSet = useMemo(
    () =>
      new Set(
        submittedVoteDocs
          .map((voteDoc) => String(voteDoc?.id || "").trim())
          .filter(Boolean)
      ),
    [submittedVoteDocs]
  );
  const participants = useMemo(() => {
    const combined = new Map();
    explicitParticipantIds.forEach((id) => {
      const profile = explicitParticipantProfilesById[id];
      if (profile) combined.set(id, profile);
    });
    questingGroupMemberIds.forEach((id) => {
      const profile = questingGroupMemberProfiles[id];
      if (profile) combined.set(id, profile);
    });
    return Array.from(combined.values()).map((profile) => {
      const vote =
        voteMapById.get(profile.id) ||
        (profile.email ? voteMapByEmail.get(normalizeEmail(profile.email)) : null);
      const submittedVote =
        submittedVoteMapById.get(profile.id) ||
        (profile.email ? submittedVoteMapByEmail.get(normalizeEmail(profile.email)) : null);
      const email = profile.email ? normalizeEmail(profile.email) : vote?.userEmail || null;
      return {
        ...profile,
        email,
        avatar: vote?.userAvatar || profile.photoURL || null,
        hasVoted: Boolean(submittedVote),
        isGroupMember: questingGroupMemberSet.has(profile.id),
        isPendingInvite: email ? pendingInviteEmailSet.has(normalizeEmail(email)) : false,
      };
    });
  }, [
    explicitParticipantIds,
    explicitParticipantProfilesById,
    questingGroupMemberIds,
    questingGroupMemberProfiles,
    voteMapById,
    voteMapByEmail,
    submittedVoteMapById,
    submittedVoteMapByEmail,
    questingGroupMemberSet,
    pendingInviteEmailSet,
  ]);
  const nonGroupParticipants = useMemo(
    () => participants.filter((participant) => !participant.isGroupMember),
    [participants]
  );
  const participantMapByEmail = useMemo(
    () =>
      new Map(
        participants
          .filter((participant) => participant.email)
          .map((participant) => [normalizeEmail(participant.email), participant])
      ),
    [participants]
  );
  const requiredAttendanceOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    participants.forEach((participant) => {
      const email = normalizeEmail(participant.email);
      if (!email || seen.has(email)) return;
      seen.add(email);
      options.push({ ...participant, email });
    });
    return options.sort((a, b) => {
      const aLabel = (getUserLabel(a) || "").toLowerCase();
      const bLabel = (getUserLabel(b) || "").toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
  }, [participants]);
  const requiredAttendanceOptionsByEmail = useMemo(
    () => new Map(requiredAttendanceOptions.map((participant) => [participant.email, participant])),
    [requiredAttendanceOptions]
  );
  const requiredAttendanceSet = useMemo(
    () => new Set(requiredAttendance),
    [requiredAttendance]
  );
  const requiredAttendanceUsers = useMemo(
    () =>
      requiredAttendance
        .map((email) => requiredAttendanceOptionsByEmail.get(email))
        .filter(Boolean)
        .map((participant) => participant),
    [requiredAttendance, requiredAttendanceOptionsByEmail]
  );
  const requiredAttendanceLabel = useMemo(() => {
    if (requiredAttendance.length === 0) return "Anyone";
    const selected = requiredAttendance
      .map((email) => requiredAttendanceOptionsByEmail.get(email))
      .filter(Boolean);
    const first = selected[0];
    if (!first) return `${requiredAttendance.length} required`;
    const label = getUserLabel(first) || "Participant";
    if (selected.length > 1) {
      return `${label} +${selected.length - 1}`;
    }
    return label;
  }, [requiredAttendance, requiredAttendanceOptionsByEmail]);
  const requiredAttendanceTitle = useMemo(() => {
    if (requiredAttendance.length === 0) return "No required attendance filter";
    const names = requiredAttendance
      .map((email) => {
        const participant = requiredAttendanceOptionsByEmail.get(email);
        return safeParticipantLabel(participant);
      })
      .filter(Boolean);
    return `Required attendance: ${names.join(", ")}`;
  }, [requiredAttendance, requiredAttendanceOptionsByEmail]);
  const hasRequiredAttendanceFilter = requiredAttendance.length > 0;

  useEffect(() => {
    if (requiredAttendance.length === 0) return;
    const validSet = new Set(requiredAttendanceOptions.map((participant) => participant.email));
    const next = requiredAttendance.filter((email) => validSet.has(email));
    if (next.length === requiredAttendance.length) return;
    setRequiredAttendance(next);
  }, [requiredAttendance, requiredAttendanceOptions]);
  const pendingInviteNonParticipants = useMemo(
    () =>
      pendingInviteUsers.filter((invitee) => {
        const normalized = normalizeEmail(invitee.email);
        if (!normalized) return false;
        return !participantMapByEmail.has(normalized);
      }),
    [pendingInviteUsers, participantMapByEmail]
  );
  const groupUsersWithStatus = useMemo(
    () =>
      groupUsers.map((member) => {
        const vote =
          (member.id && voteMapById.get(member.id)) ||
          (member.email ? voteMapByEmail.get(normalizeEmail(member.email)) : null);
        const submittedVote =
          (member.id && submittedVoteMapById.get(member.id)) ||
          (member.email ? submittedVoteMapByEmail.get(normalizeEmail(member.email)) : null);
        return {
          ...member,
          email: member.email ? normalizeEmail(member.email) : member.email,
          avatar: vote?.userAvatar || member.avatar || member.photoURL || null,
          hasVoted: Boolean(submittedVote),
        };
      }),
    [groupUsers, voteMapById, voteMapByEmail, submittedVoteMapById, submittedVoteMapByEmail]
  );
  const schedulerAnonymousIdentityMap = useMemo(() => {
    if (schedulerVoteIdentityDisplayMode !== "anonymous") return null;
    const seededUsers = [...participants, ...groupUsersWithStatus];
    Object.values(slotVoters || {}).forEach((slotVoteUsers) => {
      seededUsers.push(...(slotVoteUsers?.preferred || []));
      seededUsers.push(...(slotVoteUsers?.feasible || []));
    });
    return buildAnonymousIdentityMap(seededUsers, { scopeKey: `scheduler:${id}` });
  }, [
    groupUsersWithStatus,
    id,
    participants,
    schedulerVoteIdentityDisplayMode,
    slotVoters,
  ]);
  const participantsForDisplay = useMemo(() => {
    if (schedulerVoteIdentityDisplayMode !== "anonymous") return participants;
    return anonymizeUsers(participants, schedulerAnonymousIdentityMap, {
      keyPrefix: "participants",
      currentUser: user,
    });
  }, [participants, schedulerAnonymousIdentityMap, schedulerVoteIdentityDisplayMode, user]);
  const groupUsersWithStatusForDisplay = useMemo(() => {
    if (schedulerVoteIdentityDisplayMode !== "anonymous") return groupUsersWithStatus;
    return anonymizeUsers(groupUsersWithStatus, schedulerAnonymousIdentityMap, {
      keyPrefix: "group-users",
      currentUser: user,
    });
  }, [
    groupUsersWithStatus,
    schedulerAnonymousIdentityMap,
    schedulerVoteIdentityDisplayMode,
    user,
  ]);
  const nonGroupParticipantsForDisplay = useMemo(() => {
    if (schedulerVoteIdentityDisplayMode !== "anonymous") return nonGroupParticipants;
    return anonymizeUsers(nonGroupParticipants, schedulerAnonymousIdentityMap, {
      keyPrefix: "non-group-participants",
      currentUser: user,
    });
  }, [
    nonGroupParticipants,
    schedulerAnonymousIdentityMap,
    schedulerVoteIdentityDisplayMode,
    user,
  ]);
  // Shared inputs for the per-row roster contract (buildSchedulerRosterRow).
  // `viewer.hasVoted` drives the reveal trigger; `settings`/`pollState` mirror
  // useSchedulerData's canReadAllVotes inputs so render matches the subscription.
  const rosterViewer = useMemo(
    () => ({ isCreator, hasVoted: hasSubmittedSchedulerVote(userVote.data) }),
    [isCreator, userVote.data]
  );
  const rosterSettings = useMemo(
    () => ({
      voteVisibility: resolveVoteVisibility(scheduler.data?.voteVisibility),
      voteAnonymization: resolveVoteAnonymization(scheduler.data?.voteAnonymization),
      hideVoterIdentities: scheduler.data?.hideVoterIdentities === true,
    }),
    [
      scheduler.data?.voteVisibility,
      scheduler.data?.voteAnonymization,
      scheduler.data?.hideVoterIdentities,
    ]
  );
  const rosterPollState = useMemo(
    () => ({
      allParticipantsVoted: scheduler.data?.votesAllSubmitted === true,
      isFinalized: scheduler.data?.status === "FINALIZED",
    }),
    [scheduler.data?.votesAllSubmitted, scheduler.data?.status]
  );
  const slotVotersForDisplay = useMemo(() => {
    if (schedulerVoteIdentityDisplayMode !== "anonymous") return slotVoters;
    return Object.fromEntries(
      Object.entries(slotVoters || {}).map(([slotId, slotVoteUsers]) => [
        slotId,
        {
          preferred: anonymizeUsers(
            slotVoteUsers?.preferred || [],
            schedulerAnonymousIdentityMap,
            { keyPrefix: `${slotId}-preferred`, currentUser: user }
          ),
          feasible: anonymizeUsers(
            slotVoteUsers?.feasible || [],
            schedulerAnonymousIdentityMap,
            { keyPrefix: `${slotId}-feasible`, currentUser: user }
          ),
        },
      ])
    );
  }, [schedulerAnonymousIdentityMap, schedulerVoteIdentityDisplayMode, slotVoters, user]);
  const participantIdSet = useMemo(
    () =>
      new Set(
        [...explicitParticipantIds, ...questingGroupMemberIds].filter(Boolean)
      ),
    [explicitParticipantIds, questingGroupMemberIds]
  );
  const nudgeEligibleParticipantIds = useMemo(() => {
    const participantIds = new Set(
      [...explicitParticipantIds, ...questingGroupMemberIds]
        .map((idValue) => String(idValue || "").trim())
        .filter(Boolean)
    );
    if (scheduler.data?.creatorId) {
      participantIds.delete(String(scheduler.data.creatorId));
    }
    return Array.from(participantIds);
  }, [explicitParticipantIds, questingGroupMemberIds, scheduler.data?.creatorId]);
  const sessionPollMissingNudgeUserIds = useMemo(
    () =>
      nudgeEligibleParticipantIds.filter(
        (participantId) => !submittedVoteIdSet.has(String(participantId))
      ),
    [nudgeEligibleParticipantIds, submittedVoteIdSet]
  );
  const hasRequiredEmbeddedNudgeTargets = useMemo(() => {
    if (nudgeEligibleParticipantIds.length === 0) return false;

    const requiredOpenPolls = (embeddedPolls || []).filter((poll) => {
      if (!poll?.required) return false;
      const pollStatus = String(poll?.status || "OPEN").toUpperCase();
      return pollStatus === "OPEN";
    });

    if (requiredOpenPolls.length === 0) return false;

    return requiredOpenPolls.some((poll) => {
      const pollVotes = embeddedVotesByPoll[poll.id] || [];
      const submittedVoterIds = new Set(
        (pollVotes || [])
          .filter((voteDoc) => hasSubmittedEmbeddedVote(poll, voteDoc))
          .map((voteDoc) => String(voteDoc?.id || "").trim())
          .filter(Boolean)
      );

      return nudgeEligibleParticipantIds.some(
        (participantId) => !submittedVoterIds.has(participantId)
      );
    });
  }, [embeddedPolls, embeddedVotesByPoll, nudgeEligibleParticipantIds]);
  const participantCount = participantIdSet.size;
  const knownVoteCount = submittedVoteDocs.length;
  // Participant-only submitted count for the "all votes in" completion +
  // notification gates: a guest vote (or a stale non-participant doc) must never
  // trip premature completion or an "everyone voted" notification. Mirrors the
  // trigger's participantSubmittedVoteCount. The DISPLAY count (knownVoteCount /
  // voteCount) stays guest-inclusive (C6.2 — guests are real voters).
  const participantSubmittedCount = useMemo(
    () =>
      submittedVoteDocs.filter(
        (voteDoc) =>
          participantIdSet.has(String(voteDoc?.id || "").trim()) &&
          voteDoc?.isGuest !== true,
      ).length,
    [submittedVoteDocs, participantIdSet],
  );
  // Aggregate "N voted" (layer A) is always visible to everyone, in every
  // privacy mode. When this viewer can load the per-voter list, prefer the live
  // realtime count (freshest, drives the optimistic "all votes in" math). When
  // they can't (e.g. hideVoterIdentities detaches the votes listener), fall back
  // to the denormalized scheduler counter so the count still shows, then to
  // self-only as a last resort.
  const voteCount = showSchedulerVoteProgress
    ? knownVoteCount
    : Number.isFinite(scheduler.data?.submittedVoteCount)
      ? scheduler.data.submittedVoteCount
      : hasSubmittedSchedulerVote(userVote.data)
        ? 1
        : 0;
  // PR-5: guest-aware tally from the denormalized scheduler counters. Used by the
  // SPACIOUS "Participants" summary, which breaks out participants vs guests when
  // any guest has voted; everywhere else uses the combined guest-inclusive count.
  const schedulerTally = useMemo(
    () => getVoteTally(scheduler.data || {}),
    [scheduler.data]
  );
  const allVotesIn = useMemo(() => {
    if (scheduler.data?.status !== "OPEN") return false;
    if (!participantCount) return false;
    if (scheduler.data?.votesAllSubmitted === true) return true;
    return showSchedulerVoteProgress && participantSubmittedCount >= participantCount;
  }, [
    participantSubmittedCount,
    participantCount,
    scheduler.data?.status,
    scheduler.data?.votesAllSubmitted,
    showSchedulerVoteProgress,
  ]);
  const winningSlot = useMemo(() => {
    const winningId = scheduler.data?.winningSlotId;
    if (!winningId) return null;
    return slots.data.find((slot) => slot.id === winningId) || null;
  }, [scheduler.data?.winningSlotId, slots.data]);

  // Layer-B privacy: slot ORDER reveals vote popularity. When this viewer is not
  // entitled to per-slot vote details (showSchedulerVoteDetails === false — e.g.
  // a non-creator under a restrictive voteVisibility while votes are still loaded
  // because identities aren't hidden), sort purely chronologically so the hidden
  // tallies never leak through ordering. sortResultSlots applies the
  // tally-weighted comparators only once details are revealed.
  const sortedSlots = useMemo(() => {
    const rows = slots.data.map((slot) => {
      const counts = tallies[slot.id] || { feasible: 0, preferred: 0 };
      return { ...slot, counts };
    });
    return sortResultSlots({
      slots: rows,
      sortMode,
      detailsRevealed: showSchedulerVoteDetails,
    });
  }, [slots.data, sortMode, tallies, showSchedulerVoteDetails]);

  const filteredSortedSlots = useMemo(
    () => {
      // Layer-B privacy: filtering by required attendance reveals WHO feasible-
      // voted WHICH slot. Only apply it when this viewer is entitled to per-slot
      // vote details; otherwise pass the (chronologically) sorted slots through.
      const base = showSchedulerVoteDetails
        ? filterSlotsByRequiredAttendance({
            slots: sortedSlots,
            slotVotersById: slotVoters,
            requiredEmails: requiredAttendance,
          })
        : sortedSlots;
      // Hide external calendar-created winning slot from list (shown in reschedule banner instead)
      const calRescheduled = scheduler.data?.calendarSync?.rescheduled;
      const appRescheduled = scheduler.data?.rescheduledFrom;
      const isExternalReschedule = Boolean(calRescheduled) && !appRescheduled;
      if (isExternalReschedule && scheduler.data?.winningSlotId) {
        const winId = scheduler.data.winningSlotId;
        return base.filter((s) => s.id !== winId || s.source !== "calendar");
      }
      return base;
    },
    [requiredAttendance, slotVoters, sortedSlots, scheduler.data, showSchedulerVoteDetails]
  );

  const slotsByDate = useMemo(() => {
    return [...slots.data].sort((a, b) => {
      const aTime = a.start ? new Date(a.start).getTime() : 0;
      const bTime = b.start ? new Date(b.start).getTime() : 0;
      return aTime - bTime;
    });
  }, [slots.data]);
  // Shared loading→error→empty→ready precedence for the proposed-times list.
  const slotListState = resolveLoadState({
    loading: slots.loading,
    error: slots.error,
    isEmpty: slots.data.length === 0,
  });

  const pastSlotIds = useMemo(() => {
    const now = Date.now();
    return new Set(
      slots.data
        .filter((slot) => slot.start && new Date(slot.start).getTime() < now)
        .map((slot) => slot.id)
    );
  }, [slots.data]);

  const allSlotsPast = useMemo(() =>
    slots.data.length > 0 && slots.data.every(s => pastSlotIds.has(s.id)),
    [slots.data, pastSlotIds]
  );

  const sessionEnded = useMemo(() => {
    if (!isLocked || !scheduler.data?.winningSlotId) return false;
    const winningSlot = slots.data.find(s => s.id === scheduler.data.winningSlotId);
    if (!winningSlot?.end) return false;
    return new Date(winningSlot.end) < new Date();
  }, [isLocked, scheduler.data?.winningSlotId, slots.data]);

  const rescheduleInfo = useMemo(() => {
    const calR = scheduler.data?.calendarSync?.rescheduled;
    const appR = scheduler.data?.rescheduledFrom;
    if (!calR && !appR) return null;
    const fromSlotId = calR?.fromSlotId || appR?.slotId;
    const fromSlot = slots.data.find((s) => s.id === fromSlotId);
    const isExternal = Boolean(calR) && !appR;
    return { fromSlot, fromSlotId, isExternal };
  }, [scheduler.data, slots.data]);

  const canCopyVotes = useMemo(() => {
    return canUserCopyVotes({
      slots: slots.data,
      userVoteDoc: userVote.data,
      nowMs: Date.now(),
    });
  }, [slots.data, userVote.data]);

  const calendarEvents = useMemo(() => {
    return slots.data.map((slot) => {
      const startRaw = slot.start ? new Date(slot.start) : new Date();
      const endRaw = slot.end ? new Date(slot.end) : startRaw;
      const displayStart = toDisplayDate(startRaw, displayTimeZone) || startRaw;
      const displayEnd = toDisplayDate(endRaw, displayTimeZone) || endRaw;
      const counts = tallies[slot.id] || { feasible: 0, preferred: 0 };
      const voters = slotVotersForDisplay[slot.id] || { preferred: [], feasible: [] };
      return {
        id: slot.id,
        start: displayStart,
        end: displayEnd,
        timeLabel: formatZonedTime(startRaw, displayTimeZone, "h:mm a", { showTimeZone }),
        rangeLabel: formatZonedTimeRange({
          start: startRaw,
          end: endRaw,
          timeZone: displayTimeZone,
          showTimeZone,
        }),
        preferredCount: counts.preferred,
        feasibleCount: counts.feasible,
        preferredVoters: voters.preferred,
        feasibleVoters: voters.feasible,
      };
    });
  }, [displayTimeZone, slotVotersForDisplay, slots.data, tallies]);

  const calendarEventCountByDay = useMemo(() => {
    return calendarEvents.reduce((counts, event) => {
      const dayKey = format(event.start, "yyyy-MM-dd");
      counts[dayKey] = (counts[dayKey] || 0) + 1;
      return counts;
    }, {});
  }, [calendarEvents]);

  // Audit A-2: when a poll's slots are all in a future month, open the calendar
  // grid on the earliest slot's month rather than an empty "today".
  // `calendarEvents[i].start` is already display-tz-shifted — the same basis the
  // grid compares `calendarDate` against. The one-shot is keyed by
  // `${id}:${displayTimeZone}`: per poll so it never fights the user's later
  // navigation, but re-armed once if the display timezone changes (settings load
  // asynchronously after first render) so a boundary slot that shifts months
  // when the real tz resolves still gets a single corrective navigation. The
  // helper returns null whenever a slot already falls in the shown month, so a
  // settled view is never yanked.
  const didInitCalendarMonthRef = useRef(null);
  useEffect(() => {
    if (slots.loading || calendarEvents.length === 0) return;
    const initKey = `${id}:${displayTimeZone}`;
    if (didInitCalendarMonthRef.current === initKey) return;
    didInitCalendarMonthRef.current = initKey;
    const target = resolveInitialCalendarMonth(
      calendarEvents.map((event) => event.start),
      new Date()
    );
    if (target) setCalendarDate(target);
  }, [slots.loading, calendarEvents, id, displayTimeZone]);

  // ---------------------------------------------------------------
  // Chunk E4 — translucent linked-event overlays (week + day views)
  // ---------------------------------------------------------------
  //
  // Visible-window math: month → full month + ±1 week (matches RBC's grid
  // padding), week → 21 days (visible week + ±1 week buffer), day → 15 days.
  // Buffer prevents records flickering in/out as the user paginates by a day.
  const overlayWindow = useMemo(() => {
    const d = calendarDate instanceof Date ? calendarDate : new Date(calendarDate);
    if (calendarView === "week") {
      const start = startOfWeek(d, { weekStartsOn: 0 });
      return {
        start: addDays(start, -7),
        end: addDays(start, 14),
      };
    }
    if (calendarView === "day") {
      const start = startOfDay(d);
      return {
        start: addDays(start, -7),
        end: addDays(start, 8),
      };
    }
    // month
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    return {
      start: addDays(first, -7),
      end: addDays(last, 7),
    };
  }, [calendarDate, calendarView]);

  // Map of calendarId → linkedCalendar doc for fast lookup during classify.
  const userLinkedCalendarsById = useMemo(() => {
    const map = {};
    for (const c of userLinkedCalendars.data || []) {
      if (c?.id) map[c.id] = c;
    }
    return map;
  }, [userLinkedCalendars.data]);

  // Classify the four data sources into a unified Overlay[] for the layout
  // helper. NOTE: `kind` here is the OVERLAY visual class (conflict /
  // preferred-align / feasible-align), a separate namespace from
  // voteAutoReason.kind (which uses the same strings — both happen to derive
  // from the engine's tier vocabulary). We deliberately keep the union
  // narrow so CalendarOverlayBlock + the layout helper agree on shape.
  const overlayRecords = useMemo(
    () =>
      buildOverlayRecords({
        uid: user?.uid,
        window: overlayWindow,
        currentSchedulerId: id,
        availabilityEvents: availabilityEvents.data,
        availabilityMarks: availabilityMarks.data,
        finalizedSessions: finalizedSessions.data,
        linkedCalendarsById: userLinkedCalendarsById,
      }),
    [
      user?.uid,
      overlayWindow,
      id,
      availabilityEvents.data,
      availabilityMarks.data,
      finalizedSessions.data,
      userLinkedCalendarsById,
    ]
  );

  // Full slot-span overlay source for the List View badges — independent of the
  // calendar grid's viewport (overlayWindow) so a conflict on a slot months away
  // from the grid's current view still badges its card, and badges don't mutate
  // as the user paginates the grid (A-7). Same builder, wider window.
  const listOverlayWindow = useMemo(
    () => slotSpanWindow(slots.data),
    [slots.data]
  );
  const listOverlayRecords = useMemo(
    () =>
      buildOverlayRecords({
        uid: user?.uid,
        window: listOverlayWindow,
        currentSchedulerId: id,
        availabilityEvents: availabilityEvents.data,
        availabilityMarks: availabilityMarks.data,
        finalizedSessions: finalizedSessions.data,
        linkedCalendarsById: userLinkedCalendarsById,
      }),
    [
      user?.uid,
      listOverlayWindow,
      id,
      availabilityEvents.data,
      availabilityMarks.data,
      finalizedSessions.data,
      userLinkedCalendarsById,
    ]
  );

  // Per-slot mapping of overlay records that OVERLAP each slot, so the List
  // View's unified <SlotIndicator> signals can merge calendar-overlay conflicts
  // (and free-time alignments) with the auto-vote engine reason — one indicator
  // per signal kind, no more duplicate triangles. Uses the canonical
  // half-open interval-overlap predicate. (A-7: sourced from the full slot-span
  // window, not the grid viewport, so far-month conflicts still badge.)
  const listOverlayRecordsBySlotId = useMemo(() => {
    const out = {};
    for (const slot of slots.data || []) {
      if (!slot?.id) continue;
      const overlapping = [];
      for (const r of listOverlayRecords) {
        if (intervalsOverlap(slot.start, slot.end, r.startUtc, r.endUtc)) {
          overlapping.push(r);
        }
      }
      if (overlapping.length) out[slot.id] = overlapping;
    }
    return out;
  }, [slots.data, listOverlayRecords]);

  // Run the overlays through the layout helper to get visibility + laneStyle.
  // The viewKey passed only matters for week/day; month view doesn't render
  // overlays so we keep "week" as a harmless default.
  const layoutViewKey = calendarView === "day" ? "day" : "week";
  const laidOutOverlays = useMemo(
    () =>
      layoutCalendarOverlays({
        overlays: overlayRecords,
        pollSlots: slots.data,
        viewKey: layoutViewKey,
        visibilityMode: overlayVisibilityMode,
      }),
    [overlayRecords, slots.data, layoutViewKey, overlayVisibilityMode],
  );

  // Convert visible overlays into RBC-shaped events so they ride the same
  // <Calendar events> array. EventCell + eventPropGetter branch on
  // `__overlayKind` to render <CalendarOverlayBlock> with right-lane geometry.
  // Month view → suppress overlay rendering entirely (E4 spec is week + day).
  const overlayEvents = useMemo(() => {
    if (calendarView === "month") return [];
    return laidOutOverlays
      .filter((o) => o.visible)
      .map((o) => ({
        id: `overlay:${o.id}`,
        title: o.title,
        start: new Date(o.startUtc),
        end: new Date(o.endUtc),
        allDay: !!o.allDay,
        __overlayKind: o.kind,
        __overlayRecord: o,
      }));
  }, [laidOutOverlays, calendarView]);

  // Per-day column tint map. Conflict-wins precedence — if a day has
  // multiple all-day overlays the "strongest" (conflict) wins; otherwise
  // preferred-align beats feasible-align. Keyed by yyyy-MM-dd in local time
  // so it matches RBC's dayPropGetter date argument.
  const overlayDayTintByKey = useMemo(() => {
    if (calendarView === "month") return {};
    const PRIORITY = { conflict: 3, "preferred-align": 2, "feasible-align": 1 };
    const map = {};
    for (const o of laidOutOverlays) {
      if (!o.visible || !o.allDay) continue;
      // Walk each day the overlay covers (RBC strips trim end by 1ms above).
      const start = new Date(o.startUtc);
      const end = new Date(o.endUtc);
      // Iterate by local-day boundaries — same precision as dayPropGetter.
      let cursor = startOfDay(start);
      const last = startOfDay(end);
      // Cap iterations to avoid pathological inputs.
      for (let i = 0; i < 366 && cursor.getTime() <= last.getTime(); i++) {
        const key = format(cursor, "yyyy-MM-dd");
        const cur = map[key];
        if (!cur || (PRIORITY[o.kind] || 0) > (PRIORITY[cur] || 0)) {
          map[key] = o.kind;
        }
        cursor = addDays(cursor, 1);
      }
    }
    return map;
  }, [laidOutOverlays, calendarView]);

  // Combined event list passed to <Calendar>. Order doesn't matter for RBC's
  // layout, but keeping poll slots first preserves their natural stacking.
  const allCalendarEvents = useMemo(
    () => [...calendarEvents, ...overlayEvents],
    [calendarEvents, overlayEvents],
  );

  const {
    scrollToTime,
    selectedEventId,
    setSelectedEventId,
    hasEvents,
    hasEventsInView,
    jumpNext,
    jumpPrev,
    jumpNextWindow,
    jumpPrevWindow,
  } = useCalendarNavigation({
    events: calendarEvents,
    view: calendarView,
    date: calendarDate,
    height: 780,
    onNavigate: setCalendarDate,
  });

  const calendarKey =
    calendarView === "month"
      ? `month-${calendarDate.toDateString()}`
      : `${calendarView}-${calendarDate.toDateString()}-${scrollToTime?.getTime?.() || 0}`;

  const slotsForModal = useMemo(() => {
    if (!modalDate) return [];
    const sameDay = slots.data.filter((slot) => {
      if (!slot.start) return false;
      const displayStart = toDisplayDate(new Date(slot.start), displayTimeZone);
      return displayStart ? isSameDay(displayStart, modalDate) : false;
    });
    return sameDay.sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [modalDate, slots.data, displayTimeZone]);

  const linkedCalendars = useMemo(() => {
    const ids = settings?.googleCalendarIds || [];
    const names = settings?.googleCalendarNames || {};
    return ids
      .map((id) => ({
        id,
        name: names[id] || id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [settings?.googleCalendarIds, settings?.googleCalendarNames]);

  const hasPendingVotes = useMemo(() => {
    const savedVotes = userVote.data?.votes || {};
    const savedNoTimes = Boolean(userVote.data?.noTimesWork);
    const draft = draftVotes || {};
    const keys = new Set([...Object.keys(savedVotes), ...Object.keys(draft)]);
    if (savedNoTimes !== noTimesWork) return true;
    if (keys.size === 0) return false;
    for (const key of keys) {
      if ((savedVotes[key] || null) !== (draft[key] || null)) {
        return true;
      }
    }
    return false;
  }, [draftVotes, userVote.data, noTimesWork]);

  const toggleExpanded = (slotId) => {
    setExpandedSlots((prev) => ({ ...prev, [slotId]: !prev[slotId] }));
  };

  const toggleRequiredAttendance = (email) => {
    setRequiredAttendance((prev) => {
      if (prev.includes(email)) {
        return prev.filter((item) => item !== email);
      }
      return [...prev, email];
    });
  };

  const clearRequiredAttendance = () => {
    setRequiredAttendance([]);
  };

  const handleAcceptInvite = async () => {
    if (!id || !user?.email) return;
    setInvitePromptBusy(true);
    try {
      await acceptPollInvite(id, user.email, user.uid);
      [
        pollInviteNotificationId(id, user.email),
        pollInviteLegacyNotificationId(id),
      ]
        .filter(Boolean)
        .forEach((notificationId) => removeNotification(notificationId));
      setInvitePromptOpen(false);
    } catch (err) {
      console.error("Failed to accept poll invite:", err);
      toast.error(err.message || "Failed to accept invitation");
    } finally {
      setInvitePromptBusy(false);
    }
  };

  const handleDeclineInvite = async () => {
    if (!id || !user?.email) return;
    setInvitePromptBusy(true);
    try {
      if (isPendingInvite) {
        await declinePollInvite(id, user.email, user.uid);
        [
          pollInviteNotificationId(id, user.email),
          pollInviteLegacyNotificationId(id),
        ]
          .filter(Boolean)
          .forEach((notificationId) => removeNotification(notificationId));
      }
      setInvitePromptOpen(false);
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to decline poll invite:", err);
      toast.error(err.message || "Failed to decline invitation");
    } finally {
      setInvitePromptBusy(false);
    }
  };

  const handleLeavePoll = async () => {
    if (!id || !user?.email) return;
    setLeaveSaving(true);
    try {
      await removeParticipantFromPoll(id, user.email, true, false, user.uid);
      toast.success("You left the poll");
      setLeaveOpen(false);
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to leave poll:", err);
      toast.error(err.message || "Failed to leave poll");
    } finally {
      setLeaveSaving(false);
    }
  };

  const handleRemoveParticipant = async () => {
    if (!id || !memberToRemove) return;
    const memberEmail = memberToRemove.email || "";
    const memberId = memberToRemove.id || null;
    const isGroupMemberMatch = memberId
      ? questingGroupMemberSet.has(memberId)
      : questingGroupMemberEmailSet.has(normalizeEmail(memberEmail));
    if (isGroupMemberMatch) {
      toast.error("Questing group members cannot be removed from this poll.");
      return;
    }
    try {
      await removeParticipantFromPoll(id, memberEmail, true, true, memberId);
      toast.success("Participant removed");
      setRemoveMemberOpen(false);
      setMemberToRemove(null);
    } catch (err) {
      console.error("Failed to remove participant:", err);
      toast.error(err.message || "Failed to remove participant");
    }
  };

  const handleRevokeInvite = async () => {
    if (!id || !inviteToRevoke) return;
    try {
      const inviteeUserId = await findUserIdByEmail(inviteToRevoke);
      await revokePollInvite(id, inviteToRevoke, inviteeUserId);
      toast.success("Invite removed");
      setRevokeInviteOpen(false);
      setInviteToRevoke(null);
    } catch (err) {
      console.error("Failed to revoke poll invite:", err);
      toast.error(err.message || "Failed to revoke invite");
    }
  };

  const resolveAvatarUser = (userInfo) =>
    resolveVoterForDisplay(userInfo, participantMapByEmail, schedulerVoteIdentityDisplayMode);

  const AvatarBubbleWithColors = ({ user, size = 24 }) => (
    <AvatarBubble user={resolveAvatarUser(user)} size={size} colorMap={colorMap} />
  );

  const AvatarStackWithColors = ({ users, max = 4, size = 20, onClick }) => (
    <AvatarStack
      users={(users || []).map((userInfo) => resolveAvatarUser(userInfo))}
      max={max}
      size={size}
      colorMap={colorMap}
      onClick={onClick}
    />
  );

  const EventCell = ({ event }) => {
    // Chunk E4 — overlay events ride the same RBC events array as poll slots.
    // They render as translucent right-lane tiles via CalendarOverlayBlock and
    // never expose vote affordances. The all-day branch is handled by RBC's
    // strip natively (this component renders inside the strip too).
    if (event.__overlayKind) {
      return (
        <CalendarOverlayBlock
          overlay={event.__overlayRecord}
          displayTimeZone={displayTimeZone}
          testId={`${calendarView}-overlay-${event.__overlayRecord?.id}`}
        />
      );
    }
    const durationMinutes =
      event.end && event.start ? Math.max(0, Math.round((event.end - event.start) / 60000)) : 0;
    // Engine signal for this slot (Chunk E3): drives both the SlotIndicator
    // icon and the inner-tint wrapper. Wrapper is suppressed when a vote-state
    // inline bg will win or when noTimesWork is set; icon always renders if
    // a kind is present.
    const autoReason = voteAutoReasonBySlotId?.[event.id] || null;
    const autoSource = voteAutoSourceBySlotId?.[event.id] || null;
    const indicatorKind = autoReason?.kind || null;

    if (calendarView === "month") {
      const preferredCount = event.preferredCount ?? 0;
      const feasibleCount = event.feasibleCount ?? 0;
      const compactDurationLabel = formatCompactDuration(durationMinutes);
      const vote = draftVotes[event.id] || null;
      const isPast = pastSlotIds.has(event.id);
      const dayKey = format(event.start, "yyyy-MM-dd");
      const hasMultipleSlots = (calendarEventCountByDay[dayKey] || 0) > 1;
      const canInteract = canVote && !isLocked && !isPast && !noTimesWork;
      const voteState = noTimesWork ? "unavailable" : vote ? vote.toLowerCase() : "none";
      // Chunk G: visual signals (vote-fill + engine-glow + dashed border) are
      // applied to RBC's outer .rbc-event via eventPropGetter (inline style for
      // fill + className for glow/border). The inner wrapper here is purely
      // structural — applying fill/glow again would double-paint. SlotIndicator
      // icon still surfaces the engine signal in-content.

      return (
        <div
          className="space-y-1"
          data-testid={`month-slot-${event.id}`}
          data-slot-id={event.id}
          data-vote-state={voteState}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 space-y-0.5">
              <div className="truncate text-xs font-semibold">{event.timeLabel}</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/70">
                {compactDurationLabel}
              </div>
            </div>
            {!noTimesWork && (
              <QuickVoteCluster
                slotId={event.id}
                vote={vote}
                canInteract={canInteract}
                hasMultipleSlots={hasMultipleSlots}
                ariaLabelTarget={event.timeLabel}
                testIdPrefix="month-slot"
                onSetVote={setVote}
              />
            )}
          </div>
          <div className="flex items-center justify-between gap-1 text-[10px] text-white/90">
            {showSchedulerVoteDetails ? (
              <span data-testid={`month-slot-tally-${event.id}`}>
                ★ {preferredCount} <span className="text-white/70">·</span> ✓ {feasibleCount}
              </span>
            ) : (
              // Layout spacer: keeps the past-marker / SlotIndicator right-aligned.
              <span />
            )}
            {isPast ? (
              <span
                data-testid={`month-slot-past-marker-${event.id}`}
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/85"
              >
                <Clock className="h-3 w-3" aria-hidden="true" />
                <span>Past</span>
              </span>
            ) : (
              indicatorKind && (
                <SlotIndicator
                  reason={autoReason}
                  voteAutoSource={autoSource}
                  onRestore={handleRestoreOverride}
                  onConfirm={handleConfirmOverride}
                  schedulerId={id}
                  slotId={event.id}
                  displayTimeZone={displayTimeZone}
                  groupNames={groupNames}
                  testId={`month-slot-indicator-${event.id}`}
                />
              )
            )}
          </div>
          {noTimesWork && (
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70">
              Unavailable
            </div>
          )}
        </div>
      );
    }

    // Week + day views share a single timed-slot cell with quick-vote header
    // (Chunk E2 parity). The cell duration drives the layout tier; viewKey
    // controls minor sizing differences (avatar stack size, text size).
    const viewKey = calendarView === "week" ? "week" : "day";
    const vote = draftVotes[event.id] || null;
    const isPast = pastSlotIds.has(event.id);
    const dayKey = format(event.start, "yyyy-MM-dd");
    const hasMultipleSlots = (calendarEventCountByDay[dayKey] || 0) > 1;
    const canInteract = canVote && !isLocked && !isPast && !noTimesWork;

    return (
      <SchedulerCalendarSlot
        event={event}
        viewKey={viewKey}
        durationMinutes={durationMinutes}
        vote={vote}
        canInteract={canInteract}
        noTimesWork={noTimesWork}
        hasMultipleSlots={hasMultipleSlots}
        onSetVote={setVote}
        avatarStack={AvatarStackWithColors}
        showVoteDetails={showSchedulerVoteDetails}
        voteAutoReason={autoReason}
        voteAutoSource={autoSource}
        onRestore={handleRestoreOverride}
        onConfirm={handleConfirmOverride}
        isPast={isPast}
        schedulerId={id}
        displayTimeZone={displayTimeZone}
        groupNames={groupNames}
      />
    );
  };

  const setVote = async (slotId, nextValue) => {
    if (!isSchedulerVotingOpen(scheduler.data, Date.now())) {
      toast.error("Voting is closed for this session poll.");
      return;
    }
    if (!isAcceptedParticipant && !canGuestVote) {
      toast.error("Accept the invite to vote on this poll.");
      return;
    }
    if (pastSlotIds.has(slotId)) {
      toast.error("This time slot is in the past and can no longer be voted on.");
      return;
    }
    if (noTimesWork) {
      setNoTimesWork(false);
    }

    // Click-to-override (Phase 2 Chunk D): when the user toggles a slot the
    // engine has filled (voteAutoSource non-null AND voteProposed === true),
    // commit the override immediately. This locks the vote as manual and
    // clears the engine fields so a subsequent re-evaluation won't overwrite.
    const engineSrc = voteAutoSourceBySlotId[slotId] || null;
    const wasProposed = voteProposedBySlotId[slotId] === true;
    if (engineSrc && wasProposed && user?.uid) {
      // Click-to-override commits an attending vote immediately (it skips
      // handleSave), so apply the required-add-on-poll gate here too — otherwise
      // a user could lock in attendance on an engine-proposed slot while a
      // required poll is unanswered. Clearing the slot (falsy nextValue) is a
      // retraction and is never gated.
      if (isAttendingVote(nextValue) && isAttendingVoteBlockedByRequiredPolls()) {
        return;
      }
      // H2 (acknowledge-on-vote): when the slot has currently-active
      // conflict refIds, thread them into the override patch so the engine
      // doesn't immediately re-fire override on the same conflict. This
      // covers both regular auto-* slots AND auto-conflict-override slots
      // (where click-to-vote acts as Restore-with-arbitrary-value).
      const reason = voteAutoReasonBySlotId?.[slotId] || null;
      const events = Array.isArray(reason?.events) ? reason.events : [];
      const busyWindows = Array.isArray(reason?.busyWindows) ? reason.busyWindows : [];
      const currentRefIds = [
        ...events.map((e) => e?.eventId).filter(Boolean),
        ...busyWindows.map((b) => b?.sourceSchedulerId).filter(Boolean),
      ];
      const existingAcknowledgedRefIds = Array.isArray(reason?.acknowledgedRefIds)
        ? reason.acknowledgedRefIds
        : [];
      const patch = buildOverridePatch(slotId, nextValue, {
        currentRefIds,
        existingAcknowledgedRefIds,
      });
      try {
        await upsertSchedulerVote(id, user.uid, {
          ...patch,
          voterId: user.uid,
          userEmail: user.email || null,
          userAvatar: user.photoURL || null,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.error("override write failed", err);
        toast.error("Couldn't lock that vote. Try again.");
        return;
      }
      // Mirror into draft state so the UI reflects without waiting for the
      // onSnapshot round-trip.
      setDraftVotes((prev) => {
        const next = { ...prev };
        if (!nextValue) {
          delete next[slotId];
        } else {
          next[slotId] = nextValue;
        }
        return next;
      });
      return;
    }

    // Default path: edit the draft, user submits later.
    setDraftVotes((prev) => {
      const next = { ...prev };
      if (!nextValue) {
        delete next[slotId];
      } else {
        next[slotId] = nextValue;
      }
      return next;
    });
  };

  // Chunk G6: handle the "Restore my vote" click on the urgent override
  // popover. Re-locks the slot manually with the previously-stored vote and
  // appends ALL current conflict refIds to acknowledgedRefIds so the urgent
  // (red) treatment downgrades to plain (orange) until a NEW refId arrives.
  const handleRestoreOverride = async (slotId) => {
    if (!user?.uid) return;
    if (!isSchedulerVotingOpen(scheduler.data, Date.now())) {
      toast.error("Voting is closed for this session poll.");
      return;
    }
    const reason = voteAutoReasonBySlotId[slotId] || {};
    const previousManualVote = reason.previousManualVote ?? null;
    // Restoring re-commits an attending vote immediately — gate it the same as
    // an explicit submit when a required add-on poll is unanswered. Restoring a
    // cleared (non-attending) vote is never gated.
    if (isAttendingVote(previousManualVote) && isAttendingVoteBlockedByRequiredPolls()) {
      return;
    }
    const events = Array.isArray(reason.events) ? reason.events : [];
    const busyWindows = Array.isArray(reason.busyWindows) ? reason.busyWindows : [];
    const currentRefIds = [
      ...events.map((e) => e?.eventId).filter(Boolean),
      ...busyWindows.map((b) => b?.sourceSchedulerId).filter(Boolean),
    ];
    const existingAck = Array.isArray(reason.acknowledgedRefIds)
      ? reason.acknowledgedRefIds
      : [];
    const patch = buildRestorePatch(
      slotId,
      previousManualVote,
      currentRefIds,
      existingAck,
    );
    try {
      await upsertSchedulerVote(id, user.uid, {
        ...patch,
        voterId: user.uid,
        userEmail: user.email || null,
        userAvatar: user.photoURL || null,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("restore override write failed", err);
      toast.error("Couldn't restore that vote. Try again.");
      return;
    }
    // Mirror into draft state so the UI reflects without waiting for the
    // onSnapshot round-trip.
    setDraftVotes((prev) => {
      const next = { ...prev };
      if (previousManualVote == null) {
        delete next[slotId];
      } else {
        next[slotId] = previousManualVote;
      }
      return next;
    });
  };

  // Chunk G6: handle the "Keep cleared" click — accept the engine's
  // override, append all current refIds to acknowledgedRefIds so the urgent
  // treatment downgrades to plain. Vote stays cleared; voteAutoSource stays
  // "auto-conflict-override" (so the engine continues to skip the slot).
  const handleConfirmOverride = async (slotId) => {
    if (!user?.uid) return;
    if (!isSchedulerVotingOpen(scheduler.data, Date.now())) {
      toast.error("Voting is closed for this session poll.");
      return;
    }
    const reason = voteAutoReasonBySlotId[slotId] || {};
    const events = Array.isArray(reason.events) ? reason.events : [];
    const busyWindows = Array.isArray(reason.busyWindows) ? reason.busyWindows : [];
    const currentRefIds = [
      ...events.map((e) => e?.eventId).filter(Boolean),
      ...busyWindows.map((b) => b?.sourceSchedulerId).filter(Boolean),
    ];
    const existingAck = Array.isArray(reason.acknowledgedRefIds)
      ? reason.acknowledgedRefIds
      : [];
    const patch = buildConfirmOverridePatch(slotId, currentRefIds, existingAck);
    try {
      await upsertSchedulerVote(id, user.uid, {
        ...patch,
        voterId: user.uid,
        userEmail: user.email || null,
        userAvatar: user.photoURL || null,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("confirm override write failed", err);
      toast.error("Couldn't confirm. Try again.");
    }
    // No draft mirror needed — the slot stays cleared (which it already is).
  };

  // Phase 3 M5 — handle the SlotAutoIndicator chip's Restore click.
  // Behavior depends on the chip mode the user clicked:
  //   "auto-cast"      Tier 1 — engine pre-filled but never wrote a vote.
  //                    Restore = stop pre-filling. Clears voteAutoSource +
  //                    voteProposed; votes[slot] is untouched (never set).
  //   "auto-submitted" Tier 2/3 — engine wrote votes[slot]. Restore reverts
  //                    the slot to `previousManualVote ?? null` and clears
  //                    voteAutoSource + voteProposed.
  //
  // In both cases the slot is appended to userTouchedSlots so the engine's
  // userTouchedSlots gate freezes the slot on the next reeval. Restore is
  // also an implicit acknowledgement of any active conflict refIds — same
  // pattern as click-to-override (H2).
  const handleAutoChipRestore = async (slotId, mode, previousManualVote) => {
    if (!user?.uid) return;
    if (!isSchedulerVotingOpen(scheduler.data, Date.now())) {
      toast.error("Voting is closed for this session poll.");
      return;
    }
    const reason = voteAutoReasonBySlotId[slotId] || {};
    const events = Array.isArray(reason.events) ? reason.events : [];
    const busyWindows = Array.isArray(reason.busyWindows) ? reason.busyWindows : [];
    const currentRefIds = [
      ...events.map((e) => e?.eventId).filter(Boolean),
      ...busyWindows.map((b) => b?.sourceSchedulerId).filter(Boolean),
    ];
    // Phase 3a-cascade T5 — when restoring a free-block-bumped-prior-vote
    // override (e.g. Tier 3 upgraded manual FEASIBLE → PREFERRED), the
    // reason carries a positive-block refId (mark or free-calendar event).
    // Append it to acknowledgedRefIds so the engine knows the user has
    // explicitly rejected this upgrade and won't re-fire on next reeval.
    const upgradeRefId =
      reason.kind === "preferred-align" || reason.kind === "feasible-align"
        ? reason.mark?.markId || reason.event?.eventId || reason.refId
        : null;
    if (upgradeRefId) currentRefIds.push(upgradeRefId);
    const existingAck = Array.isArray(reason.acknowledgedRefIds)
      ? reason.acknowledgedRefIds
      : [];
    const nextManual = mode === "auto-submitted" ? previousManualVote ?? null : null;
    // "auto-submitted" restore re-commits an attending vote; gate it like a
    // submit when a required add-on poll is unanswered. "auto-cast" restore
    // clears the slot (nextManual null) and is never gated.
    if (isAttendingVote(nextManual) && isAttendingVoteBlockedByRequiredPolls()) {
      return;
    }
    const overridePatch = buildOverridePatch(slotId, nextManual, {
      currentRefIds,
      existingAcknowledgedRefIds: existingAck,
    });
    const existingTouched = Array.isArray(userVote.data?.userTouchedSlots)
      ? userVote.data.userTouchedSlots
      : [];
    const nextUserTouched = Array.from(
      new Set([...existingTouched, slotId]),
    ).sort();
    try {
      await upsertSchedulerVote(id, user.uid, {
        ...overridePatch,
        userTouchedSlots: nextUserTouched,
        voterId: user.uid,
        userEmail: user.email || null,
        userAvatar: user.photoURL || null,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("auto-chip restore write failed", err);
      toast.error("Couldn't update that vote. Try again.");
      return;
    }
    setDraftVotes((prev) => {
      const next = { ...prev };
      if (mode === "auto-submitted") {
        if (previousManualVote == null) delete next[slotId];
        else next[slotId] = previousManualVote;
      } else {
        delete next[slotId];
      }
      return next;
    });
  };

  const setEmbeddedMultipleChoiceSelection = (poll, optionId) => {
    const pollId = poll?.id;
    if (!pollId) return;
    const allowMultiple = poll?.settings?.allowMultiple === true;
    setEmbeddedVoteErrors((previous) => ({ ...previous, [pollId]: null }));
    setEmbeddedVoteDrafts((previous) => {
      const current = previous[pollId] || { optionIds: [], otherText: "" };
      const { draft: nextDraft } = setMultipleChoiceOptionOnVoteDraft(current, optionId, {
        allowMultiple,
      });
      if (nextDraft === current) return previous;
      return {
        ...previous,
        [pollId]: nextDraft,
      };
    });
  };

  const setEmbeddedOtherText = (pollId, value) => {
    if (!pollId) return;
    setEmbeddedVoteDrafts((previous) => {
      const current = previous[pollId] || { optionIds: [], otherText: "" };
      const nextDraft = setOtherTextOnVoteDraft(current, value);
      if (nextDraft === current) return previous;
      return {
        ...previous,
        [pollId]: nextDraft,
      };
    });
  };

  const addEmbeddedRankedOption = (pollId, optionId) => {
    if (!pollId || !optionId) return;
    setEmbeddedVoteErrors((previous) => ({ ...previous, [pollId]: null }));
    setEmbeddedVoteDrafts((previous) => {
      const current = previous[pollId] || { rankings: [] };
      const nextDraft = addRankedOptionToVoteDraft(current, optionId);
      if (nextDraft === current) return previous;
      return {
        ...previous,
        [pollId]: nextDraft,
      };
    });
  };

  const moveEmbeddedRankedOption = (pollId, optionId, direction) => {
    if (!pollId || !optionId) return;
    setEmbeddedVoteDrafts((previous) => {
      const current = previous[pollId] || { rankings: [] };
      const nextDraft = moveRankedOptionInVoteDraft(current, optionId, direction);
      if (nextDraft === current) return previous;
      return {
        ...previous,
        [pollId]: nextDraft,
      };
    });
  };

  const removeEmbeddedRankedOption = (pollId, optionId) => {
    if (!pollId || !optionId) return;
    setEmbeddedVoteDrafts((previous) => {
      const current = previous[pollId] || { rankings: [] };
      const nextDraft = removeRankedOptionFromVoteDraft(current, optionId);
      if (nextDraft === current) return previous;
      return {
        ...previous,
        [pollId]: nextDraft,
      };
    });
  };

  const submitEmbeddedPollVote = async (poll) => {
    const pollId = poll?.id;
    if (!pollId || !id || !user?.uid) return;
    if (!canVoteEmbeddedPoll(poll)) {
      setEmbeddedVoteErrors((previous) => ({
        ...previous,
        [pollId]: "Voting is closed for this add-on poll.",
      }));
      return;
    }
    const voteType = poll?.settings?.voteType || "MULTIPLE_CHOICE";
    setEmbeddedSubmittingByPoll((previous) => ({ ...previous, [pollId]: true }));
    setEmbeddedVoteErrors((previous) => ({ ...previous, [pollId]: null }));
    try {
      if (voteType === "RANKED_CHOICE") {
        const draft = embeddedVoteDrafts[pollId] || {};
        const rankings = Array.isArray(draft.rankings) ? draft.rankings.filter(Boolean) : [];
        if (rankings.length === 0) {
          throw new Error("Rank at least one option before submitting.");
        }
        await submitBasicPollVote("scheduler", id, pollId, user.uid, {
          rankings,
          ...(canGuestVote ? { guestName, isGuest: true } : { source: "web" }),
        });
        // Optimistically reflect the submission so the required-poll gate (which
        // reads embeddedMyVotes) doesn't false-block a same-session session-vote
        // submit while waiting on the onSnapshot echo. The echo overwrites this
        // with the identical persisted doc.
        setEmbeddedMyVotes((previous) => ({ ...previous, [pollId]: { rankings } }));
        return;
      }

      const draft = embeddedVoteDrafts[pollId] || { optionIds: [], otherText: "" };
      const optionIds = Array.isArray(draft.optionIds) ? draft.optionIds.filter(Boolean) : [];
      const allowWriteIn = poll?.settings?.allowWriteIn === true;
      const normalizedOtherText = String(draft.otherText || "").trim();
      if (optionIds.length === 0 && (!allowWriteIn || normalizedOtherText.length === 0)) {
        throw new Error("Select at least one option before submitting.");
      }
      await submitBasicPollVote("scheduler", id, pollId, user.uid, {
        optionIds,
        otherText: allowWriteIn ? normalizedOtherText : "",
        ...(canGuestVote ? { guestName, isGuest: true } : { source: "web" }),
      });
      // Optimistically reflect the submission (see ranked path above) so the
      // required-poll gate sees this poll as answered immediately.
      setEmbeddedMyVotes((previous) => ({
        ...previous,
        [pollId]: { optionIds, otherText: allowWriteIn ? normalizedOtherText : "" },
      }));
    } catch (error) {
      setEmbeddedVoteErrors((previous) => ({
        ...previous,
        [pollId]: error?.message || "Failed to submit vote.",
      }));
    } finally {
      setEmbeddedSubmittingByPoll((previous) => ({ ...previous, [pollId]: false }));
    }
  };

  const clearEmbeddedPollVote = async (poll) => {
    const pollId = poll?.id;
    if (!pollId || !id || !user?.uid) return;
    if (!canVoteEmbeddedPoll(poll)) return;
    setEmbeddedClearingByPoll((previous) => ({ ...previous, [pollId]: true }));
    setEmbeddedVoteErrors((previous) => ({ ...previous, [pollId]: null }));
    try {
      await deleteBasicPollVote("scheduler", id, pollId, user.uid);
      setEmbeddedVoteDrafts((previous) => ({
        ...previous,
        [pollId]: { optionIds: [], otherText: "", rankings: [] },
      }));
    } catch (error) {
      setEmbeddedVoteErrors((previous) => ({
        ...previous,
        [pollId]: error?.message || "Failed to clear vote.",
      }));
    } finally {
      setEmbeddedClearingByPoll((previous) => ({ ...previous, [pollId]: false }));
    }
  };

  const finalizeEmbeddedPollIndividually = async (poll) => {
    const pollId = poll?.id;
    if (!pollId || !id || !isCreator) return false;
    setEmbeddedLifecycleBusyByPoll((previous) => ({ ...previous, [pollId]: true }));
    try {
      await finalizeEmbeddedBasicPoll(id, pollId);
      toast.success(`Finalized add-on poll: ${poll?.title || "Untitled poll"}`);
      return true;
    } catch (error) {
      console.error("Failed to finalize add-on poll:", error);
      toast.error(error?.message || "Failed to finalize add-on poll.");
      return false;
    } finally {
      setEmbeddedLifecycleBusyByPoll((previous) => ({ ...previous, [pollId]: false }));
    }
  };

  const reopenEmbeddedPollIndividually = async (poll) => {
    const pollId = poll?.id;
    if (!pollId || !id || !isCreator) return false;
    setEmbeddedLifecycleBusyByPoll((previous) => ({ ...previous, [pollId]: true }));
    try {
      await reopenEmbeddedBasicPoll(id, pollId);
      toast.success(`Re-opened add-on poll: ${poll?.title || "Untitled poll"}`);
      return true;
    } catch (error) {
      console.error("Failed to re-open add-on poll:", error);
      toast.error(error?.message || "Failed to re-open add-on poll.");
      return false;
    } finally {
      setEmbeddedLifecycleBusyByPoll((previous) => ({ ...previous, [pollId]: false }));
    }
  };

  const breakEmbeddedPollTieIndividually = async (poll, method) => {
    const pollId = poll?.id;
    if (!pollId || !id || !isCreator) return false;
    setEmbeddedLifecycleBusyByPoll((previous) => ({ ...previous, [pollId]: true }));
    setEmbeddedVoteErrors((previous) => ({ ...previous, [pollId]: null }));
    try {
      await breakBasicPollTieForParent("scheduler", id, pollId, method);
      toast.success(`Tie-break applied: ${poll?.title || "Untitled poll"}`);
      return true;
    } catch (error) {
      console.error("Failed to break embedded poll tie:", error);
      setEmbeddedVoteErrors((previous) => ({
        ...previous,
        [pollId]: error?.message || "Failed to break tie.",
      }));
      return false;
    } finally {
      setEmbeddedLifecycleBusyByPoll((previous) => ({ ...previous, [pollId]: false }));
    }
  };

  const addCloneInvite = async (input) => {
    const raw = String(input || "").trim();
    if (!raw) return;
    let resolved;
    try {
      resolved = await resolveIdentifier(raw);
    } catch (err) {
      setCloneInviteError(err.message || "Enter a valid email or Discord username.");
      return;
    }
    const validation = validateInviteCandidate({
      email: resolved.email,
      selfEmail: scheduler.data?.creatorEmail,
      groupMemberSet: cloneGroupMemberSet,
      existingInvites: cloneInvites,
    });
    if (!validation.ok) {
      setCloneInviteError(validation.error);
      return;
    }
    const normalized = validation.normalized;
    setCloneInvites((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
    setCloneInviteInput("");
    setCloneInviteError(null);
  };

  const removeCloneInvite = (email) => {
    setCloneInvites((prev) => prev.filter((item) => item !== email));
  };

  const handleSave = async () => {
    if (!user || !userVoteRef) return;
    if (!isSchedulerVotingOpen(scheduler.data, Date.now())) {
      toast.error("Voting is closed for this session poll.");
      return false;
    }
    if (!isAcceptedParticipant && !canGuestVote) {
      toast.error("Accept the invite to vote on this poll.");
      return false;
    }
    const hasAttendingVote = Object.values(draftVotes || {}).some((value) =>
      isAttendingVote(value)
    );
    const willSubmitVote = Boolean(noTimesWork || hasAttendingVote);

    // Required add-on polls gate an ATTENDING session vote: a participant can't
    // submit "these times work for me" while a still-open required embedded poll
    // is unanswered. "No times work" opts out of the session, so it bypasses the
    // gate. (Shared with the click-to-override + restore paths below.)
    if (hasAttendingVote && !noTimesWork && isAttendingVoteBlockedByRequiredPolls()) {
      return false;
    }

    // Phase 3a-cascade T4.5 — detect yes-to-no transition on a finalized
    // poll's winning slot. The engine retracts the user's busyWindow when
    // their winning-slot vote leaves the attending set, which clears
    // auto-rsvp-cascade overrides on other open polls. We snapshot the
    // pre-save state so we can fire the propagation-candidates query
    // immediately after the write succeeds and offer the user a chance
    // to restore those prior votes.
    const isFinalizedPoll =
      scheduler.data?.status === "FINALIZED" &&
      Boolean(scheduler.data?.winningSlotId);
    const finalizedWinningSlotId = isFinalizedPoll
      ? scheduler.data.winningSlotId
      : null;
    const wasAttendingFinalized = isFinalizedPoll
      ? !userVote.data?.noTimesWork &&
        isAttendingVote(userVote.data?.votes?.[finalizedWinningSlotId])
      : false;
    const isAttendingFinalizedNow = isFinalizedPoll
      ? !noTimesWork &&
        isAttendingVote(
          (noTimesWork ? {} : draftVotes)?.[finalizedWinningSlotId],
        )
      : false;
    const shouldOfferPropagation =
      wasAttendingFinalized && !isAttendingFinalizedNow;
    const wasAllVotesIn = allVotesIn;
    const hadVote = hasSubmittedSchedulerVote(userVote.data);
    // Gate the "all votes in" notification on PARTICIPANT submissions only: a
    // guest vote must never trip it (participantSubmittedCount excludes guests),
    // and a guest submitter never notifies (the emit is also !isAnonymous-gated).
    const nextVoteCount = hadVote
      ? (willSubmitVote ? participantSubmittedCount : Math.max(participantSubmittedCount - 1, 0))
      : (willSubmitVote ? participantSubmittedCount + 1 : participantSubmittedCount);
    const shouldNotifyAllVotesIn =
      willSubmitVote &&
      !wasAllVotesIn &&
      !user.isAnonymous &&
      scheduler.data?.status === "OPEN" &&
      participantCount > 0 &&
      nextVoteCount >= participantCount;
    setSaving(true);
    let success = false;
    try {
      if (willSubmitVote) {
        // Phase 3 — track userTouchedSlots so the engine can distinguish
        // intentional manual votes from passive defaults.
        const submittedVotes = noTimesWork ? {} : draftVotes;
        const userTouchedSlots = computeNextUserTouchedSlots({
          persistedVotes: userVote.data?.votes || {},
          submittedVotes,
          existingTouched: userVote.data?.userTouchedSlots || [],
        });

        // Build a per-slot diff patch instead of sending the whole draftVotes
        // map. setDoc({merge: true}) on a nested map only adds/updates keys —
        // it never deletes missing ones — so a raw `votes: draftVotes` write
        // silently leaves cleared slots in Firestore (the user's clear "undoes
        // itself" via the next snapshot). buildVoteSubmitPatch emits
        // deleteField() for cleared slots and voteAutoSource[slot]=null for
        // every changed slot so the engine's manual-lock invariant holds and
        // the Discord enrichment can render 👤 instead of the 💡 fallback.
        const { votes, voteAutoSource, hasChanges } = buildVoteSubmitPatch({
          persistedVotes: userVote.data?.votes || {},
          draftVotes: submittedVotes,
        });

        if (canGuestVote) {
          // Guest votes are locked to the rules whitelist — any stray key
          // (voterId/userEmail/userAvatar/voteAutoSource/userTouchedSlots)
          // is rejected by the field whitelist, so write ONLY these fields.
          // withVoteMapsIfChanged omits `votes` on a no-change re-submit so an
          // empty map can't clear the guest's existing votes under merge.
          await upsertSchedulerVote(
            id,
            user.uid,
            withVoteMapsIfChanged(
              {
                noTimesWork,
                guestName,
                isGuest: true,
                updatedAt: serverTimestamp(),
              },
              { votes, hasChanges }
            )
          );
        } else {
          // withVoteMapsIfChanged attaches votes/voteAutoSource ONLY when a slot
          // actually changed. Writing an empty map under setDoc({merge:true})
          // CLEARS the field, so an unchanged re-submit (re-open + Submit with no
          // edits) would otherwise wipe the user's entire vote set — the prod
          // incident of 2026-07-07.
          await upsertSchedulerVote(
            id,
            user.uid,
            withVoteMapsIfChanged(
              {
                voterId: user.uid,
                userEmail: user.email,
                userAvatar: user.photoURL,
                noTimesWork,
                userTouchedSlots,
                updatedAt: serverTimestamp(),
              },
              { votes, voteAutoSource, hasChanges }
            )
          );
        }

        // Anonymous guests have no identity to attribute a notification to and
        // must not nudge the creator/participants — skip both the per-vote and
        // the all-votes-in emits on the guest path.
        if (!user.isAnonymous) {
          const recipient = normalizeEmail(scheduler.data?.creatorEmail);
          if (recipient && normalizeEmail(user.email) !== recipient) {
            const recipients = {
              userIds: scheduler.data?.creatorId ? [scheduler.data.creatorId] : [],
              emails: recipient ? [recipient] : [],
            };
            if (recipients.userIds.length || recipients.emails.length) {
              try {
                await emitPollEvent({
                  eventType: "VOTE_SUBMITTED",
                  schedulerId: id,
                  pollTitle: scheduler.data?.title || "Session Poll",
                  actor: buildNotificationActor(user),
                  payload: {
                    pollTitle: scheduler.data?.title || "Session Poll",
                    voterEmail: normalizeEmail(user.email) || user.email,
                    voterUserId: user.uid,
                  },
                  recipients,
                  dedupeKey: `poll:${id}:vote:${user.uid}`,
                });
              } catch (notifyErr) {
                console.error("Failed to notify creator about vote:", notifyErr);
              }
            }
          }
        }
      } else if (userVote.data) {
        await deleteSchedulerVote(id, user.uid);
      }

      if (shouldNotifyAllVotesIn && !user.isAnonymous) {
        try {
          const normalizedCreatorEmail = normalizeEmail(scheduler.data?.creatorEmail) || null;
          const creatorRecipients = {
            userIds: scheduler.data?.creatorId ? [scheduler.data.creatorId] : [],
            emails: normalizedCreatorEmail ? [normalizedCreatorEmail] : [],
          };
          if (creatorRecipients.userIds.length || creatorRecipients.emails.length) {
            await emitPollEvent({
              eventType: "POLL_READY_TO_FINALIZE",
              schedulerId: id,
              pollTitle: scheduler.data?.title || "Session Poll",
              actor: buildNotificationActor(user),
              payload: {
                pollTitle: scheduler.data?.title || "Session Poll",
              },
              recipients: creatorRecipients,
              dedupeKey: `poll:${id}:ready-to-finalize`,
            });
          }

          const normalizedActorEmail = normalizeEmail(user.email) || null;
          const participantRecipientIds = Array.from(participantIdSet).filter(
            (participantId) =>
              participantId &&
              participantId !== scheduler.data?.creatorId &&
              participantId !== user.uid
          );
          const participantRecipientEmails = participantEmails.filter(
            (email) =>
              email &&
              email !== normalizedCreatorEmail &&
              email !== normalizedActorEmail
          );
          if (participantRecipientIds.length || participantRecipientEmails.length) {
            await emitPollEvent({
              eventType: "POLL_ALL_VOTES_IN",
              schedulerId: id,
              pollTitle: scheduler.data?.title || "Session Poll",
              actor: buildNotificationActor(user),
              payload: {
                pollTitle: scheduler.data?.title || "Session Poll",
              },
              recipients: {
                userIds: participantRecipientIds,
                emails: participantRecipientEmails,
              },
              dedupeKey: `poll:${id}:all-votes-in`,
            });
          }
        } catch (notifyErr) {
          console.error("Failed to notify all votes in:", notifyErr);
        }
      }
      toast.success(willSubmitVote ? "Votes saved successfully" : "Votes cleared");
      success = true;
      // After a guest casts a real vote, offer the account-conversion nudge so
      // their vote can be claimed by a real account (Task 7 / C5).
      if (willSubmitVote && canGuestVote) {
        setShowGuestNudge(true);
      }

      // Phase 3a-cascade T4.5 — offer to restore prior votes on other
      // open polls whose auto-rsvp-cascade overrides will clear because
      // of this RSVP-change. Fire AFTER the write succeeds; the engine's
      // re-eval is debounced (~5s), so the cascade overrides are still
      // in place when this query runs. Failures here don't surface as
      // errors — the user's vote is already saved; the modal is purely
      // additive UX.
      if (shouldOfferPropagation) {
        try {
          const functions = getFunctions();
          const computeCandidates = httpsCallable(
            functions,
            "computeVotePropagationCandidates",
          );
          const result = await computeCandidates({
            trigger: {
              kind: "rsvp-change",
              schedulerId: id,
              schedulerLabel: scheduler.data?.title || null,
            },
          });
          const candidates = Array.isArray(result.data) ? result.data : [];
          if (candidates.length > 0) {
            setPropagateModal({
              open: true,
              candidates,
              contextLabel: scheduler.data?.title || "this session",
              applying: false,
            });
          }
        } catch (queryErr) {
          console.error(
            "Failed to compute vote propagation candidates:",
            queryErr,
          );
        }
      }
    } catch (err) {
      console.error("Failed to save votes:", err);
      toast.error(err.message || "Failed to save votes. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
    return success;
  };

  // ── Guest vote migration (Task 7 / plan C5 + C9) ─────────────────────────
  // The pure decision logic + failure-atomic sequencing lives in
  // guest-vote-migration.js (`migrateGuestVote`). Here we wire real Firebase +
  // data-layer ops into its injected `services`, and translate the result into
  // toasts + the conflict modal. The stash is written to sessionStorage BEFORE
  // the popup opens so a crash/reload/sign-in failure can always recover.

  function summarizeExistingVote(vote) {
    const count = vote?.votes ? Object.keys(vote.votes).length : 0;
    if (count === 1) return "1 slot";
    return count > 1 ? `${count} slots` : "previous vote";
  }

  function buildGuestMigrationServices() {
    return {
      linkGoogleAccount: () => linkGoogleAccount(),
      credentialFromError: (err) => GoogleAuthProvider.credentialFromError(err),
      signInWithCredential: async (credential) => {
        const result = await signInWithCredential(auth, credential);
        return result.user;
      },
      isParticipant: (uid) =>
        Boolean((scheduler.data?.participantIds || []).includes(uid)),
      join: (uid) =>
        updateScheduler(id, {
          participantIds: arrayUnion(uid),
          updatedAt: serverTimestamp(),
        }),
      leave: (uid) =>
        updateScheduler(id, {
          participantIds: arrayRemove(uid),
          updatedAt: serverTimestamp(),
        }),
      writeParticipantVote: async (uid, identity, stash) => {
        // Rewrite as a normal participant vote: carry identity fields and
        // strip the guest markers with deleteField() (a merge alone would not
        // remove isGuest/guestName, which would keep the doc mis-counted).
        await upsertSchedulerVote(id, uid, {
          votes: stash.schedulerVotes.votes,
          noTimesWork: Boolean(stash.schedulerVotes.noTimesWork),
          voterId: uid,
          userEmail: identity.email || null,
          userAvatar: identity.photoURL || null,
          isGuest: deleteField(),
          guestName: deleteField(),
          updatedAt: serverTimestamp(),
        });
        await Promise.all(
          (stash.embeddedVotes || []).map((ev) =>
            submitBasicPollVote("scheduler", id, ev.pollId, uid, {
              ...(ev.rankings ? { rankings: ev.rankings } : { optionIds: ev.optionIds || [] }),
              ...(ev.otherText ? { otherText: ev.otherText } : {}),
              source: "web",
              isGuest: deleteField(),
              guestName: deleteField(),
            })
          )
        );
      },
      deleteAnonDocs: async (uid, stash) => {
        await deleteSchedulerVote(id, uid);
        await Promise.all(
          (stash.embeddedVotes || []).map((ev) =>
            deleteBasicPollVote("scheduler", id, ev.pollId, uid)
          )
        );
      },
      restoreAnonDocs: async (uid, stash) => {
        // Still anonymous here (sign-in failed), so the guest write rule allows
        // re-creating the anon docs verbatim from the stash.
        await upsertSchedulerVote(id, uid, {
          votes: stash.schedulerVotes.votes,
          noTimesWork: Boolean(stash.schedulerVotes.noTimesWork),
          guestName: stash.guestName || "",
          isGuest: true,
          updatedAt: serverTimestamp(),
        });
        await Promise.all(
          (stash.embeddedVotes || []).map((ev) =>
            submitBasicPollVote("scheduler", id, ev.pollId, uid, {
              ...(ev.rankings ? { rankings: ev.rankings } : { optionIds: ev.optionIds || [] }),
              ...(ev.otherText ? { otherText: ev.otherText } : {}),
              guestName: stash.guestName || "",
              isGuest: true,
            })
          )
        );
      },
      readExistingVote: async (uid) => {
        const snap = await getDoc(schedulerVoteDocRef(id, uid));
        return snap.exists() ? snap.data() : null;
      },
      clearStash: () => clearGuestVote(id),
    };
  }

  function promptGuestConflict(existingVote, stash) {
    return new Promise((resolve) => {
      guestConflictResolverRef.current = resolve;
      setGuestConflict({
        existingVote,
        stash,
        existingSummary: summarizeExistingVote(existingVote),
      });
    });
  }

  function handleGuestConflictPreserve() {
    setGuestConflict(null);
    guestConflictResolverRef.current?.("preserve");
    guestConflictResolverRef.current = null;
  }

  function handleGuestConflictReplace() {
    setGuestConflict(null);
    guestConflictResolverRef.current?.("replace");
    guestConflictResolverRef.current = null;
  }

  function handleGuestMigrationResult(result) {
    switch (result.outcome) {
      case "linked":
      case "migrated":
      case "replaced":
        toast.success("Your vote is now saved to your account.");
        setShowGuestNudge(false);
        break;
      case "preserved":
        toast.success("Your previous vote on this poll was kept.");
        setShowGuestNudge(false);
        break;
      case "cancelled":
        // Popup closed before resolving — guest vote doc + stash intact. No-op.
        break;
      default: {
        if (result.stage === "signin" && result.restored) {
          toast.error("Couldn't sign in — your guest vote is safe on this poll.");
        } else if (result.stage === "credential") {
          toast.error("We couldn't verify your Google account. Your guest vote is saved on this poll.");
        } else if (result.selfLeft) {
          toast.error("Couldn't finish moving your vote. Your guest vote is saved on this poll — try again.");
        } else {
          toast.error("Couldn't move your vote to an account right now. Your guest vote is saved on this poll.");
        }
      }
    }
  }

  async function handleGuestConversion() {
    if (!id || !user?.isAnonymous || guestConverting) return;
    setGuestConverting(true);
    try {
      // 1. Build + persist the stash BEFORE the popup (safety net).
      const stashed = buildGuestVoteStash({
        guestName,
        schedulerVote: userVote.data,
        embeddedVotes: (embeddedPolls || []).map((poll) => ({
          pollId: poll.id,
          vote: embeddedMyVotes[poll.id] || null,
        })),
      });
      stashGuestVote({ schedulerId: id, ...stashed });

      // 2. Run the migration (auth transitions + writes are injected).
      const result = await migrateGuestVote({
        schedulerId: id,
        anonUid: user.uid,
        stashed,
        services: buildGuestMigrationServices(),
        promptConflict: promptGuestConflict,
      });
      handleGuestMigrationResult(result);
    } catch (err) {
      console.error("Guest vote migration failed:", err);
      toast.error("Couldn't move your vote to an account right now. Your guest vote is saved on this poll.");
    } finally {
      setGuestConverting(false);
    }
  }

  // Load-time recovery: if the page reloaded mid-conversion and the user is now
  // non-anonymous with a pending stash, finish the migration best-effort.
  useEffect(() => {
    if (!id || !user || user.isAnonymous) return;
    const stashed = popGuestVote(id);
    if (!stashed) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await resumeGuestVoteMigration({
          user,
          stashed,
          services: buildGuestMigrationServices(),
          promptConflict: promptGuestConflict,
        });
        if (cancelled) return;
        handleGuestMigrationResult(result);
      } catch (err) {
        console.error("Guest vote migration resume failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.uid, user?.isAnonymous]);

  // Phase 3a-cascade T4.5 — fired by RsvpPanel after a successful RSVP
  // change that transitions the user out of attending state on a
  // finalized poll's winning slot. RsvpPanel has already cleared the
  // slot vote (so busy-windows.js will retract the busyWindow); here we
  // surface the propagate-votes modal so the user can opt in to
  // restoring prior votes on other open polls whose auto-rsvp-cascade
  // overrides are about to clear.
  const handleRsvpAttendingTransitionedOff = async () => {
    try {
      const functions = getFunctions();
      const computeCandidates = httpsCallable(
        functions,
        "computeVotePropagationCandidates",
      );
      const result = await computeCandidates({
        trigger: {
          kind: "rsvp-change",
          schedulerId: id,
          schedulerLabel: scheduler.data?.title || null,
        },
      });
      const candidates = Array.isArray(result.data) ? result.data : [];
      if (candidates.length > 0) {
        setPropagateModal({
          open: true,
          candidates,
          contextLabel: scheduler.data?.title || "this session",
          applying: false,
        });
      }
    } catch (queryErr) {
      console.error(
        "Failed to compute vote propagation candidates after RSVP change:",
        queryErr,
      );
    }
  };

  const handlePropagateApply = async (selectedRows) => {
    if (!user || selectedRows.length === 0) {
      setPropagateModal((prev) => ({ ...prev, open: false }));
      return;
    }
    setPropagateModal((prev) => ({ ...prev, applying: true }));
    try {
      // Group rows by pollId so we make one merge-write per affected poll
      // doc (cheaper than per-slot, and atomic per-doc). Each write
      // restores the suggested vote, clears the override marker, and
      // adds the slot to userTouchedSlots so the engine won't re-override.
      const byPoll = new Map();
      for (const row of selectedRows) {
        if (!byPoll.has(row.pollId)) byPoll.set(row.pollId, []);
        byPoll.get(row.pollId).push(row);
      }
      for (const [pollId, rows] of byPoll) {
        const patch = {
          votes: {},
          voteAutoSource: {},
          voteAutoReason: {},
          updatedAt: serverTimestamp(),
        };
        for (const row of rows) {
          patch.votes[row.slotId] = row.suggestedVote;
          patch.voteAutoSource[row.slotId] = null;
          patch.voteAutoReason[row.slotId] = null;
        }
        // userTouchedSlots is a flat array; merge by reading current then
        // unioning the restored slot ids. Read via a one-shot getDoc so
        // we don't depend on a real-time listener for the target poll.
        const slotIds = rows.map((r) => r.slotId);
        let existingTouched = [];
        try {
          const snap = await getDoc(
            doc(db, "schedulers", pollId, "votes", user.uid),
          );
          if (snap.exists()) {
            const tArr = snap.data().userTouchedSlots;
            if (Array.isArray(tArr)) existingTouched = tArr;
          }
        } catch (readErr) {
          console.warn(
            `Could not read userTouchedSlots for ${pollId}; merging without prior list.`,
            readErr,
          );
        }
        const nextTouched = Array.from(
          new Set([...existingTouched, ...slotIds]),
        );
        patch.userTouchedSlots = nextTouched;
        await upsertSchedulerVote(pollId, user.uid, patch);
      }
      toast.success(
        `Restored ${selectedRows.length} vote${selectedRows.length === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      console.error("Failed to propagate votes:", err);
      toast.error(err.message || "Failed to restore votes. Try again.");
    } finally {
      setPropagateModal({
        open: false,
        candidates: [],
        contextLabel: null,
        applying: false,
      });
    }
  };

  const handlePropagateSkip = () => {
    setPropagateModal({
      open: false,
      candidates: [],
      contextLabel: null,
      applying: false,
    });
  };

  const openFinalize = (slotId) => {
    if (pastSlotIds.has(slotId)) {
      toast.error("This time slot is in the past and cannot be selected.");
      return;
    }
    const linkedCalendarId = linkedCalendars[0]?.id || "";
    const baseTitle = scheduler.data?.title || "Quest Session";
    const groupName = questingGroupName;
    const calendarTitle = groupName ? `[${groupName}] ${baseTitle}` : baseTitle;
    const slot = slots.data.find((item) => item.id === slotId);
    const duration = slot?.start && slot?.end ? Math.round((new Date(slot.end) - new Date(slot.start)) / 60000) : 240;
    setFinalizeSlotId(slotId);
    setEventTitle(calendarTitle);
    setEventDescription(scheduler.data?.description || "");
    setEventDuration(duration || settings?.defaultDurationMinutes || 240);
    setEventAttendees(participantEmails.join(", "));
    setSelectedCalendarId(linkedCalendarId);
    setCreateCalendarEvent(Boolean(linkedCalendars.length));
    setDeleteOldEvent(true);
    setFinalizeOpen(true);
  };

  const requestEmbeddedFinalizeChoice = (slotId) => {
    const unfinalizedEmbeddedPollCount = (embeddedPolls || []).filter(
      (poll) => (poll?.status || "OPEN") !== "FINALIZED"
    ).length;
    if (isCreator && unfinalizedEmbeddedPollCount > 0) {
      setFinalizeSlotId(slotId);
      setFinalizeEmbeddedChoiceOpen(true);
      return;
    }
    setFinalizeOutstandingEmbeddedPolls(false);
    openFinalize(slotId);
  };

  const requestReschedule = (slotId) => {
    if (pastSlotIds.has(slotId)) return;
    if (scheduler.data?.status === "CANCELLED") return;
    if (slotId === scheduler.data?.winningSlotId) return;
    setIsReschedule(true);
    openFinalize(slotId);
  };

  const requestFinalize = (slotId, options = {}) => {
    const skipPendingVoteCheck = options.skipPendingVoteCheck === true;
    if (scheduler.data?.status === "CANCELLED") return;
    if (isLocked) return;
    if (pastSlotIds.has(slotId)) return;
    if (!skipPendingVoteCheck && isCreator && hasPendingVotes) {
      setPendingFinalizeSlotId(slotId);
      setPendingVotesOpen(true);
      return;
    }
    if (!isCreator) {
      openFinalize(slotId);
      return;
    }

    setRequiredFinalizeChecking(true);
    fetchRequiredEmbeddedPollFinalizeSummary(id)
      .then((summary) => {
        const missingPolls = (summary?.requiredPolls || []).filter(
          (poll) => Number(poll?.missingCount || 0) > 0
        );
        if (missingPolls.length === 0) {
          requestEmbeddedFinalizeChoice(slotId);
          return;
        }

        setFinalizeSlotId(slotId);
        setRequiredFinalizeSummary(missingPolls);
        setRequiredFinalizeWarningOpen(true);
      })
      .catch((error) => {
        console.error("Failed to check required add-on polls before finalizing:", error);
        toast.error(error?.message || "Failed to check required add-on polls.");
      })
      .finally(() => {
        setRequiredFinalizeChecking(false);
      });
  };

  const continueFinalizeWithMissingRequired = () => {
    if (!finalizeSlotId) return;
    setRequiredFinalizeWarningOpen(false);
    requestEmbeddedFinalizeChoice(finalizeSlotId);
  };

  const continueFinalizeAndFinalizeEmbeddedPolls = () => {
    if (!finalizeSlotId) return;
    setFinalizeOutstandingEmbeddedPolls(true);
    setFinalizeEmbeddedChoiceOpen(false);
    openFinalize(finalizeSlotId);
  };

  const continueFinalizeWithoutFinalizingEmbeddedPolls = () => {
    if (!finalizeSlotId) return;
    setFinalizeOutstandingEmbeddedPolls(false);
    setFinalizeEmbeddedChoiceOpen(false);
    openFinalize(finalizeSlotId);
  };

  const submitVotesThenFinalize = async () => {
    if (!pendingFinalizeSlotId || !userVoteRef) return;
    setPendingFinalizeBusy(true);
    try {
      const saved = await handleSave();
      if (saved) {
        setPendingVotesOpen(false);
        requestFinalize(pendingFinalizeSlotId, { skipPendingVoteCheck: true });
      }
    } catch (err) {
      console.error("Failed to submit votes before finalizing:", err);
    } finally {
      setPendingFinalizeBusy(false);
    }
  };

  const toggleNoTimesWork = (checked) => {
    if (checked) {
      setDraftVotes({});
      setNoTimesWork(true);
    } else {
      setNoTimesWork(false);
    }
  };

  const discardVotesThenFinalize = async () => {
    if (!pendingFinalizeSlotId || !userVoteRef) return;
    setPendingFinalizeBusy(true);
    try {
      await deleteSchedulerVote(id, user.uid);
      setDraftVotes({});
      setPendingVotesOpen(false);
      requestFinalize(pendingFinalizeSlotId, { skipPendingVoteCheck: true });
    } catch (err) {
      console.error("Failed to clear votes before finalizing:", err);
      toast.error("Failed to clear your votes. Please try again.");
    } finally {
      setPendingFinalizeBusy(false);
    }
  };

  const deleteCalendarEntry = async () => {
    if (!id) return;
    const functions = getFunctions();
    const deleteEvent = httpsCallable(functions, "googleCalendarDeleteEvent");
    await deleteEvent({ schedulerId: id });
  };

  const handleFinalize = async () => {
    if (!finalizeSlotId || !schedulerDocRef) return;
    setSaving(true);
    try {
      const slot = slots.data.find((item) => item.id === finalizeSlotId);
      if (!slot?.start) {
        throw new Error("Selected slot is missing a start time.");
      }

      const start = new Date(slot.start);
      const durationMinutes = Number(eventDuration) || 240;
      const slotEnd =
        slot.end ? new Date(slot.end) : new Date(start.getTime() + durationMinutes * 60 * 1000);
      const shouldCreateEvent = createCalendarEvent && linkedCalendars.length > 0;
      if (durationMinutes < 1 || isNaN(durationMinutes)) {
        throw new Error("Invalid duration. Please enter a valid number of minutes.");
      }

      const parsedEmails = eventAttendees
        .split(/[\n,;]/)
        .map((email) => email.trim())
        .filter(Boolean);

      const creatorEmail = scheduler.data?.creatorEmail;
      const uniqueEmails = new Set(normalizeEmailList(parsedEmails));
      if (creatorEmail && !uniqueEmails.has(normalizeEmail(creatorEmail))) {
        parsedEmails.push(creatorEmail);
      }

      const invalidEmails = parsedEmails.filter((email) => !isValidEmail(email));
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid email address: ${invalidEmails[0]}`);
      }

      const linkedCalendarId = selectedCalendarId || linkedCalendars[0]?.id;
      const functions = getFunctions();
      const finalizePoll = httpsCallable(functions, "googleCalendarFinalizePoll");
      // Identity & email-privacy (Model Y): the server derives the gated attendee
      // set from each registered participant's chosen public identifier +
      // calendar-share consent, so we must NOT send the participant email list as
      // the authoritative attendee set (that was the leak vector). Send only
      // creator-typed NON-participant addresses as externalAttendees; the server
      // also drops any that resolve to a registered user's email as a backstop.
      const externalAttendees = computeExternalAttendees(
        parsedEmails,
        creatorEmail,
        participantEmails
      );
      await finalizePoll({
        schedulerId: id,
        slotId: finalizeSlotId,
        calendarId: linkedCalendarId,
        title: eventTitle,
        description: eventDescription,
        durationMinutes,
        externalAttendees,
        deleteOldEvent,
        createCalendarEvent: shouldCreateEvent,
      });

      let finalizedEmbeddedPollCount = 0;
      if (isCreator && finalizeOutstandingEmbeddedPolls) {
        const pollsToFinalize = (embeddedPolls || []).filter(
          (poll) => (poll?.status || "OPEN") !== "FINALIZED"
        );
        if (pollsToFinalize.length > 0) {
          const results = await Promise.allSettled(
            pollsToFinalize.map((poll) => finalizeEmbeddedBasicPoll(id, poll.id))
          );
          finalizedEmbeddedPollCount = results.filter(
            (result) => result.status === "fulfilled"
          ).length;
          const failedCount = results.length - finalizedEmbeddedPollCount;
          if (failedCount > 0) {
            toast.error(
              `Session finalized, but ${failedCount} add-on poll${failedCount === 1 ? "" : "s"} could not be finalized.`
            );
          }
        }
      }

      const participantIds = Array.from(
        new Set(
          [
            ...(scheduler.data?.participantIds || []),
            ...questingGroupMemberIds,
          ].filter(Boolean)
        )
      );
      try {
        const normalizedCreatorEmail = normalizeEmail(creatorEmail) || null;
        const emails = new Set();
        if (participantIds.length > 0) {
          const profilesById = await fetchPublicProfilesByIds(participantIds);
          Object.values(profilesById).forEach((profile) => {
            if (!profile?.email) return;
            const normalized = normalizeEmail(profile.email);
            emails.add(normalized);
          });
        }

        const notificationTimeZone = resolvePollTimeZone(scheduler.data?.timezone);
        const winningLabel = formatFinalizedSessionTime({
          start,
          end: slotEnd,
          timeZone: notificationTimeZone,
        });

        const recipientEmails = Array.from(emails).filter(
          (email) => email !== normalizedCreatorEmail
        );
        const recipientUserIds = participantIds.filter(
          (participantId) => participantId !== scheduler.data?.creatorId
        );
        const recipients = { userIds: recipientUserIds, emails: recipientEmails };

        if (
          shouldEmitPollLifecycleEvent({
            eventType: "POLL_FINALIZED",
            recipients,
            questingGroupDiscord: questingGroup.data?.discord,
          })
        ) {
          await emitPollEvent({
            eventType: "POLL_FINALIZED",
            schedulerId: id,
            pollTitle: scheduler.data?.title || "Session Poll",
            actor: buildNotificationActor(user),
            payload: {
              pollTitle: scheduler.data?.title || "Session Poll",
              winningDate: winningLabel,
            },
            recipients,
            dedupeKey: `poll:${id}:finalized`,
          });
        }
      } catch (notifyErr) {
        console.error("Failed to send finalization notifications:", notifyErr);
      }
      setFinalizeOpen(false);
      setFinalizeOutstandingEmbeddedPolls(false);
      toast.success(
        shouldCreateEvent
          ? finalizedEmbeddedPollCount > 0
            ? `Session finalized, calendar event created, and ${finalizedEmbeddedPollCount} add-on poll${finalizedEmbeddedPollCount === 1 ? "" : "s"} finalized`
            : "Session finalized and calendar event created"
          : finalizedEmbeddedPollCount > 0
            ? `Session finalized and ${finalizedEmbeddedPollCount} add-on poll${finalizedEmbeddedPollCount === 1 ? "" : "s"} finalized`
            : "Session finalized"
      );
    } catch (err) {
      console.error("Failed to finalize session poll:", err);
      toast.error(err.message || "Failed to finalize session poll. Your Google token may have expired - try signing out and back in.");
    } finally {
      setSaving(false);
    }
  };

  const handleReschedule = async () => {
    if (!finalizeSlotId || !schedulerDocRef) return;
    setSaving(true);
    try {
      const slot = slots.data.find((item) => item.id === finalizeSlotId);
      if (!slot?.start) {
        throw new Error("Selected slot is missing a start time.");
      }

      const durationMinutes = Number(eventDuration) || 240;
      if (durationMinutes < 1 || isNaN(durationMinutes)) {
        throw new Error("Invalid duration. Please enter a valid number of minutes.");
      }

      const parsedEmails = eventAttendees
        .split(/[\n,;]/)
        .map((email) => email.trim())
        .filter(Boolean);

      const creatorEmail = scheduler.data?.creatorEmail;
      const uniqueEmails = new Set(normalizeEmailList(parsedEmails));
      if (creatorEmail && !uniqueEmails.has(normalizeEmail(creatorEmail))) {
        parsedEmails.push(creatorEmail);
      }

      const invalidEmails = parsedEmails.filter((email) => !isValidEmail(email));
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid email address: ${invalidEmails[0]}`);
      }

      const linkedCalendarId = selectedCalendarId || linkedCalendars[0]?.id;
      const functions = getFunctions();
      const rescheduleFn = httpsCallable(functions, "rescheduleSession");
      // Identity & email-privacy (Model Y): server-side attendee derivation — see
      // the finalize path. Send only creator-typed NON-participant addresses.
      const externalAttendees = computeExternalAttendees(
        parsedEmails,
        creatorEmail,
        participantEmails
      );
      await rescheduleFn({
        schedulerId: id,
        slotId: finalizeSlotId,
        calendarId: linkedCalendarId,
        title: eventTitle,
        description: eventDescription,
        durationMinutes,
        externalAttendees,
      });

      const participantIds = Array.from(
        new Set(
          [
            ...(scheduler.data?.participantIds || []),
            ...questingGroupMemberIds,
          ].filter(Boolean)
        )
      );
      try {
        const normalizedCreatorEmail = normalizeEmail(creatorEmail) || null;
        const emails = new Set();
        if (participantIds.length > 0) {
          const profilesById = await fetchPublicProfilesByIds(participantIds);
          Object.values(profilesById).forEach((profile) => {
            if (!profile?.email) return;
            const normalized = normalizeEmail(profile.email);
            emails.add(normalized);
          });
        }

        const recipientEmails = Array.from(emails).filter(
          (email) => email !== normalizedCreatorEmail
        );
        const recipientUserIds = participantIds.filter(
          (participantId) => participantId !== scheduler.data?.creatorId
        );
        const recipients = { userIds: recipientUserIds, emails: recipientEmails };

        if (
          shouldEmitPollLifecycleEvent({
            eventType: "SESSION_RESCHEDULED",
            recipients,
            questingGroupDiscord: questingGroup.data?.discord,
          })
        ) {
          await emitPollEvent({
            eventType: "SESSION_RESCHEDULED",
            schedulerId: id,
            pollTitle: scheduler.data?.title || "Session Poll",
            actor: buildNotificationActor(user),
            payload: {
              pollTitle: scheduler.data?.title || "Session Poll",
            },
            recipients,
          });
        }
      } catch (notifyErr) {
        console.error("Failed to send reschedule notifications:", notifyErr);
      }

      setFinalizeOpen(false);
      setIsReschedule(false);
      toast.success("Session rescheduled");
    } catch (err) {
      console.error("Failed to reschedule session:", err);
      toast.error(err.message || "Failed to reschedule session.");
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    if (!schedulerDocRef) return;
    setSaving(true);
    let success = false;
    try {
      const functions = getFunctions();
      const reopenFn = httpsCallable(functions, "reopenSession");
      await reopenFn({ schedulerId: id });
      const participantIds = Array.from(
        new Set(
          [
            ...(scheduler.data?.participantIds || []),
            ...questingGroupMemberIds,
          ].filter(Boolean)
        )
      );
      try {
        const normalizedCreatorEmail = normalizeEmail(scheduler.data?.creatorEmail) || null;
        const emails = new Set();
        if (participantIds.length > 0) {
          const profilesById = await fetchPublicProfilesByIds(participantIds);
          Object.values(profilesById).forEach((profile) => {
            if (!profile?.email) return;
            const normalized = normalizeEmail(profile.email);
            emails.add(normalized);
          });
        }

        const recipientEmails = Array.from(emails).filter(
          (email) => email !== normalizedCreatorEmail
        );
        const recipientUserIds = participantIds.filter(
          (participantId) => participantId !== scheduler.data?.creatorId
        );
        const recipients = { userIds: recipientUserIds, emails: recipientEmails };

        if (
          shouldEmitPollLifecycleEvent({
            eventType: "POLL_REOPENED",
            recipients,
            questingGroupDiscord: questingGroup.data?.discord,
          })
        ) {
          await emitPollEvent({
            eventType: "POLL_REOPENED",
            schedulerId: id,
            pollTitle: scheduler.data?.title || "Session Poll",
            actor: buildNotificationActor(user),
            payload: {
              pollTitle: scheduler.data?.title || "Session Poll",
            },
            recipients,
          });
        }
      } catch (notifyErr) {
        console.error("Failed to send poll reopen notification:", notifyErr);
      }
      toast.success("Session poll re-opened");
      success = true;
    } catch (err) {
      console.error("Failed to re-open session poll:", err);
      toast.error(err.message || "Failed to re-open session poll. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
    return success;
  };

  const requestReopen = () => {
    setReopenOpen(true);
  };

  const confirmReopen = async () => {
    const success = await handleReopen();
    if (success) {
      setReopenOpen(false);
    }
  };

  const openCloneModal = () => {
    if (!scheduler.data || !user?.email) return;
    const baseTitle = scheduler.data.title || "Untitled poll";
    const originalCreatorEmail = scheduler.data.creatorEmail;

    // If user is the original creator, exclude themselves from invites
    // If user is NOT the original creator, they become the new owner:
    //   - exclude themselves from invites
    //   - add original creator to invites
    const normalizedUser = normalizeEmail(user.email);
    const baseInvites = explicitParticipantEmails.filter(
      (email) => email !== normalizedUser
    );
    const groupMemberSet = scheduler.data?.questingGroupId
      ? questingGroupMemberEmailSet
      : new Set();
    const newInvites = baseInvites.filter((email) => !groupMemberSet.has(email));

    // If not the creator, add original creator to invites
    if (!isCreator && originalCreatorEmail) {
      const normalizedCreator = normalizeEmail(originalCreatorEmail);
      if (normalizedCreator && !newInvites.includes(normalizedCreator)) {
        newInvites.push(normalizedCreator);
      }
    }

    setCloneTitle(`${baseTitle} (copy)`);
    setCloneInvites(newInvites);
    setCloneClearVotes(false);
    setCloneInviteError(null);
    setCloneGroupId(scheduler.data?.questingGroupId || null);
    setCloneOpen(true);
  };

  const handleClone = async () => {
    if (!scheduler.data || !user?.uid || !user?.email) return;
    setCloneSaving(true);
    try {
      // The cloner becomes the new owner
      const newCreatorId = user.uid;
      const newCreatorEmail = user.email;

      // Participants: new creator + all invites
      const inviteEmails = Array.from(
        new Set(
          [newCreatorEmail, ...cloneInvites]
            .map((email) => normalizeEmail(email))
            .filter(Boolean)
        )
      );
      const participantIdsByEmail = await findUserIdsByEmails(inviteEmails);
      participantIdsByEmail[normalizeEmail(newCreatorEmail)] = newCreatorId;
      const participantIds = Array.from(
        new Set(Object.values(participantIdsByEmail).filter(Boolean))
      );
      const pendingInvites = inviteEmails.filter(
        (email) => normalizeEmail(email) !== normalizeEmail(newCreatorEmail)
      );

      const cloneGroup = cloneGroupId
        ? groups.find((group) => group.id === cloneGroupId) || null
        : null;
      const cloneGroupName = cloneGroup
        ? cloneGroup.name
        : cloneGroupId === scheduler.data?.questingGroupId
          ? scheduler.data?.questingGroupName || null
          : null;
      const cloneReminderSettings = createReminderSettingsSnapshot(cloneGroup?.reminders, {
        discordLinked: Boolean(cloneGroup?.discord?.channelId),
      });
      const now = new Date();
      const futureSlots = slots.data.filter(
        (slot) => slot.start && new Date(slot.start) > now
      );
      if (futureSlots.length === 0) {
        toast.error("No future slots remain to clone");
        setCloneSaving(false);
        return;
      }

      if (isCreator && !cloneClearVotes) {
        const functions = getFunctions();
        const clonePoll = httpsCallable(functions, "cloneSchedulerPoll");
        const response = await clonePoll({
          schedulerId: id,
          title: cloneTitle || `${scheduler.data.title || "Untitled poll"} (copy)`,
          inviteEmails: cloneInvites,
          clearVotes: cloneClearVotes,
          questingGroupId: cloneGroupId,
          questingGroupName: cloneGroupName,
        });
        const newId = response.data?.schedulerId;
        if (!newId) {
          throw new Error("Failed to clone poll");
        }
        await recordRecentInvitees(user.uid, cloneInvites);
        setCloneOpen(false);
        toast.success("Poll cloned successfully");
        navigate(`/scheduler/${newId}`);
        return;
      }

      const newId = crypto.randomUUID();
      await setScheduler(newId, {
        title: cloneTitle || `${scheduler.data.title || "Untitled poll"} (copy)`,
        description: scheduler.data?.description || "",
        creatorId: newCreatorId,
        creatorEmail: newCreatorEmail,
        status: "OPEN",
        participantIds,
        pendingInvites,
        timezone: scheduler.data.timezone,
        timezoneMode: scheduler.data.timezoneMode,
        voteVisibility: resolveVoteVisibility(scheduler.data?.voteVisibility),
        voteAnonymization: resolveVoteAnonymization(scheduler.data?.voteAnonymization),
        hideVoterIdentities: scheduler.data?.hideVoterIdentities === true,
        votesAllSubmitted: false,
        winningSlotId: null,
        googleEventId: null,
        googleCalendarId: null,
        createdAt: serverTimestamp(),
        questingGroupId: cloneGroupId || null,
        questingGroupName: cloneGroupName,
        reminders: cloneReminderSettings,
      });

      const slotWrites = futureSlots.map((slot) =>
        upsertSchedulerSlot(newId, slot.id, {
          start: slot.start,
          end: slot.end,
          stats: { feasible: 0, preferred: 0 },
        })
      );
      await Promise.all(slotWrites);

      await cloneEmbeddedBasicPolls(id, newId, {
        clearVotes: cloneClearVotes,
        userId: cloneClearVotes ? null : user.uid,
        votesByPollId: embeddedMyVotes,
        reminders: cloneReminderSettings,
      });

      if (!cloneClearVotes) {
        const validSlotIds = new Set(futureSlots.map((slot) => slot.id));
        const participantIdSet = new Set(participantIds);
        await Promise.all(
          visibleVoteDocs.map((voteDoc) => {
            if (voteDoc.id !== user.uid) {
              return Promise.resolve();
            }
            if (!participantIdSet.has(voteDoc.id)) return Promise.resolve();
            const nextVotes = Object.fromEntries(
              Object.entries(voteDoc.votes || {}).filter(([slotId]) =>
                validSlotIds.has(slotId)
              )
            );
            if (Object.keys(nextVotes).length === 0 && !voteDoc.noTimesWork) {
              return Promise.resolve();
            }
            return upsertSchedulerVote(newId, voteDoc.id, {
              voterId: voteDoc.id,
              userEmail: voteDoc.userEmail,
              userAvatar: voteDoc.userAvatar,
              votes: nextVotes,
              noTimesWork: Boolean(voteDoc.noTimesWork),
              updatedAt: serverTimestamp(),
            });
          })
        );
      }

      // Record only after the full clone sequence has succeeded.
      await recordRecentInvitees(user.uid, cloneInvites);

      setCloneOpen(false);
      toast.success("Poll cloned successfully");
      navigate(`/scheduler/${newId}`);
    } catch (err) {
      console.error("Failed to clone poll:", err);
      toast.error(err.message || "Failed to clone poll");
    } finally {
      setCloneSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!id) return;
    setArchiveSaving(true);
    try {
      await archivePoll(id);
      toast.success("Session poll archived");
    } catch (err) {
      console.error("Failed to archive session poll:", err);
      toast.error(err.message || "Failed to archive session poll");
    } finally {
      setArchiveSaving(false);
    }
  };

  const handleUnarchive = async () => {
    if (!id) return;
    setArchiveSaving(true);
    try {
      await unarchivePoll(id);
      toast.success("Session poll unarchived");
    } catch (err) {
      console.error("Failed to unarchive session poll:", err);
      toast.error(err.message || "Failed to unarchive session poll");
    } finally {
      setArchiveSaving(false);
    }
  };

  const handleCancelSession = async () => {
    if (!id) return;
    setCancelSaving(true);
    try {
      await updateScheduler(id, {
        status: "CANCELLED",
        updatedAt: serverTimestamp(),
      });
      const participantIds = Array.from(
        new Set(
          [
            ...(scheduler.data?.participantIds || []),
            ...questingGroupMemberIds,
          ].filter(Boolean)
        )
      );
      try {
        const normalizedCreatorEmail = normalizeEmail(scheduler.data?.creatorEmail) || null;
        const emails = new Set();
        if (participantIds.length > 0) {
          const profilesById = await fetchPublicProfilesByIds(participantIds);
          Object.values(profilesById).forEach((profile) => {
            if (!profile?.email) return;
            const normalized = normalizeEmail(profile.email);
            emails.add(normalized);
          });
        }

        const recipientEmails = Array.from(emails).filter(
          (email) => email !== normalizedCreatorEmail
        );
        const recipientUserIds = participantIds.filter(
          (participantId) => participantId !== scheduler.data?.creatorId
        );
        const recipients = { userIds: recipientUserIds, emails: recipientEmails };

        if (
          shouldEmitPollLifecycleEvent({
            eventType: "POLL_CANCELLED",
            recipients,
            questingGroupDiscord: questingGroup.data?.discord,
          })
        ) {
          await emitPollEvent({
            eventType: "POLL_CANCELLED",
            schedulerId: id,
            pollTitle: scheduler.data?.title || "Session Poll",
            actor: buildNotificationActor(user),
            payload: {
              pollTitle: scheduler.data?.title || "Session Poll",
            },
            recipients,
            dedupeKey: `poll:${id}:cancelled`,
          });
        }
      } catch (notifyErr) {
        console.error("Failed to send poll cancellation notification:", notifyErr);
      }
      toast.success("Session cancelled");
      setCancelOpen(false);
    } catch (err) {
      console.error("Failed to cancel session:", err);
      toast.error(err.message || "Failed to cancel session");
    } finally {
      setCancelSaving(false);
    }
  };

  const handleRestoreSession = async () => {
    if (!id) return;
    setRestoreSaving(true);
    try {
      const nextStatus = scheduler.data?.winningSlotId ? "FINALIZED" : "OPEN";
      await updateScheduler(id, {
        status: nextStatus,
        updatedAt: serverTimestamp(),
      });
      const participantIds = Array.from(
        new Set(
          [
            ...(scheduler.data?.participantIds || []),
            ...questingGroupMemberIds,
          ].filter(Boolean)
        )
      );
      try {
        const normalizedCreatorEmail = normalizeEmail(scheduler.data?.creatorEmail) || null;
        const emails = new Set();
        if (participantIds.length > 0) {
          const profilesById = await fetchPublicProfilesByIds(participantIds);
          Object.values(profilesById).forEach((profile) => {
            if (!profile?.email) return;
            const normalized = normalizeEmail(profile.email);
            emails.add(normalized);
          });
        }

        const recipientEmails = Array.from(emails).filter(
          (email) => email !== normalizedCreatorEmail
        );
        const recipientUserIds = participantIds.filter(
          (participantId) => participantId !== scheduler.data?.creatorId
        );
        const recipients = { userIds: recipientUserIds, emails: recipientEmails };

        if (
          shouldEmitPollLifecycleEvent({
            eventType: "POLL_RESTORED",
            recipients,
            questingGroupDiscord: questingGroup.data?.discord,
          })
        ) {
          await emitPollEvent({
            eventType: "POLL_RESTORED",
            schedulerId: id,
            pollTitle: scheduler.data?.title || "Session Poll",
            actor: buildNotificationActor(user),
            payload: {
              pollTitle: scheduler.data?.title || "Session Poll",
            },
            recipients,
            dedupeKey: `poll:${id}:restored`,
          });
        }
      } catch (notifyErr) {
        console.error("Failed to send poll restoration notification:", notifyErr);
      }
      toast.success("Session restored");
    } catch (err) {
      console.error("Failed to restore session:", err);
      toast.error(err.message || "Failed to restore session");
    } finally {
      setRestoreSaving(false);
    }
  };

  const handleNudge = async () => {
    if (!id) return;
    setNudgeSending(true);
    try {
      const result = await nudgeDiscordSessionPoll(id);
      const { nudgedCount = 0, totalNonVoters = 0 } = result || {};
      if (nudgedCount < totalNonVoters) {
        toast.success(
          `Nudged ${nudgedCount} participant${nudgedCount === 1 ? "" : "s"} on Discord. ${totalNonVoters - nudgedCount} non-voter${totalNonVoters - nudgedCount === 1 ? " has" : "s have"} not linked Discord.`
        );
      } else {
        toast.success(
          `Nudged ${nudgedCount} participant${nudgedCount === 1 ? "" : "s"} on Discord!`
        );
      }
    } catch (err) {
      console.error("Failed to nudge participants:", err);
      toast.error(err.message || "Failed to nudge participants");
    } finally {
      setNudgeSending(false);
    }
  };

  const handleSaveAutoNag = async (nextAutoNag) => {
    if (!id) return;
    await updateScheduler(id, {
      autoNag: nextAutoNag,
      updatedAt: serverTimestamp(),
    });
    toast.success(
      nextAutoNag.enabled
        ? "Auto-nag settings updated"
        : "Auto-nag turned off. Manual Nudge now follows the shared 8-hour cooldown."
    );
  };

  const handleManualCalendarSync = async () => {
    if (!id || calendarSyncing) return;
    setCalendarSyncing(true);
    try {
      const functions = getFunctions();
      const syncFn = httpsCallable(functions, "manualCalendarSync");
      const result = await syncFn({ schedulerId: id });
      const { state, changed } = result.data || {};
      if (changed) {
        toast.success(
          state === "CANCELLED"
            ? "Calendar event was deleted."
            : state === "RESCHEDULED"
              ? "Calendar event was rescheduled."
              : "Calendar sync updated."
        );
      } else {
        toast.success("Calendar is up to date.");
      }
    } catch (err) {
      console.error("Manual calendar sync failed:", err);
      toast.error(err.message || "Failed to sync calendar event.");
    } finally {
      setCalendarSyncing(false);
    }
  };

  const handleRepostDiscordPoll = async () => {
    if (!id) return;
    setRepostSending(true);
    try {
      await repostDiscordPollCard(id);
      toast.success("Reposted the poll to Discord.");
    } catch (err) {
      console.error("Failed to repost Discord poll:", err);
      toast.error(err.message || "Failed to repost the poll to Discord");
    } finally {
      setRepostSending(false);
    }
  };

  const handleDelete = async () => {
    if (!schedulerDocRef || !id) return;
    setDeleteSaving(true);
    try {
      if (deleteUpdateCalendar && scheduler.data?.googleEventId) {
        await deleteCalendarEntry();
      }
      await deleteSchedulerWithRelatedData(id);

      toast.success("Session poll deleted");
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to delete session poll:", err);
      toast.error(err.message || "Failed to delete session poll");
    } finally {
      setDeleteSaving(false);
      setDeleteOpen(false);
    }
  };

  if (scheduler.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center dark:text-slate-300">
        <LoadingState message="Loading session poll..." />
      </div>
    );
  }

  if (scheduler.error?.code === "permission-denied") {
    // A guest whose read of a link-shared scheduler is denied (allowLinkSharing
    // off, or a stale link) gets the guest denial screen with a real sign-in
    // path — never the dashboard redirect (which would loop an anon user).
    if (user?.isAnonymous) {
      return <NotPublicScreen onSignIn={() => navigate("/auth")} />;
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 text-center text-slate-600 dark:text-slate-400">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
            Access denied
          </p>
          <p className="mt-2 text-lg font-semibold text-slate-800 dark:text-slate-200">
            You don&apos;t have access to this session poll.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  // Any other listener failure (network, unavailable, etc.) surfaces an on-brand
  // error with retry — distinct from "not found" and from the loading spinner
  // (audit A-4), so a failed load never silently looks like an empty/missing poll.
  if (scheduler.error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <ErrorState
          title="Couldn't load this poll"
          message="We hit a problem loading this session poll. Check your connection and try again."
          onRetry={() => window.location.reload()}
          className="w-full max-w-md"
        />
      </div>
    );
  }

  if (!scheduler.data) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-600 dark:text-slate-400">
        Session poll not found.
      </div>
    );
  }

  const canVote = Boolean((isAcceptedParticipant || canGuestVote) && !isVotingInputLocked);
  // A guest must enter a display name before voting (unless the scheduler is
  // fully anonymous — then nobody sees the name, so skip the card). Once a name
  // is captured (or the guest already has a vote), the voting grid renders.
  const showGuestNameCard =
    canGuestVote &&
    !guestName &&
    shouldShowNameCard({ scheduler: scheduler.data, existingVote: userVote.data });

  if (showGuestNameCard) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 py-10">
        <GuestNameCard onSubmit={(name) => setGuestName(name)} />
      </div>
    );
  }
  const embeddedPollsSorted = [...embeddedPolls].sort((left, right) => {
    const leftOrder = Number.isFinite(left?.order) ? left.order : Number.MAX_SAFE_INTEGER;
    const rightOrder = Number.isFinite(right?.order) ? right.order : Number.MAX_SAFE_INTEGER;
    return leftOrder - rightOrder;
  });
  const unfinalizedEmbeddedPollCount = embeddedPollsSorted.filter(
    (poll) => (poll?.status || "OPEN") !== "FINALIZED"
  ).length;
  const embeddedCompletedCount = embeddedPollsSorted.filter((poll) =>
    hasSubmittedEmbeddedVote(poll, embeddedMyVotes[poll.id])
  ).length;
  const requiredEmbeddedPolls = embeddedPollsSorted.filter((poll) => poll.required);
  const requiredEmbeddedPendingCount = requiredEmbeddedPolls.filter(
    (poll) => !hasSubmittedEmbeddedVote(poll, embeddedMyVotes[poll.id])
  ).length;
  const showSessionNudgeButton = Boolean(
    isCreator &&
      scheduler.data?.discord?.messageId &&
      scheduler.data?.status === "OPEN" &&
      (sessionPollMissingNudgeUserIds.length > 0 || hasRequiredEmbeddedNudgeTargets)
  );
  const pendingInviteMeta =
    (normalizedUserEmail && scheduler.data.pendingInviteMeta?.[normalizedUserEmail]) || {};
  const inviterLabel = pendingInviteMeta.invitedByEmail || scheduler.data.creatorEmail || "someone";
  const inviterProfile =
    participantMapByEmail.get(normalizeEmail(inviterLabel)) || { email: inviterLabel };
  const voteVisibilityMode = resolveVoteVisibility(scheduler.data?.voteVisibility);
  const voteVisibilityHint = showSchedulerVoteDetails
    ? null
    : voteVisibilityMode === "hidden_while_voting"
      ? "Vote details unlock after you submit your vote."
      : voteVisibilityMode === "hidden_until_all_voted"
        ? "Vote details unlock once all participants have voted."
        : voteVisibilityMode === "hidden_until_finalized"
          ? "Vote details unlock once the poll is finalized."
          : voteVisibilityMode === "hidden"
            ? "Only the poll creator can view participant vote details."
            : null;
  const pollDescription = (scheduler.data.description || "").trim();
  const canAccess =
    isCreator ||
    scheduler.data.allowLinkSharing ||
    isAcceptedParticipant ||
    isPendingInvite;
  const canRepostDiscordPoll = Boolean(
    isCreator &&
      scheduler.data?.questingGroupId &&
      questingGroup.data?.discord?.channelId &&
      questingGroup.data?.discord?.guildId
  );
  if (!canAccess) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 text-center text-slate-600 dark:text-slate-400">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
            Access denied
          </p>
          <p className="mt-2 text-lg font-semibold text-slate-800 dark:text-slate-200">
            This session poll requires an invite.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-3xl bg-white p-8 shadow-xl shadow-slate-200 dark:bg-slate-800 dark:shadow-slate-900/50">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">
                Session Poll
              </p>
              <h2 className="text-2xl font-display font-semibold dark:text-slate-100">{scheduler.data.title}</h2>
              <PollMarkdownContent
                content={pollDescription}
                className="mt-2 text-sm text-slate-600 dark:text-slate-300"
              />
              <PollStatusMeta
                scheduler={scheduler.data}
                winningSlot={winningSlot}
                slots={slots.data}
                allVotesIn={allVotesIn}
                isArchived={isPollArchived}
                questingGroupName={questingGroupName}
                questingGroupColor={questingGroupColor}
                guestCount={nonGroupParticipants.length}
                displayTimeZone={displayTimeZone}
                showTimeZone={showTimeZone}
              />
              <PollDiscordMetaRow
                statusLabel={discordStatus}
                messageUrl={discordMessageUrl}
                eventUrl={discordEventUrl}
                pendingSync={scheduler.data?.discord?.pendingSync === true}
                className="mt-2"
              >
                {showSessionNudgeButton ? (
                  autoNagEnabled ? (
                    <AutoNagStatusControl
                      value={activeAutoNagSettings}
                      group={questingGroup.data}
                      timeZone={activeAutoNagSettings?.timeZone || pollTimeZone || displayTimeZone || "UTC"}
                      votingDeadlineAt={resolveSchedulerVotingDeadline(scheduler.data)}
                      earliestSlotAt={earliestSlotAt}
                      nagState={scheduler.data?.nagState}
                      onSave={handleSaveAutoNag}
                    />
                  ) : (
                    <PollNudgeButton
                      onClick={handleNudge}
                      sending={nudgeSending}
                      lastNudgeAt={latestNagAt}
                    />
                  )
                ) : null}
                <SessionCalendarControl
                  scheduler={scheduler.data}
                  viewerUid={user?.uid}
                  slot={winningSlot}
                  viewer={calendarViewer}
                  filenameBase={id}
                  variant="chip"
                />
                {googleCalendarUrl && calendarSyncEnabled && isCreator ? (
                  calendarSyncing ? (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-200">
                      Syncing…
                    </span>
                  ) : !isCalendarSyncActionable(calendarSyncState) ? (
                    // Calendar unlinked: the OAuth token is gone, so a manual
                    // sync would only fail. Show a neutral, non-actionable badge.
                    <span
                      data-testid="calendar-sync-unlinked"
                      className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400"
                      title="Google Calendar disconnected — reconnect in Settings to resume sync"
                    >
                      {calendarSyncLabel(calendarSyncState)}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleManualCalendarSync}
                      className={`rounded-full border px-2 py-0.5 text-xs font-semibold transition-colors ${
                        !calendarSyncState || calendarSyncState === "OK" || calendarSyncState === "SYNCED"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700/60 dark:bg-emerald-900/30 dark:text-emerald-200 dark:hover:bg-emerald-900/50"
                          : calendarSyncState === "CANCELLED"
                            ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-700/60 dark:bg-rose-900/30 dark:text-rose-200 dark:hover:bg-rose-900/50"
                            : calendarSyncState === "ERROR"
                              ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-700/60 dark:bg-rose-900/30 dark:text-rose-200 dark:hover:bg-rose-900/50"
                              : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50"
                      }`}
                      title="Click to sync now"
                    >
                      {calendarSyncLabel(calendarSyncState)}
                    </button>
                  )
                ) : null}
                {googleCalendarUrl && calendarSyncEnabled && !isCreator && calendarSyncState && calendarSyncState !== "ERROR" ? (
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${
                    calendarSyncState === "OK" || calendarSyncState === "SYNCED"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700/60 dark:bg-emerald-900/30 dark:text-emerald-200"
                      : calendarSyncState === "CANCELLED"
                        ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-700/60 dark:bg-rose-900/30 dark:text-rose-200"
                        : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-200"
                  }`}>
                    {calendarSyncLabel(calendarSyncState)}
                  </span>
                ) : null}
              </PollDiscordMetaRow>
              <SessionVotingDeadlineStatus
                scheduler={scheduler.data}
                now={sessionVotingNow}
                displayTimeZone={displayTimeZone}
                showTimeZone={showTimeZone}
              />
              {/* Calendar sync: error state banner */}
              {scheduler.data?.calendarSync?.state === "ERROR" && isCreator && (
                <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 dark:border-rose-800 dark:bg-rose-900/30">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600 dark:text-rose-400" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-rose-800 dark:text-rose-200">
                        Calendar sync paused
                      </p>
                      <p className="mt-0.5 text-xs text-rose-700 dark:text-rose-300">
                        {scheduler.data.calendarSync.error?.message || "Re-link your calendar in Settings to resume."}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="rounded-full border border-slate-200 p-2 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
                  aria-label="Poll options"
                >
                  <MoreVertical className="h-5 w-5 text-slate-500 dark:text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {/* Edit - owner only, not archived, not finalized */}
                {isCreator && !isPollArchived && !isLocked && (
                  <DropdownMenuItem onClick={() => navigate(`/scheduler/${id}/edit`)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit poll
                  </DropdownMenuItem>
                )}
                {/* Clone - available to everyone */}
                <DropdownMenuItem onClick={openCloneModal}>
                  <Copy className="mr-2 h-4 w-4" />
                  Clone poll
                </DropdownMenuItem>
                {canCopyVotes && (
                  <DropdownMenuItem onClick={() => setCopyVotesOpen(true)}>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy votes
                  </DropdownMenuItem>
                )}
                {canRepostDiscordPoll && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleRepostDiscordPoll} disabled={repostSending}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {repostSending ? "Reposting..." : "Repost Discord poll"}
                    </DropdownMenuItem>
                  </>
                )}
                {isExplicitParticipant && !isCreator && !isPendingInvite && (
                  <DropdownMenuItem
                    onClick={() => {
                      if (isGroupMember) {
                        toast.error("Leave the questing group to be removed from this poll.");
                        return;
                      }
                      setLeaveOpen(true);
                    }}
                  >
                    Leave poll
                  </DropdownMenuItem>
                )}
                {/* Archive/Unarchive - available to everyone */}
                {isPollArchived ? (
                  <DropdownMenuItem onClick={handleUnarchive} disabled={archiveSaving}>
                    <ArchiveRestore className="mr-2 h-4 w-4" />
                    {archiveSaving ? "Unarchiving..." : "Unarchive poll"}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={handleArchive} disabled={archiveSaving}>
                    <Archive className="mr-2 h-4 w-4" />
                    {archiveSaving ? "Archiving..." : "Archive poll"}
                  </DropdownMenuItem>
                )}
                {/* Re-open - owner only, finalized polls */}
                {isCreator && scheduler.data?.status === "FINALIZED" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={requestReopen} disabled={saving}>
                      Re-open poll
                    </DropdownMenuItem>
                  </>
                )}
                {isCreator && scheduler.data?.status !== "CANCELLED" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setCancelOpen(true)}
                      className="text-amber-700 focus:text-amber-700 dark:text-amber-200 dark:focus:text-amber-200"
                    >
                      Cancel session
                    </DropdownMenuItem>
                  </>
                )}
                {isCreator && scheduler.data?.status === "CANCELLED" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleRestoreSession} disabled={restoreSaving}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      {restoreSaving ? "Restoring..." : "Restore session"}
                    </DropdownMenuItem>
                  </>
                )}
                {/* Delete - owner only */}
                {isCreator && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeleteOpen(true)}
                      className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete poll
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {isLocked && scheduler.data?.winningSlotId && !sessionEnded && (
            <RsvpPanel
              schedulerId={id}
              user={user}
              userVote={userVote.data ? { id: user?.uid, ...userVote.data } : null}
              allVotes={mergedVoteDocs}
              winningSlotId={scheduler.data.winningSlotId}
              participantProfiles={Object.values(profilesByUserId || {})}
              identityDisplayMode={schedulerVoteIdentityDisplayMode}
              onAttendingTransitionedOff={handleRsvpAttendingTransitionedOff}
            />
          )}

          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {participantCount} participants
          </p>

          <div className="mt-6 rounded-3xl border border-slate-200/70 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Participants</h3>
                <p
                  data-testid="participant-vote-summary"
                  className="text-xs text-slate-500 dark:text-slate-400"
                >
                  {/* Unified phrasing: the leading figures ALWAYS mean
                      "{invited} total · {participants voted}"; the guest count is
                      appended as a separate clause only when a guest has voted,
                      so the meaning of the figures never flips between modes. The
                      participant "voted" figure is participant-only (guests are
                      broken out), keeping it ≤ the invited total. */}
                  {`${participantCount} total · ${schedulerTally.hasGuests ? schedulerTally.participantVoted : voteCount} voted`}
                  {schedulerTally.hasGuests
                    ? ` · ${schedulerTally.guestVoted} guest${schedulerTally.guestVoted === 1 ? "" : "s"}`
                    : ""}
                </p>
              </div>
              {/*
                Invitee stack = the roster (Layer C identity), gated by
                ANONYMIZATION only — never the neutral silhouette. Under hide-only
                (`schedulerVoteIdentityDisplayMode === "hidden"`) the invitee
                NAMES stay real (only the per-person status badge below reads
                "Hidden"); under anonymization `participantsForDisplay` is already
                aliased. So always render the (real or aliased) avatar stack.
              */}
              <AvatarStackWithColors
                users={participantsForDisplay}
                max={8}
                size={24}
                onClick={schedulerHandleAvatarClick}
              />
            </div>
            {voteVisibilityHint ? (
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">{voteVisibilityHint}</p>
            ) : null}
            {questingGroupName && (
              <div
                className="mt-3 rounded-2xl border px-3 py-3 text-xs"
                style={{
                  borderColor: questingGroupColor || "#10b981",
                  backgroundColor: `${questingGroupColor || "#10b981"}22`,
                }}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-100">
                    Questing group
                  </span>
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:text-slate-100"
                    style={{
                      backgroundColor: `${questingGroupColor || "#10b981"}33`,
                    }}
                  >
                    {questingGroupName}
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-300">
                    {questingGroupMembers.length} members
                  </span>
                </div>
                <div className="mt-2 grid gap-2">
                  {groupUsersWithStatusForDisplay.length === 0 && (
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      No members listed for this group.
                    </span>
                  )}
                  {groupUsersWithStatus.map((rawMember, index) => {
                    const row = buildSchedulerRosterRow({
                      rawMember,
                      displayMember: groupUsersWithStatusForDisplay[index],
                      currentUser: user,
                      viewer: rosterViewer,
                      settings: rosterSettings,
                      pollState: rosterPollState,
                      statusKnown: rosterStatusKnown,
                    });
                    const displayMember = row.displayUser;
                    return (
                    <button
                      type="button"
                      key={rawMember.id || rawMember.email}
                      className={`flex items-center gap-2 rounded-xl border border-transparent bg-white/70 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors dark:bg-slate-900/70 dark:text-slate-200 text-left ${
                        row.canOpenIdentityActions
                          ? "cursor-pointer hover:bg-white dark:hover:bg-slate-800/90"
                          : "cursor-default"
                      }`}
                      onClick={
                        row.canOpenIdentityActions
                          ? () => schedulerHandleUserClick(displayMember)
                          : undefined
                      }
                    >
                      {/* Roster identity is gated by anonymization only — real
                          under hide-only, aliased under anonymization, NEVER a
                          neutral silhouette. Only the status badge reads "Hidden". */}
                      <AvatarBubbleWithColors user={displayMember} size={22} />
                      <UserIdentity user={displayMember} className="text-xs" />
                      <span
                        className={`ml-auto text-[10px] font-semibold ${rosterStatusBadgeClass(row)}`}
                      >
                        {row.statusLabel}
                      </span>
                    </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {questingGroupName && nonGroupParticipants.length === 0 && (
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  No additional participants outside the questing group.
                </span>
              )}
              {nonGroupParticipants.map((participant, index) => {
                const row = buildSchedulerRosterRow({
                  rawMember: participant,
                  displayMember: nonGroupParticipantsForDisplay[index],
                  currentUser: user,
                  viewer: rosterViewer,
                  settings: rosterSettings,
                  pollState: rosterPollState,
                  statusKnown: rosterStatusKnown,
                });
                const displayParticipant = row.displayUser;
                return (
                  <div
                    key={participant.id || participant.email}
                    role={row.canOpenIdentityActions ? "button" : undefined}
                    tabIndex={row.canOpenIdentityActions ? 0 : undefined}
                    onClick={
                      row.canOpenIdentityActions
                        ? () => schedulerHandleUserClick(displayParticipant)
                        : undefined
                    }
                    onKeyDown={
                      row.canOpenIdentityActions
                        ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); schedulerHandleUserClick(displayParticipant); } }
                        : undefined
                    }
                    className={`flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs transition-colors dark:border-slate-700 dark:bg-slate-900 ${
                      row.canOpenIdentityActions
                        ? "hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                        : ""
                    }`}
                  >
                    {/* Roster identity is gated by anonymization only — real
                        under hide-only, aliased under anonymization, NEVER a
                        neutral silhouette. Only the status badge reads "Hidden". */}
                    <AvatarBubbleWithColors user={displayParticipant} size={18} />
                    <UserIdentity
                      user={displayParticipant}
                      className="text-slate-700 dark:text-slate-200"
                    />
                    <span
                      className={`text-[10px] font-semibold ${rosterStatusBadgeClass(row)}`}
                    >
                      {row.statusLabel}
                    </span>
                    {isCreator &&
                      normalizeEmail(participant.email) !==
                        normalizeEmail(scheduler.data.creatorEmail) &&
                      !participant.isGroupMember && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMemberToRemove(participant);
                          setRemoveMemberOpen(true);
                        }}
                        className="ml-1 text-[10px] font-semibold text-red-500 hover:text-red-600"
                        title="Remove participant"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {pendingInviteNonParticipants.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Pending invites
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {pendingInviteNonParticipants.map((invitee) => (
                    <div
                      key={invitee.email}
                      className="flex items-center gap-2 rounded-full border border-dashed border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
                    >
                      <UserIdentity user={invitee} showIdentifier={false} />
                      {isCreator && (
                        <button
                          type="button"
                          onClick={() => {
                            setInviteToRevoke(invitee.email);
                            setRevokeInviteOpen(true);
                          }}
                          className="text-[10px] font-semibold text-amber-700 hover:text-amber-800 dark:text-amber-200 dark:hover:text-amber-100"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* View toggle - moved below participants */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-700">
              <button
                type="button"
                onClick={() => setView("list")}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  view === "list" ? "bg-brand-primary text-white" : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-600"
                }`}
              >
                List View
              </button>
              <button
                type="button"
                onClick={() => setView("calendar")}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  view === "calendar" ? "bg-brand-primary text-white" : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-600"
                }`}
              >
                Calendar View
              </button>
            </div>
            {!isLocked && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Select a winning slot from Results when ready to finalize.
              </p>
            )}
          </div>

          {view === "calendar" && (
            <div className="mt-6 rounded-3xl border border-slate-200/70 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div
                    data-testid="calendar-no-times-work-toggle"
                    className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200/70 bg-white px-4 py-2 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                  >
                    <span>No times work for me</span>
                    <Switch
                      checked={noTimesWork}
                      disabled={isVotingInputLocked || allSlotsPast}
                      onCheckedChange={toggleNoTimesWork}
                    />
                  </div>
                  {(calendarView === "week" || calendarView === "day") && (
                    <div
                      data-testid="calendar-overlay-visibility-toggle"
                      className="inline-flex items-center gap-1 rounded-2xl border border-slate-200/70 bg-white p-1 text-[11px] font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                      role="group"
                      aria-label="Linked-event overlays"
                    >
                      {[
                        { value: "hide", label: "Hide" },
                        { value: "overlapping", label: "Overlapping only" },
                        { value: "all", label: "Show all" },
                      ].map((opt) => {
                        const active = overlayVisibilityMode === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            data-testid={`calendar-overlay-mode-${opt.value}`}
                            data-active={active ? "true" : "false"}
                            onClick={() => setOverlayVisibilityMode(opt.value)}
                            className={
                              active
                                ? "rounded-xl bg-slate-200/80 px-3 py-1 text-slate-900 dark:bg-slate-700 dark:text-slate-100"
                                : "rounded-xl px-3 py-1 hover:bg-slate-100 dark:hover:bg-slate-800"
                            }
                            aria-pressed={active}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <CalendarJumpControls
                  hasEvents={hasEvents}
                  hasEventsInView={hasEventsInView}
                  onPrev={jumpPrev}
                  onNext={jumpNext}
                  onPrevWindow={jumpPrevWindow}
                  onNextWindow={jumpNextWindow}
                  label="Jump to slot"
                />
              </div>
              {/* Below md the month/week grids are unreadable crushed into
                  ~393px. Give the grid a comfortable min-width and let the
                  region scroll horizontally (Google-Calendar-on-mobile style) —
                  the toggle, toolbar, and all RBC and month-slot selectors stay
                  intact, so this is purely a layout compromise. md+ is a no-op. */}
              <div className="-mx-1 overflow-x-auto px-1 md:mx-0 md:overflow-x-visible md:px-0">
              {/* Day view is a single column — it fits a phone, so skip the
                  44rem min-width (which would force a pointless horizontal
                  scroll). Month/week keep it for their 7 columns. md+ = no-op. */}
              <div className={`${calendarView === "day" ? "min-w-0" : "min-w-[44rem]"} md:min-w-0`}>
              <Calendar
                key={calendarKey}
                localizer={localizer}
                events={allCalendarEvents}
                startAccessor="start"
                endAccessor="end"
                selectable
                scrollToTime={scrollToTime}
                enableAutoScroll={calendarView !== "month"}
                date={calendarDate}
                onNavigate={setCalendarDate}
                views={["month", "week", "day"]}
                view={calendarView}
                onView={(nextView) => setCalendarView(nextView)}
                onSelectSlot={(slotInfo) => {
                  if (calendarView === "month") return;
                  setModalDate(slotInfo.start);
                }}
                onSelectEvent={(event) => {
                  // Chunk E4 — overlay events shouldn't drive selection or
                  // open the vote modal; their deep-link button (↗) is the
                  // only affordance. Ignore selection events on overlays.
                  if (event.__overlayKind) return;
                  setSelectedEventId(event.id);
                  if (calendarView === "month") return;
                  setModalDate(event.start);
                }}
                doShowMoreDrillDown={false}
                onShowMore={(events, date) => {
                  if (Array.isArray(events) && events.length > 0) {
                    const firstSlot = events.find((e) => !e.__overlayKind);
                    if (firstSlot) setSelectedEventId(firstSlot.id || null);
                  }
                  setModalDate(date);
                }}
                dayPropGetter={(date) => {
                  const classes = [];
                  if (isBefore(date, startOfDay(new Date()))) {
                    classes.push("rbc-past-day");
                  }
                  // Chunk E4 — per-day column tint when an all-day overlay
                  // covers this date. Conflict-wins precedence is enforced
                  // upstream when overlayDayTintByKey is built.
                  const tintKind = overlayDayTintByKey[format(date, "yyyy-MM-dd")];
                  if (tintKind && calendarView !== "month") {
                    classes.push(`scheduler-overlay-day-tint--${tintKind}`);
                  }
                  return classes.length ? { className: classes.join(" ") } : {};
                }}
                slotPropGetter={(date) => {
                  if (isBefore(date, startOfHour(new Date()))) {
                    return { className: "rbc-past-slot" };
                  }
                  return {};
                }}
                components={{
                  event: EventCell,
                  toolbar: CalendarToolbar,
                }}
                eventPropGetter={(event) => {
                  // Chunk E4 — overlay events render via CalendarOverlayBlock,
                  // which paints its own translucent fill via the variant CSS
                  // class. Wipe RBC's default .rbc-event chrome (background,
                  // border, padding) so it doesn't fight the inner block.
                  if (event.__overlayKind) {
                    const overlay = event.__overlayRecord || null;
                    const baseStyle = {
                      backgroundColor: "transparent",
                      border: "none",
                      padding: 0,
                      // z-index 1 keeps overlay below opaque poll slots (which
                      // sit at the default z-index 2 in RBC's stacking).
                      zIndex: 1,
                    };
                    // Timed (non-allDay) overlays get right-lane geometry so
                    // poll slots keep the left lane (mirror-gap layout).
                    if (overlay && !overlay.allDay && overlay.laneStyle) {
                      Object.assign(baseStyle, overlay.laneStyle);
                    }
                    return { style: baseStyle };
                  }
                  const style = {};
                  const winningSlotId = scheduler.data?.winningSlotId;
                  const isFinalized =
                    scheduler.data?.status === "FINALIZED" && Boolean(winningSlotId);
                  const isWinner = winningSlotId === event.id;
                  const myVote = draftVotes[event.id] || null;

                  const isPrevWinner = previousWinnerSlotId === event.id;

                  // Past flag — used by the past-slot branch below as well
                  // as the slotVisualsFor() call further down. Computing it
                  // up-front keeps both call-sites consistent.
                  const eventIsPast = pastSlotIds.has(event.id);

                  if (isFinalized) {
                    if (isWinner) {
                      // V-1: darken green-500→green-700 so white text passes AA
                      // (was 2.28:1 → 5.0:1). Lighter green-600 edge for shape.
                      style.backgroundColor = "#15803d";
                      style.borderColor = "#16a34a";
                      style.color = "#ffffff";
                      style.opacity = 0.95;
                      style.fontWeight = 600;
                    } else if (isPrevWinner) {
                      // V-1: amber-500→amber-700 (white 2.15:1 → ~4.6:1).
                      style.backgroundColor = "#b45309";
                      style.borderColor = "#d97706";
                      style.color = "#ffffff";
                      style.opacity = 0.6;
                      style.fontWeight = 500;
                    } else {
                      // V-1: the inner slot text is text-white, so the light
                      // #e2e8f0 fill rendered invisible white-on-light text.
                      // Use a muted slate that pairs with white at AA, kept
                      // faded (opacity) to read as "inactive / not chosen".
                      style.backgroundColor = "#64748b";
                      style.borderColor = "#475569";
                      style.color = "#ffffff";
                      style.opacity = 0.75;
                      style.fontWeight = 500;
                    }
                  } else if (eventIsPast) {
                    // Chunk G3: desaturate past slots in the calendar views.
                    // The cell-internal QuickVoteCluster + click-to-vote
                    // handlers are already gated on canInteract (false for
                    // past), so no pointer-events:none here — RBC still needs
                    // to manage selection state on its outer .rbc-event.
                    style.filter = "grayscale(0.85)";
                    style.opacity = 0.55;
                    style.cursor = "not-allowed";
                  } else if (noTimesWork) {
                    style.backgroundColor = "#64748b";
                    style.borderColor = "#475569";
                    style.color = "#e2e8f0";
                    style.opacity = 0.45;
                  } else if (myVote === "PREFERRED") {
                    // Chunk G palette: PREFERRED → green. V-1: green-600→700
                    // so the inner white slot text passes AA (3.30:1 → 5.0:1);
                    // matches the list-view .scheduler-vote-fill-preferred.
                    style.backgroundColor = "#15803d";
                    style.borderColor = "#22c55e";
                    style.color = "#ffffff";
                  } else if (myVote === "FEASIBLE") {
                    // Chunk G palette: FEASIBLE → blue. V-1: blue-600→700 for
                    // white-text AA margin (4.6:1 → 6.7:1); matches the
                    // list-view .scheduler-vote-fill-feasible.
                    style.backgroundColor = "#1d4ed8";
                    style.borderColor = "#3b82f6";
                    style.color = "#ffffff";
                  } else {
                    // Chunk H3: not-voted slots — opaque platinum/slate so
                    // they're clearly distinguishable from feasible-blue
                    // AND from past-slot translucent gray. V-1: slate-400→500
                    // so the inner white slot text passes AA (2.56:1 → 4.6:1).
                    style.backgroundColor = "#64748b"; // slate-500
                    style.borderColor = "#475569"; // slate-600
                    style.color = "#ffffff";
                  }

                  // Chunk G: layer the engine glow + dashed-auto-voted border
                  // via className. RBC supports {style, className} together.
                  // The inner cell wrapper (month branch + SchedulerCalendarSlot)
                  // is structural-only; visuals live here on the outer .rbc-event.
                  //
                  // Chunk G6: synthesize the urgent-override engineKind so
                  // the cell glow turns red when the engine has cleared a
                  // manual vote and there are still unacknowledged refIds.
                  const autoReason = voteAutoReasonBySlotId?.[event.id] || null;
                  const autoSource = voteAutoSourceBySlotId?.[event.id] || null;
                  // H12: red-alert treatment is gated on autoVoteEnabled — if the
                  // user has turned off auto-vote, downgrade urgent → amber conflict.
                  const isUrgentOverride =
                    autoVoteEnabled &&
                    autoSource === "auto-conflict-override" &&
                    autoReason?.kind === "conflict" &&
                    hasUnacknowledgedRefIds(autoReason);
                  const engineKind = isUrgentOverride
                    ? "conflict-override-urgent"
                    : autoReason?.kind || null;
                  const { glowClass, borderClass } = slotVisualsFor({
                    voteState: myVote || null,
                    engineKind,
                    autoSource,
                    isPast: eventIsPast,
                  });

                  // Selection glow trade-off: while a slot is selected the
                  // inline boxShadow wins, temporarily replacing the engine
                  // glow from the className. Acceptable since selection is a
                  // transient click-flow state — merging two box-shadows here
                  // would require duplicating the engine glow color strings.
                  if (selectedEventId === event.id) {
                    style.boxShadow =
                      "0 0 0 2px rgba(59, 130, 246, 0.7), 0 0 12px rgba(59, 130, 246, 0.35)";
                  }

                  const className = [glowClass, borderClass].filter(Boolean).join(" ") || undefined;
                  if (Object.keys(style).length && className) return { style, className };
                  if (Object.keys(style).length) return { style };
                  if (className) return { className };
                  return {};
                }}
                style={{ height: 780 }}
              />
              </div>
              </div>
            </div>
          )}

          {allSlotsPast && !isLocked && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-300">
              All proposed time slots have passed.
            </div>
          )}

          {view === "list" && (
            <div className="mt-6 space-y-4">
              <div
                data-testid="list-no-times-work-toggle"
                className="flex items-center justify-between rounded-2xl border border-slate-200/70 bg-white px-4 py-3 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              >
                <span>No times work for me</span>
                <Switch
                  checked={noTimesWork}
                  disabled={isVotingInputLocked || allSlotsPast}
                  onCheckedChange={toggleNoTimesWork}
                />
              </div>
              {slotListState === "loading" && (
                <LoadingState message="Loading slots..." className="py-4" />
              )}
              {slotListState === "error" && (
                <ErrorState
                  title="Couldn't load slots"
                  message="The proposed times failed to load. Check your connection and try again."
                  onRetry={() => window.location.reload()}
                  className="my-2"
                />
              )}
              {slotListState === "empty" && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No slots have been proposed yet.
                </p>
              )}
              {slotsByDate.map((slot) => {
                const vote = draftVotes[slot.id];
                const counts = tallies[slot.id] || { feasible: 0, preferred: 0 };
                const voters = slotVotersForDisplay[slot.id] || { feasible: [], preferred: [] };
                const startDate = slot.start ? new Date(slot.start) : null;
                const slotRangeLabel = startDate
                  ? formatZonedDateTimeRange({
                      start: startDate,
                      end: slot.end ? new Date(slot.end) : null,
                      timeZone: displayTimeZone,
                      startPattern: "EEE, MMM d, yyyy · h:mm a",
                      endPattern: "h:mm a",
                      showTimeZone,
                    })
                  : "Slot";
                const blocker = userBlockersBySlotId?.[slot.id] || null;
                const autoSource = voteAutoSourceBySlotId?.[slot.id] || null;
                const autoReason = voteAutoReasonBySlotId?.[slot.id] || null;
                const isPastSlot = pastSlotIds.has(slot.id);
                // Phase 3 M5 — tier-aware "we voted for you" chip.
                const autoChipVariant = pillVariantFor({
                  autoVoteMode,
                  voteAutoSource: autoSource,
                  voteProposed: voteProposedBySlotId[slot.id] === true,
                  hasVote: Boolean(vote),
                  isUserTouched: userTouchedSlotsSet.has(slot.id),
                  previousManualVote: autoReason?.previousManualVote ?? null,
                });
                // Chunk G: list view has full control of the wrapper (no RBC),
                // so apply the full triple. G3 dropped the prior red past-slot
                // wash — past slots now get the .scheduler-slot-past class
                // (grayscale + opacity + pointer-events:none) via fillClass,
                // freeing red for the engine override treatment in G6.
                //
                // Chunk G6 + H12: synthesize the urgent-override engineKind so
                // the row glow turns red when the engine has cleared a
                // manual vote and there are still unacknowledged refIds.
                // H12: red-alert is gated on autoVoteEnabled; if the user turned
                // off auto-vote the red treatment downgrades to amber conflict.
                const isUrgentOverride =
                  autoVoteEnabled &&
                  autoSource === "auto-conflict-override" &&
                  autoReason?.kind === "conflict" &&
                  hasUnacknowledgedRefIds(autoReason);
                const listEngineKind = isUrgentOverride
                  ? "conflict-override-urgent"
                  : autoReason?.kind || null;
                const { fillClass, glowClass, borderClass } = slotVisualsFor({
                  voteState: vote || null,
                  engineKind: listEngineKind,
                  autoSource,
                  isPast: isPastSlot,
                });
                // H12 + H15: list view uses transparent bg for any no-vote slot
                // (regardless of conflict). The platinum fill is calendar-only.
                // Past slots still need scheduler-slot-past (grayscale+opacity).
                const listFillClass = (!vote && !isPastSlot) ? "" : fillClass;
                return (
                  <div
                    key={slot.id}
                    data-testid={`list-slot-${slot.id}`}
                    title={isPastSlot ? "This time slot is in the past." : undefined}
                    className={`grid gap-3 rounded-2xl border px-4 py-3 md:grid-cols-[1.4fr_1fr_1fr_auto] border-slate-200/70 dark:border-slate-700 ${listFillClass} ${glowClass} ${borderClass}`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {slotRangeLabel}
                        </p>
                        {isPastSlot ? (
                          <span
                            data-testid={`list-slot-past-marker-${slot.id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400"
                          >
                            <Clock className="h-3 w-3" aria-hidden="true" />
                            <span>Past</span>
                          </span>
                        ) : (
                          <>
                            {(() => {
                              // Unified per-slot indicators: ONE icon per signal
                              // kind (conflict / preferred-align / feasible-align
                              // / no-group-match), merging the auto-vote engine
                              // reason with the calendar-overlay records so a
                              // voter with auto-vote ON and a calendar conflict
                              // sees a single deep-linkable triangle instead of
                              // two. Overlay-sourced rows are suppressed in
                              // "hide" mode (engine-reason signals still render).
                              const slotOverlapRecords =
                                overlayVisibilityMode === "hide"
                                  ? []
                                  : (listOverlayRecordsBySlotId[slot.id] || []);
                              const slotSignals = buildSlotIndicatorSignals({
                                reason: autoReason,
                                voteAutoSource: autoSource,
                                overlayRecords: slotOverlapRecords,
                              });
                              return slotSignals.map((sig) => (
                                <SlotIndicator
                                  key={sig.key}
                                  reason={sig.reason}
                                  voteAutoSource={sig.voteAutoSource}
                                  onRestore={handleRestoreOverride}
                                  onConfirm={handleConfirmOverride}
                                  schedulerId={id}
                                  slotId={slot.id}
                                  displayTimeZone={displayTimeZone}
                                  groupNames={groupNames}
                                  // The engine-driven signal keeps SlotIndicator's
                                  // default testid (`slot-indicator-<slotId>`) so
                                  // existing locators resolve; overlay-only signals
                                  // get a distinct kind-suffixed testid.
                                  testId={
                                    sig.fromEngine
                                      ? undefined
                                      : `list-slot-indicator-${sig.kind}-${slot.id}`
                                  }
                                />
                              ));
                            })()}
                            {autoChipVariant.mode && (
                              <SlotAutoIndicator
                                mode={autoChipVariant.mode}
                                isOverride={autoChipVariant.isOverride}
                                previousManualVote={
                                  autoReason?.previousManualVote ?? null
                                }
                                reasonText={autoReason?.humanLabel || null}
                                disabled={saving}
                                onRestore={(prev) =>
                                  handleAutoChipRestore(
                                    slot.id,
                                    autoChipVariant.mode,
                                    prev,
                                  )
                                }
                                testId={`list-slot-auto-chip-${slot.id}`}
                              />
                            )}
                          </>
                        )}
                      </div>
                      {showSchedulerVoteDetails ? (
                        <div data-testid={`list-slot-tally-${slot.id}`}>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Preferred {counts.preferred} · Feasible {counts.feasible}
                          </p>
                          <div className="mt-2 flex flex-col gap-2">
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-semibold">Preferred</span>
                              <VotingAvatarStack users={voters.preferred} size={20} colorMap={colorMap} onClick={schedulerHandleAvatarClick} />
                              <span className="text-slate-400 dark:text-slate-500">
                                {counts.preferred}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-semibold">Feasible</span>
                              <VotingAvatarStack users={voters.feasible} size={20} colorMap={colorMap} onClick={schedulerHandleAvatarClick} />
                              <span className="text-slate-400 dark:text-slate-500">
                                {counts.feasible}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Feasible
                      </span>
                      <VoteToggle
                        checked={vote === "FEASIBLE" || vote === "PREFERRED"}
                        disabled={!canVote || noTimesWork || vote === "PREFERRED" || pastSlotIds.has(slot.id)}
                        label={`Toggle feasible vote for ${slotRangeLabel}`}
                        testId={`list-slot-feasible-${slot.id}`}
                        onChange={(checked) => setVote(slot.id, checked ? "FEASIBLE" : null)}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Preferred
                      </span>
                      <VoteToggle
                        checked={vote === "PREFERRED"}
                        disabled={!canVote || noTimesWork || pastSlotIds.has(slot.id)}
                        label={`Toggle preferred vote for ${slotRangeLabel}`}
                        testId={`list-slot-preferred-${slot.id}`}
                        onChange={(checked) =>
                          setVote(slot.id, checked ? "PREFERRED" : null)
                        }
                      />
                    </div>
                    <div className="flex items-center justify-end">
                      <SlotStatusText
                        vote={vote}
                        hasConflict={!!blocker}
                        noTimesWork={noTimesWork}
                        isUrgentOverride={isUrgentOverride}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-10 rounded-3xl border border-slate-200/70 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800/60">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">Results</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Sort by preferred votes or total attendance.
                  {/* Layer-B: the "X of Y slots match" hint counts feasible-voters
                      per slot, so it leaks the hidden bias vector. Gate on the
                      per-slot-details entitlement. */}
                  {showSchedulerVoteDetails && hasRequiredAttendanceFilter && (
                    <span className="ml-2 text-slate-400 dark:text-slate-500">
                      {filteredSortedSlots.length} of {sortedSlots.length} slots match required
                      attendance.
                    </span>
                  )}
                </p>
              </div>
              {/* Layer-B privacy: the sort-mode toggle (orders by hidden
                  tallies) and the Required-attendance filter (reveals WHO
                  feasible-voted WHICH slot) are results-analysis tools over the
                  hidden bias vector. Render the whole control cluster only when
                  this viewer is entitled to per-slot vote details. */}
              {showSchedulerVoteDetails && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-700">
                  <button
                    type="button"
                    onClick={() => setSortMode("preferred")}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                      sortMode === "preferred"
                        ? "bg-brand-primary text-white"
                        : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-600"
                    }`}
                  >
                    Maximize Preferred
                  </button>
                  <button
                    type="button"
                    onClick={() => setSortMode("attendance")}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                      sortMode === "attendance"
                        ? "bg-brand-primary text-white"
                        : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-600"
                    }`}
                  >
                    Maximize Attendance
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        title={requiredAttendanceTitle}
                        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                      >
                        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-300">
                          Required
                        </span>
                        {hasRequiredAttendanceFilter ? (
                          <>
                            <AvatarStackWithColors
                              users={requiredAttendanceUsers}
                              max={3}
                              size={18}
                              onClick={schedulerHandleAvatarClick}
                            />
                            <span className="text-xs text-slate-700 dark:text-slate-100">
                              {requiredAttendanceLabel}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-slate-600/60 dark:text-slate-200">
                              {requiredAttendance.length}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-slate-400 dark:text-slate-300">
                            Anyone
                          </span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72 p-2">
                      <DropdownMenuLabel>Required attendance</DropdownMenuLabel>
                      <div className="max-h-72 space-y-1 overflow-y-auto">
                        {requiredAttendanceOptions.length === 0 && (
                          <p className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                            No participants available yet.
                          </p>
                        )}
                        {requiredAttendanceOptions.map((participant) => {
                          const isSelected = requiredAttendanceSet.has(participant.email);
                          return (
                            <DropdownMenuItem
                              key={participant.email}
                              role="menuitemcheckbox"
                              aria-checked={isSelected}
                              onSelect={(event) => {
                                event.preventDefault();
                                toggleRequiredAttendance(participant.email);
                              }}
                              className="gap-2"
                            >
                              <span
                                className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${
                                  isSelected
                                    ? "border-brand-primary bg-brand-primary text-white"
                                    : "border-slate-200 text-transparent dark:border-slate-600"
                                }`}
                              >
                                <Check size={12} />
                              </span>
                              <AvatarBubbleWithColors user={participant} size={18} />
                              <div className="min-w-0 flex-1">
                                <UserIdentity
                                  user={participant}
                                  className="block truncate text-slate-700 dark:text-slate-100"
                                />
                              </div>
                              <div className="ml-auto flex items-center gap-1">
                                {participant.isPendingInvite && (
                                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
                                    Pending
                                  </span>
                                )}
                                {!participant.hasVoted && (
                                  <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                    No votes
                                  </span>
                                )}
                              </div>
                            </DropdownMenuItem>
                          );
                        })}
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        disabled={!hasRequiredAttendanceFilter}
                        onSelect={(event) => {
                          event.preventDefault();
                          clearRequiredAttendance();
                        }}
                        className="justify-between text-xs font-semibold text-slate-500"
                      >
                        Clear filter
                        {hasRequiredAttendanceFilter && <X size={12} />}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {hasRequiredAttendanceFilter && (
                    <button
                      type="button"
                      onClick={clearRequiredAttendance}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-600"
                      aria-label="Clear required attendance filter"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              )}
            </div>
            {rescheduleInfo && isLocked && (() => {
              const winningSlot = slots.data.find((s) => s.id === scheduler.data?.winningSlotId);
              const oldStart = rescheduleInfo.fromSlot?.start ? new Date(rescheduleInfo.fromSlot.start) : null;
              const oldEnd = rescheduleInfo.fromSlot?.end ? new Date(rescheduleInfo.fromSlot.end) : null;
              const newStart = winningSlot?.start ? new Date(winningSlot.start) : null;
              const newEnd = winningSlot?.end ? new Date(winningSlot.end) : null;
              return (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 dark:border-amber-700/50 dark:bg-amber-900/20">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                    {rescheduleInfo.isExternal ? "Rescheduled via Google Calendar" : "Rescheduled"}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                    {oldStart && (
                      <span className="line-through opacity-70">
                        {formatZonedDateTimeRange({
                          start: oldStart,
                          end: oldEnd,
                          timeZone: displayTimeZone,
                          startPattern: "EEE, MMM d · h:mm a",
                          endPattern: "h:mm a",
                          showTimeZone,
                        })}
                      </span>
                    )}
                    {oldStart && newStart && <span>&rarr;</span>}
                    {newStart && (
                      <span className="font-semibold">
                        {formatZonedDateTimeRange({
                          start: newStart,
                          end: newEnd,
                          timeZone: displayTimeZone,
                          startPattern: "EEE, MMM d · h:mm a",
                          endPattern: "h:mm a",
                          showTimeZone,
                        })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
            <div className="mt-4 space-y-3">
              {filteredSortedSlots.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-4 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                  {hasRequiredAttendanceFilter
                    ? "No slots match the required attendance filter."
                    : "No slots to display yet."}
                  {hasRequiredAttendanceFilter && (
                    <button
                      type="button"
                      onClick={clearRequiredAttendance}
                      className="ml-3 text-xs font-semibold text-brand-primary"
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              ) : (
                filteredSortedSlots.map((slot) => {
                const startDate = slot.start ? new Date(slot.start) : null;
                const endDate = slot.end ? new Date(slot.end) : null;
                const voters = slotVotersForDisplay[slot.id] || { feasible: [], preferred: [] };
                const isPast = pastSlotIds.has(slot.id);
                const hasWinner = Boolean(scheduler.data?.winningSlotId);
                const isWinner = scheduler.data?.winningSlotId === slot.id && isLocked;
                const isPreviousWinner = previousWinnerSlotId === slot.id && isLocked && !isWinner;
                const isMuted = hasWinner && !isWinner && !isPreviousWinner && isLocked;
                return (
                  <div
                    key={slot.id}
                    data-testid={`result-slot-${slot.id}`}
                    title={isPast ? "This time slot is in the past." : undefined}
                    className={`rounded-2xl border px-4 py-3 dark:bg-slate-900 ${
                      isWinner
                        ? "border-emerald-300 bg-emerald-50/70 dark:border-emerald-700 dark:bg-emerald-900/30"
                      : isPreviousWinner
                          ? "border-amber-300 bg-amber-50/70 dark:border-amber-700 dark:bg-amber-900/30"
                      : isPast
                          ? "border-slate-200/70 bg-white grayscale opacity-60 dark:border-slate-700"
                          : "border-slate-200/70 bg-white dark:border-slate-700"
                    } ${isMuted ? "opacity-60 grayscale" : ""}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {startDate
                            ? formatZonedDateTimeRange({
                                start: startDate,
                                end: endDate,
                                timeZone: displayTimeZone,
                                startPattern: "EEE, MMM d, yyyy · h:mm a",
                                endPattern: "h:mm a",
                                showTimeZone,
                              })
                            : "Slot"}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {showSchedulerVoteDetails
                            ? `Preferred ${slot.counts.preferred} · Feasible ${slot.counts.feasible}`
                            : "Vote details hidden"}
                        </p>
                        {isPreviousWinner && (
                          <p className="mt-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                            Previously scheduled
                          </p>
                        )}
                        {isPast && !isPreviousWinner && (
                          <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                            This slot is in the past.
                          </p>
                        )}
                        {showSchedulerVoteDetails && expandedSlots[slot.id] && (
                          <div className="mt-2 flex flex-col gap-2">
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-semibold">★ Preferred</span>
                              {showSchedulerVoterIdentities ? (
                                <AvatarStackWithColors users={voters.preferred} max={8} size={22} onClick={schedulerHandleAvatarClick} />
                              ) : null}
                              <span className="text-slate-400 dark:text-slate-500">
                                {slot.counts.preferred}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-semibold">✓ Feasible</span>
                              {showSchedulerVoterIdentities ? (
                                <AvatarStackWithColors users={voters.feasible} max={8} size={22} onClick={schedulerHandleAvatarClick} />
                              ) : null}
                              <span className="text-slate-400 dark:text-slate-500">
                                {slot.counts.feasible}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {showSchedulerVoteDetails ? (
                          <button
                            type="button"
                            data-testid={`result-slot-details-${slot.id}`}
                            onClick={() => toggleExpanded(slot.id)}
                            className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
                          >
                            {expandedSlots[slot.id] ? "Hide details" : "Details"}
                          </button>
                        ) : null}
                        {isCreator && (
                          <span
                            title={
                              isPast
                                ? "This time slot is in the past and cannot be selected."
                                : ""
                            }
                          >
                            <button
                              type="button"
                              disabled={isPast || isWinner || requiredFinalizeChecking}
                              onClick={() => isLocked ? requestReschedule(slot.id) : requestFinalize(slot.id)}
                              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                            >
                              {isWinner
                                ? "Winner"
                                : isLocked
                                  ? "Reschedule"
                                  : "Select winner"}
                            </button>
                          </span>
                        )}
                      </div>
                    </div>
                    {showSchedulerVoteDetails && expandedSlots[slot.id] && (
                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200/70 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                            ★ Preferred voters
                          </p>
                          <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            {showSchedulerVoterIdentities ? (
                              <AvatarStackWithColors users={voters.preferred} max={8} size={22} onClick={schedulerHandleAvatarClick} />
                            ) : null}
                            <span className="text-slate-400 dark:text-slate-500">
                              {slot.counts.preferred}
                            </span>
                          </div>
                          {voters.preferred.length === 0 ? (
                            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                              No preferred votes yet.
                            </p>
                          ) : showSchedulerVoterIdentities ? (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {voters.preferred.map((voter) => (
                                <div
                                  key={voter.email}
                                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                                >
                                  <AvatarBubbleWithColors user={voter} size={20} />
                                  <UserIdentity
                                    user={resolveVoterIdentityForDisplay(
                                      voter,
                                      participantMapByEmail,
                                      schedulerVoteIdentityDisplayMode
                                    )}
                                    className="text-slate-700 dark:text-slate-200"
                                  />
                                  {voter.source === "discord" && (
                                    <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600 dark:border-indigo-700/60 dark:bg-indigo-900/40 dark:text-indigo-200">
                                      Discord
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                              {slot.counts.preferred} preferred vote
                              {slot.counts.preferred === 1 ? "" : "s"} recorded.
                            </p>
                          )}
                        </div>
                        <div className="rounded-2xl border border-slate-200/70 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                            ✓ Feasible voters
                          </p>
                          <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            {showSchedulerVoterIdentities ? (
                              <AvatarStackWithColors users={voters.feasible} max={8} size={22} onClick={schedulerHandleAvatarClick} />
                            ) : null}
                            <span className="text-slate-400 dark:text-slate-500">
                              {slot.counts.feasible}
                            </span>
                          </div>
                          {voters.feasible.length === 0 ? (
                            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                              No feasible votes yet.
                            </p>
                          ) : showSchedulerVoterIdentities ? (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {voters.feasible.map((voter) => (
                                <div
                                  key={voter.email}
                                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                                >
                                  <AvatarBubbleWithColors user={voter} size={20} />
                                  <UserIdentity
                                    user={resolveVoterIdentityForDisplay(
                                      voter,
                                      participantMapByEmail,
                                      schedulerVoteIdentityDisplayMode
                                    )}
                                    className="text-slate-700 dark:text-slate-200"
                                  />
                                  {voter.source === "discord" && (
                                    <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600 dark:border-indigo-700/60 dark:bg-indigo-900/40 dark:text-indigo-200">
                                      Discord
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                              {slot.counts.feasible} feasible vote
                              {slot.counts.feasible === 1 ? "" : "s"} recorded.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
                })
              )}
            </div>
          </div>

          {embeddedPollsLoading ? (
            <div className="mt-10 rounded-3xl border border-slate-200/70 bg-slate-50 p-6 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
              Loading add-on polls...
            </div>
          ) : null}
          {!embeddedPollsLoading && embeddedPollsSorted.length > 0 ? (
            <div className="mt-10 rounded-3xl border border-slate-200/70 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800/60">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Add-on polls
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {embeddedCompletedCount}/{embeddedPollsSorted.length} polls completed
                  </p>
                </div>
                {requiredEmbeddedPendingCount > 0 ? (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200">
                    {requiredEmbeddedPendingCount} required add-on poll
                    {requiredEmbeddedPendingCount === 1 ? "" : "s"} pending
                  </span>
                ) : null}
              </div>
              <div className="mt-3 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
                <div
                  className="h-2 rounded-full bg-brand-primary transition-all"
                  style={{
                    width: `${Math.round(
                      embeddedPollsSorted.length > 0
                        ? (embeddedCompletedCount / embeddedPollsSorted.length) * 100
                        : 0
                    )}%`,
                  }}
                />
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {embeddedPollsSorted.map((poll) => {
                  const draft = embeddedVoteDrafts[poll.id] || {};
                  const hasSubmitted = hasSubmittedEmbeddedVote(poll, embeddedMyVotes[poll.id]);
                  const myVote = embeddedMyVotes[poll.id] || null;
                  const pollVotes = embeddedVotesByPoll[poll.id] || [];
                  const pollSubmittedVotes = pollVotes.filter((voteDoc) =>
                    hasSubmittedEmbeddedVote(poll, voteDoc)
                  );
                  // Embedded polls inherit the parent scheduler's vote privacy
                  // (per-poll controls removed), clamped so they're never less
                  // private than what the poll itself stored (no retro de-anon).
                  const embeddedPrivacy = resolveEmbeddedPollPrivacy({
                    parent: scheduler.data,
                    poll,
                  });
                  const canReadEmbeddedVoteDetails = canViewOtherVotesForUser({
                    voteVisibility: embeddedPrivacy.voteVisibility,
                    isCreator,
                    hasVoted: hasSubmitted,
                    allParticipantsVoted: poll?.votesAllSubmitted === true,
                    isFinalized: (poll?.status || "OPEN") === "FINALIZED",
                  });
                  const embeddedVoteIdentityDisplayMode = getVoteIdentityDisplayMode({
                    isCreator,
                    hideVoterIdentities: embeddedPrivacy.hideVoterIdentities,
                    voteAnonymization: embeddedPrivacy.voteAnonymization,
                    detailsRevealed: canReadEmbeddedVoteDetails,
                  });
                  const canViewEmbeddedVoterIdentities =
                    embeddedVoteIdentityDisplayMode !== "hidden";
                  const canReadEmbeddedVoteProgress =
                    canReadEmbeddedVoteDetails || canViewEmbeddedVoterIdentities;
                  const embeddedFinalResults = poll?.finalResults || null;
                  const canVoteEmbedded = canVoteEmbeddedPoll(poll);
                  const lifecycleBusy = embeddedLifecycleBusyByPoll[poll.id] === true;
                  const cardBusy =
                    embeddedSubmittingByPoll[poll.id] ||
                    embeddedClearingByPoll[poll.id] ||
                    lifecycleBusy;
                  // statusKnown: is the per-person voted/pending SPLIT known for
                  // this poll? True only once the gated peer-votes snapshot
                  // resolved (viewer entitled AND read succeeded). False while
                  // loading, denied, or own-only — then we render a neutral split.
                  const embeddedStatusKnown =
                    embeddedPollStatusKnown[poll.id] === true;
                  // Layer-A count must be authoritative even when the viewer is
                  // NOT entitled to peer status (then embeddedPollVoteCounts holds
                  // an own-only count). Fall back to the denormalized poll counter
                  // (submittedVoteCount), which the trigger keeps current, so the
                  // "N/M voted" line never collapses to 0/1 under restrictive
                  // privacy. Entitled viewers keep the live loaded-doc count.
                  // PR-5: the guest-aware denominator comes from the centralized
                  // vote-tally helper. submittedVoteCount is guest-inclusive
                  // while participantCount is participant-only; the tally grows
                  // the denominator by the guest-vote count so the displayed
                  // count is guest-inclusive AND never shows X>Y.
                  const denormalizedVoteCount = Number.isFinite(poll?.submittedVoteCount)
                    ? poll.submittedVoteCount
                    : null;
                  const voteCount =
                    !canVoteEmbedded && Number.isFinite(embeddedFinalResults?.voterCount)
                      ? embeddedFinalResults.voterCount
                      : canReadEmbeddedVoteProgress
                        ? embeddedPollVoteCounts[poll.id] || 0
                        : denormalizedVoteCount ?? (embeddedPollVoteCounts[poll.id] || 0);
                  // Guest-aware denominator (and guest split) from the poll's
                  // denormalized counters. Falls back gracefully to
                  // participantCount when the counters aren't on the doc yet.
                  const embeddedTally = getVoteTally(poll);
                  const embeddedDisplayTotal = embeddedTally.hasGuests
                    ? embeddedTally.displayTotal
                    : participantCount;
                  const eligibleUsers = participants.filter((participant) => participant?.email);
                  const votedIdSet = new Set(
                    pollSubmittedVotes
                      .map((voteDoc) => String(voteDoc?.id || "").trim())
                      .filter(Boolean)
                  );
                  const votedUsers = eligibleUsers.filter((participant) =>
                    votedIdSet.has(String(participant.id || ""))
                  );
                  const pendingUsers = eligibleUsers.filter(
                    (participant) => !votedIdSet.has(String(participant.id || ""))
                  );
                  const embeddedAnonymousIdentityMap =
                    embeddedVoteIdentityDisplayMode === "anonymous"
                      ? buildAnonymousIdentityMap(
                          [...eligibleUsers, ...votedUsers, ...pendingUsers],
                          { scopeKey: `scheduler:${id}:embedded:${poll.id}` }
                        )
                      : null;
                  const eligibleUsersForDisplay =
                    embeddedVoteIdentityDisplayMode === "anonymous"
                      ? anonymizeUsers(eligibleUsers, embeddedAnonymousIdentityMap, {
                          keyPrefix: "eligible",
                          currentUser: user,
                        })
                      : eligibleUsers;
                  const votedUsersForDisplay =
                    embeddedVoteIdentityDisplayMode === "anonymous"
                      ? anonymizeUsers(votedUsers, embeddedAnonymousIdentityMap, {
                          keyPrefix: "voted",
                          currentUser: user,
                        })
                      : votedUsers;
                  const pendingUsersForDisplay =
                    embeddedVoteIdentityDisplayMode === "anonymous"
                      ? anonymizeUsers(pendingUsers, embeddedAnonymousIdentityMap, {
                          keyPrefix: "pending",
                          currentUser: user,
                        })
                      : pendingUsers;
                  const embeddedVoteVisibilityHint = !canReadEmbeddedVoteDetails
                    ? embeddedPrivacy.voteVisibility === "hidden_while_voting"
                      ? "Vote details unlock after you submit your vote."
                      : embeddedPrivacy.voteVisibility === "hidden_until_all_voted"
                        ? "Vote details unlock once all participants have voted."
                        : embeddedPrivacy.voteVisibility === "hidden_until_finalized"
                          ? "Vote details unlock once this poll is finalized."
                          : embeddedPrivacy.voteVisibility === "hidden"
                            ? "Only the poll creator can view participant vote details."
                            : null
                    : null;

                  return (
                    <BasicPollVotingCard
                      key={poll.id}
                      poll={poll}
                      participantCount={embeddedDisplayTotal}
                      voteCount={voteCount}
                      hasSubmitted={hasSubmitted}
                      myVote={myVote}
                      draft={draft}
                      canVote={canVoteEmbedded}
                      cardBusy={cardBusy}
                      voteError={embeddedVoteErrors[poll.id] || null}
                      isCreator={isCreator}
                      lifecycleBusy={lifecycleBusy}
                      submittedVotes={pollSubmittedVotes}
                      canBreakTie={isCreator}
                      tieBreakBusy={lifecycleBusy}
                      isHighlighted={highlightedEmbeddedPollId === poll.id}
                      parentCancelled={scheduler.data?.status === "CANCELLED"}
                      onSetRef={(node) => {
                        if (node) {
                          embeddedPollCardRefs.current[poll.id] = node;
                        } else {
                          delete embeddedPollCardRefs.current[poll.id];
                        }
                      }}
                      onMoveRankedOption={(optionId, direction) =>
                        moveEmbeddedRankedOption(poll.id, optionId, direction)
                      }
                      onAddRankedOption={(optionId) => addEmbeddedRankedOption(poll.id, optionId)}
                      onRemoveRankedOption={(optionId) =>
                        removeEmbeddedRankedOption(poll.id, optionId)
                      }
                      onSelectOption={(optionId) => setEmbeddedMultipleChoiceSelection(poll, optionId)}
                      onChangeOtherText={(value) => setEmbeddedOtherText(poll.id, value)}
                      onSubmitVote={() => submitEmbeddedPollVote(poll)}
                      onClearVote={() => clearEmbeddedPollVote(poll)}
                      eligibleUsers={eligibleUsersForDisplay}
                      votedUsers={votedUsersForDisplay}
                      pendingUsers={pendingUsersForDisplay}
                      showVoteProgress={canReadEmbeddedVoteProgress}
                      statusKnown={embeddedStatusKnown}
                      showVoterIdentities={canViewEmbeddedVoterIdentities}
                      voteIdentityDisplayMode={embeddedVoteIdentityDisplayMode}
                      voteVisibilityHint={embeddedVoteVisibilityHint}
                      votes={pollVotes}
                      canReadVoteDetails={canReadEmbeddedVoteDetails}
                      canReadVoteProgress={canReadEmbeddedVoteProgress}
                      onFinalizePoll={() => finalizeEmbeddedPollIndividually(poll)}
                      onReopenPoll={() => reopenEmbeddedPollIndividually(poll)}
                      onBreakTie={(method) => breakEmbeddedPollTieIndividually(poll, method)}
                      onViewOptionNote={(pollTitle, option) =>
                        openEmbeddedOptionNoteViewer(pollTitle, option)
                      }
                    />
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || isVotingInputLocked || allSlotsPast}
              className="rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-50"
            >
              {isVotingInputLocked ? "Voting closed" : saving ? "Saving..." : "Submit votes"}
            </button>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {isVotingInputLocked
                ? "Voting is closed. Existing votes are read-only."
                : "You can come back and edit your votes until voting closes."}
            </p>
          </div>

          {showGuestNudge && canGuestVote ? (
            <MakeAccountNudge
              onConvert={handleGuestConversion}
              onDismiss={() => setShowGuestNudge(false)}
            />
          ) : null}
        </div>

      <PollOptionNoteDialog
        noteViewer={embeddedOptionNoteViewer}
        onClose={() => setEmbeddedOptionNoteViewer(null)}
      />

      <VoteDialog
        open={Boolean(modalDate)}
        onOpenChange={(open) => {
          if (!open) setModalDate(null);
        }}
        modalDate={modalDate}
        slots={slotsForModal}
        displayTimeZone={displayTimeZone}
        showTimeZone={showTimeZone}
        noTimesWork={noTimesWork}
        canVote={canVote}
        onToggleNoTimesWork={toggleNoTimesWork}
        draftVotes={draftVotes}
        pastSlotIds={pastSlotIds}
        blockersBySlotId={userBlockersBySlotId}
        blockerTitleBySchedulerId={Object.fromEntries(
          Object.entries(blockingSchedulersById || {}).map(([id, data]) => [id, data?.title || null])
        )}
        onNavigateToSchedulerId={(schedulerId) => navigate(`/scheduler/${schedulerId}`)}
        onSetVote={setVote}
        onRestoreOverride={handleRestoreOverride}
        onConfirmOverride={handleConfirmOverride}
        schedulerId={id}
        voteAutoSourceBySlotId={voteAutoSourceBySlotId}
        voteAutoReasonBySlotId={voteAutoReasonBySlotId}
        groupNames={groupNames}
        autoVoteEnabled={autoVoteEnabled}
      />

      <CopyVotesDialog
        open={copyVotesOpen}
        onOpenChange={setCopyVotesOpen}
        sourceSchedulerId={id}
        sourceTitle={scheduler.data?.title || "Session Poll"}
        sourceSlots={slots.data}
        sourceVoteDoc={userVote.data || null}
        sourceTimeZone={scheduler.data?.timezone || null}
        userSettings={settings || null}
      />

      <PendingVotesDialog
        open={pendingVotesOpen}
        onOpenChange={setPendingVotesOpen}
        busy={pendingFinalizeBusy}
        onDiscard={discardVotesThenFinalize}
        onSubmit={submitVotesThenFinalize}
      />

      {propagateModal.open && (
        <PropagateVotesPromptDialog
          open
          candidates={propagateModal.candidates}
          contextLabel={propagateModal.contextLabel}
          applying={propagateModal.applying}
          onApply={handlePropagateApply}
          onSkip={handlePropagateSkip}
        />
      )}

      <RequiredEmbeddedFinalizeWarningDialog
        open={requiredFinalizeWarningOpen}
        onOpenChange={setRequiredFinalizeWarningOpen}
        pollSummaries={requiredFinalizeSummary}
        busy={requiredFinalizeChecking}
        onContinue={continueFinalizeWithMissingRequired}
      />

      <FinalizeEmbeddedPollsChoiceDialog
        open={finalizeEmbeddedChoiceOpen}
        onOpenChange={setFinalizeEmbeddedChoiceOpen}
        unfinalizedCount={unfinalizedEmbeddedPollCount}
        onFinalizeAll={continueFinalizeAndFinalizeEmbeddedPolls}
        onFinalizeSessionOnly={continueFinalizeWithoutFinalizingEmbeddedPolls}
      />

      <FinalizeDialog
        open={finalizeOpen}
        onOpenChange={(open) => {
          setFinalizeOpen(open);
          if (!open) {
            setFinalizeOutstandingEmbeddedPolls(false);
            setIsReschedule(false);
          }
        }}
        saving={saving}
        isReschedule={isReschedule}
        createCalendarEvent={createCalendarEvent}
        onToggleCreateCalendarEvent={setCreateCalendarEvent}
        linkedCalendars={linkedCalendars}
        selectedCalendarId={selectedCalendarId}
        originalCalendarId={scheduler.data?.googleCalendarId || ""}
        onSelectCalendarId={setSelectedCalendarId}
        eventTitle={eventTitle}
        onChangeEventTitle={setEventTitle}
        eventDescription={eventDescription}
        onChangeEventDescription={setEventDescription}
        eventDuration={eventDuration}
        onChangeEventDuration={setEventDuration}
        eventAttendees={eventAttendees}
        onChangeEventAttendees={setEventAttendees}
        deleteOldEvent={deleteOldEvent}
        onToggleDeleteOldEvent={setDeleteOldEvent}
        hasExistingEvent={Boolean(scheduler.data?.googleEventId)}
        onOpenSettings={() => navigate("/settings")}
        onFinalize={isReschedule ? handleReschedule : handleFinalize}
      />

      <CloneDialog
        open={cloneOpen}
        onOpenChange={setCloneOpen}
        cloneTitle={cloneTitle}
        onChangeCloneTitle={setCloneTitle}
        cloneGroupId={cloneGroupId}
        onChangeGroupId={(value) => setCloneGroupId(value === "none" ? null : value)}
        groupOptions={cloneGroupOptions}
        includedUser={
          scheduler.data?.creatorEmail
            ? participantMapByEmail.get(normalizeEmail(scheduler.data.creatorEmail)) || {
                email: scheduler.data.creatorEmail,
              }
            : null
        }
        groupName={cloneSelectedGroup?.name || null}
        groupColor={cloneGroupColor || null}
        groupMembers={cloneGroupUsers}
        inviteUsers={cloneInviteUsers}
        onRemoveInvite={removeCloneInvite}
        inviteEmptyLabel="No additional invitees yet."
        recommendedUsers={cloneRecommendedUsers}
        recentInvitees={recentInvitees}
        onAddInvite={addCloneInvite}
        inputValue={cloneInviteInput}
        onInputChange={setCloneInviteInput}
        onAddInput={() => addCloneInvite(cloneInviteInput)}
        inviteError={cloneInviteError}
        cloneClearVotes={cloneClearVotes}
        onToggleClearVotes={setCloneClearVotes}
        onClone={handleClone}
        saving={cloneSaving}
      />

      <ReopenDialog
        open={reopenOpen}
        onOpenChange={setReopenOpen}
        saving={saving}
        onConfirm={confirmReopen}
      />

      <CancelDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={scheduler.data?.title || "Untitled poll"}
        saving={cancelSaving}
        onCancelSession={handleCancelSession}
      />

      <DeleteDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          setDeleteOpen(open);
          if (open) {
            setDeleteUpdateCalendar(false);
          }
        }}
        title={scheduler.data?.title || "Untitled poll"}
        participantCount={participantCount}
        slotCount={slots.data?.length || 0}
        voteCount={voteCount}
        hasExistingEvent={Boolean(scheduler.data?.googleEventId)}
        updateCalendar={deleteUpdateCalendar}
        onToggleUpdateCalendar={setDeleteUpdateCalendar}
        onDelete={handleDelete}
        saving={deleteSaving}
      />

      <InvitePromptDialog
        open={invitePromptOpen || isPendingInvite}
        onOpenChange={setInvitePromptOpen}
        isPendingInvite={isPendingInvite}
        inviterProfile={inviterProfile}
        busy={invitePromptBusy}
        onDecline={handleDeclineInvite}
        onAccept={handleAcceptInvite}
      />

      <LeaveDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        onLeave={handleLeavePoll}
        saving={leaveSaving}
      />

      <RemoveParticipantDialog
        open={removeMemberOpen}
        onOpenChange={setRemoveMemberOpen}
        memberLabel={
          memberToRemove ? (
            <UserIdentity
              user={
                memberToRemove.email
                  ? participantMapByEmail.get(normalizeEmail(memberToRemove.email)) ||
                    memberToRemove
                  : memberToRemove
              }
            />
          ) : null
        }
        onRemove={handleRemoveParticipant}
        disabled={!memberToRemove}
      />

      <RevokeInviteDialog
        open={revokeInviteOpen}
        onOpenChange={setRevokeInviteOpen}
        inviteeEmail={inviteToRevoke}
        onRevoke={handleRevokeInvite}
        disabled={!inviteToRevoke}
      />

      <ProfileInteractionModals {...schedulerModalProps} />

      <GuestVoteConflictModal
        open={Boolean(guestConflict)}
        existingSummary={guestConflict?.existingSummary}
        onPreserve={handleGuestConflictPreserve}
        onReplace={handleGuestConflictReplace}
      />
    </>
  );
}
